import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleEditFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  handleSaveFieldTableRow,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Checkbox,
  FieldTable,
  Input,
  PhoneInput,
  RadioButton,
  Select,
  TextArea,
  ToggleSwitch,
} from "../common/ControlBundle";
import ExpandDiv from "../common/ExpandDiv";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function INOPPSE() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);
  const [showNarrative, setShowNarrative] = useState("Hide");

  const preSignForm = () => {
    const obj = { ...data };

    const familyHistoryDetails = JSON.parse(obj.FamilyHistoryDetails["@value"]);

    if (familyHistoryDetails)
      obj.FamilyHistoryDetails["@value"] = JSON.stringify(
        familyHistoryDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const currentMedicationDetails = JSON.parse(
      obj.CurrentMedicationDetails["@value"]
    );

    if (currentMedicationDetails)
      obj.CurrentMedicationDetails["@value"] = JSON.stringify(
        currentMedicationDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const mentalHealthHistory = JSON.parse(obj.MentalHealthHistory["@value"]);

    if (mentalHealthHistory)
      obj.MentalHealthHistory["@value"] = JSON.stringify(
        mentalHealthHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const substanceUseHistory = JSON.parse(obj.SubstanceUseHistory["@value"]);

    if (substanceUseHistory)
      obj.SubstanceUseHistory["@value"] = JSON.stringify(
        substanceUseHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const substanceUseTreatmentHistory = JSON.parse(
      obj.SubstanceUseTreatmentHistory["@value"]
    );

    if (substanceUseTreatmentHistory)
      obj.SubstanceUseTreatmentHistory["@value"] = JSON.stringify(
        substanceUseTreatmentHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const traumaHistory = JSON.parse(obj.TraumaHistory["@value"]);

    if (traumaHistory)
      obj.TraumaHistory["@value"] = JSON.stringify(
        traumaHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const diagnosisDetails = JSON.parse(obj.DiagnosisDetails["@value"]);

    if (diagnosisDetails)
      obj.DiagnosisDetails["@value"] = JSON.stringify(
        diagnosisDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const treatmentRecommendations = JSON.parse(
      obj.TreatmentRecommendations["@value"]
    );

    if (treatmentRecommendations)
      obj.TreatmentRecommendations["@value"] = JSON.stringify(
        treatmentRecommendations.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    setData(obj);
  };

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const handleEducationStatusChange = (e) => {
    handleChange(e);
    if (e.currentTarget.value === "Client has not started school") {
      const employedInput = {
        currentTarget: {
          type: "radio",
          name: "CurrentlyEmployed",
          value: "No",
        },
      };
      handleChange(employedInput);

      const militaryInput = {
        currentTarget: {
          type: "radio",
          name: "MilitaryHistory",
          value: "No",
        },
      };
      handleChange(militaryInput);
    }
  };

  const handleTraumaSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TraumaticExposureInput = {
        currentTarget: {
          type: "checkbox",
          name: "TraumaticExposure",
          checked: false,
        },
      };
      handleChange(TraumaticExposureInput);

      const AvoidantTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "AvoidantTrauma",
          checked: false,
        },
      };
      handleChange(AvoidantTraumaInput);

      const HypervigilanceTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "HypervigilanceTrauma",
          checked: false,
        },
      };
      handleChange(HypervigilanceTraumaInput);

      const IntrusiveDreamsInput = {
        currentTarget: {
          type: "checkbox",
          name: "IntrusiveDreams",
          checked: false,
        },
      };
      handleChange(IntrusiveDreamsInput);

      const NegativeThoughtsInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeThoughts",
          checked: false,
        },
      };
      handleChange(NegativeThoughtsInput);

      const CocentrationProblemsInput = {
        currentTarget: {
          type: "checkbox",
          name: "CocentrationProblems",
          checked: false,
        },
      };
      handleChange(CocentrationProblemsInput);

      const RecklessBehaviorTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "RecklessBehaviorTrauma",
          checked: false,
        },
      };
      handleChange(RecklessBehaviorTraumaInput);

      const SleepDisturbancesTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesTrauma",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesTraumaInput);

      const OtherTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherTrauma",
          checked: false,
        },
      };
      handleChange(OtherTraumaInput);

      const OtherTraumaDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherTraumaDetails",
          value: "",
        },
      };
      handleChange(OtherTraumaDetailsInput);

      const TraumaSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsFrequency",
          value: "",
        },
      };
      handleChange(TraumaSymptomsFrequencyInput);

      const TraumaSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsIntensity",
          value: "",
        },
      };
      handleChange(TraumaSymptomsIntensityInput);

      const TraumaSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsDuration",
          value: "",
        },
      };
      handleChange(TraumaSymptomsDurationInput);

      const TraumaSymptomsOnsetInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsOnset",
          value: "",
        },
      };
      handleChange(TraumaSymptomsOnsetInput);
    }
  };

  const handleAnxietyDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AnxietyDisorderExcessWorryInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderExcessWorry",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderExcessWorryInput);

      const AnxietyDisorderEasilyFatiguedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderEasilyFatigued",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderEasilyFatiguedInput);

      const MuscleTensionInput = {
        currentTarget: {
          type: "checkbox",
          name: "MuscleTension",
          checked: false,
        },
      };
      handleChange(MuscleTensionInput);

      const OverworriedInput = {
        currentTarget: {
          type: "checkbox",
          name: "Overworried",
          checked: false,
        },
      };
      handleChange(OverworriedInput);

      const AnxietyDisorderDifficultyConcentratingInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderDifficultyConcentrating",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderDifficultyConcentratingInput);

      const SleepDisturbancesAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesAnxiety",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesAnxietyInput);

      const RestlessAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "RestlessAnxiety",
          checked: false,
        },
      };
      handleChange(RestlessAnxietyInput);

      const IrritabilityInput = {
        currentTarget: {
          type: "checkbox",
          name: "Irritability",
          checked: false,
        },
      };
      handleChange(IrritabilityInput);

      const OtherAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAnxiety",
          checked: false,
        },
      };
      handleChange(OtherAnxietyInput);

      const OtherAnxietyDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAnxietyDetails",
          value: "",
        },
      };
      handleChange(OtherAnxietyDetailsInput);

      const AnxietyFrequencyInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyFrequency",
          checked: false,
        },
      };
      handleChange(AnxietyFrequencyInput);

      const AnxietyIntensityInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyIntensity",
          checked: false,
        },
      };
      handleChange(AnxietyIntensityInput);

      const AnxietyDurationInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDuration",
          checked: false,
        },
      };
      handleChange(AnxietyDurationInput);
    }
  };

  const handleDepressionChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DepressedMoodInput = {
        currentTarget: {
          type: "checkbox",
          name: "DepressedMood",
          checked: false,
        },
      };
      handleChange(DepressedMoodInput);

      const SleepChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepChanges",
          checked: false,
        },
      };
      handleChange(SleepChangesInput);

      const GuiltInput = {
        currentTarget: {
          type: "checkbox",
          name: "Guilt",
          checked: false,
        },
      };
      handleChange(GuiltInput);

      const DiminishedInterestInput = {
        currentTarget: {
          type: "checkbox",
          name: "DiminishedInterest",
          checked: false,
        },
      };
      handleChange(DiminishedInterestInput);

      const PurposelessMovementInput = {
        currentTarget: {
          type: "checkbox",
          name: "PurposelessMovement",
          checked: false,
        },
      };
      handleChange(PurposelessMovementInput);

      const WeightChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "WeightChanges",
          checked: false,
        },
      };
      handleChange(WeightChangesInput);

      const FatigueDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "FatigueDepression",
          checked: false,
        },
      };
      handleChange(FatigueDepressionInput);

      const ThoughtsOfDeathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ThoughtsOfDeath",
          checked: false,
        },
      };
      handleChange(ThoughtsOfDeathInput);

      const OtherDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherDepression",
          checked: false,
        },
      };
      handleChange(OtherDepressionInput);

      const OtherDepressionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherDepressionDetails",
          value: "",
        },
      };
      handleChange(OtherDepressionDetailsInput);

      const DepressionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DepressionFrequency",
          value: "",
        },
      };
      handleChange(DepressionFrequencyInput);

      const DepressionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DepressionIntensity",
          value: "",
        },
      };
      handleChange(DepressionIntensityInput);

      const DepressionDurationInput = {
        currentTarget: {
          type: "input",
          name: "DepressionDuration",
          value: "",
        },
      };
      handleChange(DepressionDurationInput);
    }
  };

  const handleAttentionHyperactivityChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AttentionHyperactivityEasilyDistractedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AttentionHyperactivityEasilyDistracted",
          checked: false,
        },
      };
      handleChange(AttentionHyperactivityEasilyDistractedInput);

      const DifficultyFollowThroughInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyFollowThrough",
          checked: false,
        },
      };
      handleChange(DifficultyFollowThroughInput);

      const ForgetfulInput = {
        currentTarget: {
          type: "checkbox",
          name: "Forgetful",
          checked: false,
        },
      };
      handleChange(ForgetfulInput);

      const DifficultyListeningInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyListening",
          checked: false,
        },
      };
      handleChange(DifficultyListeningInput);

      const DisorganizedAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "DisorganizedAttention",
          checked: false,
        },
      };
      handleChange(DisorganizedAttentionInput);

      const FidgetyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Fidgety",
          checked: false,
        },
      };
      handleChange(FidgetyInput);

      const LackOfDetailsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LackOfDetails",
          checked: false,
        },
      };
      handleChange(LackOfDetailsInput);

      const LosesThingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LosesThings",
          checked: false,
        },
      };
      handleChange(LosesThingsInput);

      const InterruptsOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "InterruptsOthers",
          checked: false,
        },
      };
      handleChange(InterruptsOthersInput);

      const UnableToSitStillInput = {
        currentTarget: {
          type: "checkbox",
          name: "UnableToSitStill",
          checked: false,
        },
      };
      handleChange(UnableToSitStillInput);

      const ExcessiveTalkingInput = {
        currentTarget: {
          type: "checkbox",
          name: "ExcessiveTalking",
          checked: false,
        },
      };
      handleChange(ExcessiveTalkingInput);

      const DifficultyWaitingInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyWaiting",
          checked: false,
        },
      };
      handleChange(DifficultyWaitingInput);

      const OtherAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAttention",
          checked: false,
        },
      };
      handleChange(OtherAttentionInput);

      const OtherAttentionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAttentionDetails",
          value: "",
        },
      };
      handleChange(OtherAttentionDetailsInput);

      const AttentionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "AttentionFrequency",
          value: "",
        },
      };
      handleChange(AttentionFrequencyInput);

      const AttentionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "AttentionIntensity",
          value: "",
        },
      };
      handleChange(AttentionIntensityInput);

      const AttentionDurationInput = {
        currentTarget: {
          type: "input",
          name: "AttentionDuration",
          value: "",
        },
      };
      handleChange(AttentionDurationInput);
    }
  };

  const handleSubstanceUseSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PatternOfSubstanceUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "PatternOfSubstanceUse",
          checked: false,
        },
      };
      handleChange(PatternOfSubstanceUseInput);

      const DesiresCutDownUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "DesiresCutDownUse",
          checked: false,
        },
      };
      handleChange(DesiresCutDownUseInput);

      const TimeSpentObtainingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeSpentObtaining",
          checked: false,
        },
      };
      handleChange(TimeSpentObtainingInput);

      const CravingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Cravings",
          checked: false,
        },
      };
      handleChange(CravingsInput);

      const FailureOfResponsibilitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "FailureOfResponsibilities",
          checked: false,
        },
      };
      handleChange(FailureOfResponsibilitiesInput);

      const NegativeImpactInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeImpact",
          checked: false,
        },
      };
      handleChange(NegativeImpactInput);

      const AbandonsActivitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "AbandonsActivities",
          checked: false,
        },
      };
      handleChange(AbandonsActivitiesInput);

      const ToleranceInput = {
        currentTarget: {
          type: "checkbox",
          name: "Tolerance",
          checked: false,
        },
      };
      handleChange(ToleranceInput);

      const SUWithdrawalInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUWithdrawal",
          checked: false,
        },
      };
      handleChange(SUWithdrawalInput);

      const SUOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUOther",
          checked: false,
        },
      };
      handleChange(SUOtherInput);

      const SUOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "SUOtherDetails",
          value: "",
        },
      };
      handleChange(SUOtherDetailsInput);

      const SubstanceUseFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseFrequency",
          value: "",
        },
      };
      handleChange(SubstanceUseFrequencyInput);

      const SubstanceUseIntensityInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseIntensity",
          value: "",
        },
      };
      handleChange(SubstanceUseIntensityInput);

      const SubstanceUseDurationInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseDuration",
          value: "",
        },
      };
      handleChange(SubstanceUseDurationInput);
    }
  };

  const handleDMDDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TemperInput = {
        currentTarget: {
          type: "checkbox",
          name: "Temper",
          checked: false,
        },
      };
      handleChange(TemperInput);

      const OutsideOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutsideOutbursts",
          checked: false,
        },
      };
      handleChange(OutsideOutburstsInput);

      const FrequentOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "FrequentOutbursts",
          checked: false,
        },
      };
      handleChange(FrequentOutburstsInput);

      const DMDDIrritableInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDIrritable",
          checked: false,
        },
      };
      handleChange(DMDDIrritableInput);

      const OutburstsAroundOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutburstsAroundOthers",
          checked: false,
        },
      };
      handleChange(OutburstsAroundOthersInput);

      const DMDDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDOther",
          checked: false,
        },
      };
      handleChange(DMDDOtherInput);

      const DMDDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DMDDOtherDetails",
          value: "",
        },
      };
      handleChange(DMDDOtherDetailsInput);

      const DMDDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DMDDFrequency",
          value: "",
        },
      };
      handleChange(DMDDFrequencyInput);

      const DMDDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DMDDIntensity",
          value: "",
        },
      };
      handleChange(DMDDIntensityInput);

      const DMDDDurationInput = {
        currentTarget: {
          type: "input",
          name: "DMDDDuration",
          value: "",
        },
      };
      handleChange(DMDDDurationInput);
    }
  };

  const handleConductDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AggressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "Aggression",
          checked: false,
        },
      };
      handleChange(AggressionInput);

      const DestructionOfPropertyInput = {
        currentTarget: {
          type: "checkbox",
          name: "DestructionOfProperty",
          checked: false,
        },
      };
      handleChange(DestructionOfPropertyInput);

      const TheftInput = {
        currentTarget: {
          type: "checkbox",
          name: "Theft",
          checked: false,
        },
      };
      handleChange(TheftInput);

      const SeriousViolationsInput = {
        currentTarget: {
          type: "checkbox",
          name: "SeriousViolations",
          checked: false,
        },
      };
      handleChange(SeriousViolationsInput);

      const OtherConductDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherConductDisorder",
          checked: false,
        },
      };
      handleChange(OtherConductDisorderInput);

      const OtherConductDisorderDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherConductDisorderDetails",
          value: "",
        },
      };
      handleChange(OtherConductDisorderDetailsInput);

      const ConductDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderFrequency",
          value: "",
        },
      };
      handleChange(ConductDisorderFrequencyInput);

      const ConductDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderIntensity",
          value: "",
        },
      };
      handleChange(ConductDisorderIntensityInput);

      const ConductDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderDuration",
          value: "",
        },
      };
      handleChange(ConductDisorderDurationInput);
    }
  };

  const handleODDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const IrritableODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "IrritableODD",
          checked: false,
        },
      };
      handleChange(IrritableODDInput);

      const ArgumentativeInput = {
        currentTarget: {
          type: "checkbox",
          name: "Argumentative",
          checked: false,
        },
      };
      handleChange(ArgumentativeInput);

      const VindictiveInput = {
        currentTarget: {
          type: "checkbox",
          name: "Vindictive",
          checked: false,
        },
      };
      handleChange(VindictiveInput);

      const OtherODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherODD",
          checked: false,
        },
      };
      handleChange(OtherODDInput);

      const OtherODDDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherODDDetails",
          value: "",
        },
      };
      handleChange(OtherODDDetailsInput);

      const ODDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ODDFrequency",
          value: "",
        },
      };
      handleChange(ODDFrequencyInput);

      const ODDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ODDIntensity",
          value: "",
        },
      };
      handleChange(ODDIntensityInput);

      const ODDDurationInput = {
        currentTarget: {
          type: "input",
          name: "ODDDuration",
          value: "",
        },
      };
      handleChange(ODDDurationInput);
    }
  };

  const handleOCDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OCDPresenceInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDPresence",
          checked: false,
        },
      };
      handleChange(OCDPresenceInput);

      const ObsessionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Obsessions",
          checked: false,
        },
      };
      handleChange(ObsessionsInput);

      const TimeConsumingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeConsuming",
          checked: false,
        },
      };
      handleChange(TimeConsumingInput);

      const CompulsionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Compulsions",
          checked: false,
        },
      };
      handleChange(CompulsionsInput);

      const ReduceAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "ReduceAnxiety",
          checked: false,
        },
      };
      handleChange(ReduceAnxietyInput);

      const SuppressUrgesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SuppressUrges",
          checked: false,
        },
      };
      handleChange(SuppressUrgesInput);

      const OCDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDOther",
          checked: false,
        },
      };
      handleChange(OCDOtherInput);

      const OCDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OCDOtherDetails",
          value: "",
        },
      };
      handleChange(OCDOtherDetailsInput);

      const OCDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OCDFrequency",
          value: "",
        },
      };
      handleChange(OCDFrequencyInput);

      const OCDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OCDIntensity",
          value: "",
        },
      };
      handleChange(OCDIntensityInput);

      const OCDDurationInput = {
        currentTarget: {
          type: "input",
          name: "OCDDuration",
          value: "",
        },
      };
      handleChange(OCDDurationInput);
    }
  };

  const handlePanicDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PoundingHeartInput = {
        currentTarget: {
          type: "checkbox",
          name: "PoundingHeart",
          checked: false,
        },
      };
      handleChange(PoundingHeartInput);

      const PanicDisorderSweatingInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderSweating",
          checked: false,
        },
      };
      handleChange(PanicDisorderSweatingInput);

      const TremblingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Trembling",
          checked: false,
        },
      };
      handleChange(TremblingInput);

      const ShortnessOfBreathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ShortnessOfBreath",
          checked: false,
        },
      };
      handleChange(ShortnessOfBreathInput);

      const ChokingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Choking",
          checked: false,
        },
      };
      handleChange(ChokingInput);

      const ChestPainInput = {
        currentTarget: {
          type: "checkbox",
          name: "ChestPain",
          checked: false,
        },
      };
      handleChange(ChestPainInput);

      const PanicDisorderNauseaInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderNausea",
          checked: false,
        },
      };
      handleChange(PanicDisorderNauseaInput);

      const DizzyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Dizzy",
          checked: false,
        },
      };
      handleChange(DizzyInput);

      const ChillsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Chills",
          checked: false,
        },
      };
      handleChange(ChillsInput);

      const DerealizationInput = {
        currentTarget: {
          type: "checkbox",
          name: "Derealization",
          checked: false,
        },
      };
      handleChange(DerealizationInput);

      const FearOfCrazyInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfCrazy",
          checked: false,
        },
      };
      handleChange(FearOfCrazyInput);

      const FearOfDyingInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfDying",
          checked: false,
        },
      };
      handleChange(FearOfDyingInput);

      const PanicDisorderOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderOther",
          checked: false,
        },
      };
      handleChange(PanicDisorderOtherInput);

      const PanicDisorderOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderOtherDetails",
          value: "",
        },
      };
      handleChange(PanicDisorderOtherDetailsInput);

      const PanicDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderFrequency",
          value: "",
        },
      };
      handleChange(PanicDisorderFrequencyInput);

      const PanicDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderIntensity",
          value: "",
        },
      };
      handleChange(PanicDisorderIntensityInput);

      const PanicDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderDuration",
          value: "",
        },
      };
      handleChange(PanicDisorderDurationInput);
    }
  };

  const handlePhobiasChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PhobiasSpecificFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasSpecificFear",
          checked: false,
        },
      };
      handleChange(PhobiasSpecificFearInput);

      const ImmediateFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "ImmediateFear",
          checked: false,
        },
      };
      handleChange(ImmediateFearInput);

      const PhobiasAvoidanceInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasAvoidance",
          checked: false,
        },
      };
      handleChange(PhobiasAvoidanceInput);

      const FearExaggeratedInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearExaggerated",
          checked: false,
        },
      };
      handleChange(FearExaggeratedInput);

      const PhobiasOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasOther",
          checked: false,
        },
      };
      handleChange(PhobiasOtherInput);

      const PhobiasOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasOtherDetails",
          value: "",
        },
      };
      handleChange(PhobiasOtherDetailsInput);

      const PhobiasFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasFrequency",
          value: "",
        },
      };
      handleChange(PhobiasFrequencyInput);

      const PhobiasIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasIntensity",
          value: "",
        },
      };
      handleChange(PhobiasIntensityInput);

      const PhobiasDurationInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasDuration",
          value: "",
        },
      };
      handleChange(PhobiasDurationInput);
    }
  };

  const handleDIChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DILowIQInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILowIQ",
          checked: false,
        },
      };
      handleChange(DILowIQInput);

      const DISensoryImpairmentsInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISensoryImpairments",
          checked: false,
        },
      };
      handleChange(DISensoryImpairmentsInput);

      const DISpeechDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpeechDelays",
          checked: false,
        },
      };
      handleChange(DISpeechDelaysInput);

      const DIMotorCoordinationDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DIMotorCoordinationDelays",
          checked: false,
        },
      };
      handleChange(DIMotorCoordinationDelaysInput);

      const DILearningDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILearningDisorder",
          checked: false,
        },
      };
      handleChange(DILearningDisorderInput);

      const DISpatialDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpatialDelays",
          checked: false,
        },
      };
      handleChange(DISpatialDelaysInput);

      const DevelopmentalImpairmentsOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DevelopmentalImpairmentsOther",
          checked: false,
        },
      };
      handleChange(DevelopmentalImpairmentsOtherInput);

      const DevelopmentalImpairmentsOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DevelopmentalImpairmentsOtherDetails",
          value: "",
        },
      };
      handleChange(DevelopmentalImpairmentsOtherDetailsInput);
    }
  };

  const handleOtherSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OtherSymptomsDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDetails",
          value: "",
        },
      };
      handleChange(OtherSymptomsDetailsInput);

      const OtherSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsFrequency",
          value: "",
        },
      };
      handleChange(OtherSymptomsFrequencyInput);

      const OtherSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsIntensity",
          value: "",
        },
      };
      handleChange(OtherSymptomsIntensityInput);

      const OtherSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDuration",
          value: "",
        },
      };
      handleChange(OtherSymptomsDurationInput);
    }
  };

  const getFamilyMemberConcerns = (index) => {
    const concerns = [];
    const fmData = JSON.parse(data.FamilyHistoryDetails["@value"])[index];

    if (fmData.MentalHealth === "true") concerns.push("Mental Health");
    if (fmData.Medical === "true") concerns.push("Medical");
    if (fmData.Disability === "true") concerns.push("Disability");
    if (fmData.SubstanceAbuse === "true") concerns.push("Substance Abuse");
    if (fmData.Suicidal === "true") concerns.push("Suicidal");
    if (fmData.Legal === "true") concerns.push("Legal");
    if (fmData.Trauma === "true") concerns.push("Trauma");
    if (fmData.Perpetration === "true") concerns.push("Perpetration");
    if (fmData.Other === "true") concerns.push("Other");

    if (concerns.length > 0) return concerns.join(", ");
    else return "None reported";
  };

  const getTreatmentRecommendationStatus = (index) => {
    let statuses = [];
    const trData = JSON.parse(data.TreatmentRecommendations["@value"])[index];

    if (
      trData.Deferred === "true" ||
      (trData.Deferred !== "false" && trData.StatusDeferred === "1")
    )
      statuses.push("SAFY will be providing");

    if (
      trData.Referred === "true" ||
      (trData.Referred !== "false" && trData.StatusReferred === "1")
    )
      statuses.push("SAFY will be referring to another agency/individual");

    if (trData.Declined === "true") statuses.push("Declined by Client");

    return statuses.join(", ");
  };

  const sexAtBirthOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Intersex", name: "Intersex" },
    { _id: "Sex not listed", name: "Sex not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const genderIdentityOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Non-binary", name: "Non-binary" },
    { _id: "Agender", name: "Agender" },
    { _id: "Transgender (FtM)", name: "Transgender (FtM)" },
    { _id: "Transgender (MtF)", name: "Transgender (MtF)" },
    { _id: "Identity not listed", name: "Identity not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pronounOptions = [
    { _id: "She/Her/Hers", name: "She/Her/Hers" },
    { _id: "He/Him/His", name: "He/Him/His" },
    { _id: "They/Them/Their", name: "They/Them/Their" },
    { _id: "Prefer to self-describe", name: "Prefer to self-describe" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pseTypeOptions = [
    { _id: "Initial", name: "Initial" },
    { _id: "Review/Update", name: "Review/Update" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "N/A", name: "N/A" },
  ];

  const yesNoUnknownOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const yesNoNoneOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None reported", name: "None reported" },
  ];

  const yesNoNoneNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None reported", name: "None reported" },
    { _id: "N/A", name: "N/A" },
  ];

  const educationOptions = [
    {
      _id: "Currently Enrolled in School/Client is School Aged",
      name: "Currently Enrolled in School/Client is School Aged",
    },
    {
      _id: "Client has not started school",
      name: "Client has not started school",
    },
    { _id: "Graduated", name: "Graduated" },
    { _id: "GED", name: "GED" },
    { _id: "Dropout/Did not complete", name: "Dropout/Did not complete" },
  ];

  const functioningLevelOptions = [
    { _id: "Age Appropriate", name: "Age Appropriate" },
    { _id: "Delayed", name: "Delayed" },
    { _id: "Advanced", name: "Advanced" },
    { _id: "N/A", name: "N/A" },
    { _id: "Unable to Assess", name: "Unable to Assess" },
  ];

  const useOfProtectionOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Not with Consistency", name: "Not with Consistency" },
    { _id: "N/A", name: "N/A" },
  ];

  const sexualOrientationOptions = [
    {
      _id: "Heterosexual",
      name: "Heterosexual",
    },
    {
      _id: "Gay",
      name: "Gay",
    },
    { _id: "Lesbian", name: "Lesbian" },
    { _id: "Bisexual", name: "Bisexual" },
    { _id: "Pansexual", name: "Pansexual" },
    { _id: "Polysexual", name: "Polysexual" },
    { _id: "Asexual", name: "Asexual" },
    { _id: "Queer/Questioning", name: "Queer/Questioning" },
    { _id: "Orientation not listed", name: "Orientation not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pregnancyOptions = [
    { _id: "Current", name: "Current" },
    { _id: "Past", name: "Past" },
    { _id: "None Reported", name: "None Reported" },
    { _id: "N/A", name: "N/A" },
  ];

  const traumaOptions = [
    { _id: "Physical Abuse", name: "Physical Abuse" },
    { _id: "Sexual Abuse", name: "Sexual Abuse" },
    { _id: "Neglect", name: "Neglect" },
    { _id: "Emotional Abuse", name: "Emotional Abuse" },
    { _id: "Domestic Violence", name: "Domestic Violence" },
    { _id: "Accident", name: "Accident" },
    { _id: "Acts of Violence", name: "Acts of Violence" },
    { _id: "Bullying", name: "Bullying" },
    { _id: "Death/Tragedy", name: "Death/Tragedy" },
    { _id: "Natural Disaster", name: "Natural Disaster" },
    { _id: "Community Violence", name: "Community Violence" },
    { _id: "Medical/Serious Illness", name: "Medical/Serious Illness" },
    { _id: "Abandonment", name: "Abandonment" },
    { _id: "War/Terrorism", name: "War/Terrorism" },
    { _id: "Exploitation/Trafficking", name: "Exploitation/Trafficking" },
    { _id: "Criminal Activity", name: "Criminal Activity" },
    { _id: "Caregiver Disruptions", name: "Caregiver Disruptions" },
    { _id: "Kidnapping", name: "Kidnapping" },
    { _id: "Other", name: "Other" },
  ];

  const statusOptions = [
    { _id: "Victim", name: "Victim" },
    { _id: "Witnessed", name: "Witnessed" },
    { _id: "Suspected", name: "Suspected" },
    { _id: "Threatened", name: "Threatened" },
    { _id: "Perceived", name: "Perceived" },
    { _id: "Repeated Exposure", name: "Repeated Exposure" },
    { _id: "Indirect Exposure", name: "Indirect Exposure" },
  ];

  const timeframeOptions = [
    { _id: "None/Never", name: "None/Never" },
    { _id: "Current", name: "Current" },
    { _id: "Last 30 Days", name: "Last 30 Days" },
    { _id: "Last 6 Months", name: "Last 6 Months" },
    { _id: "Last 6-12 Months", name: "Last 6-12 Months" },
    { _id: "More Than 12 Months Ago", name: "More Than 12 Months Ago" },
  ];

  const riskOptions = [
    { _id: "None Noted", name: "None Noted" },
    { _id: "Low", name: "Low" },
    { _id: "Moderate", name: "Moderate" },
    { _id: "High", name: "High" },
  ];

  const treatmentRecommendationOptions = [
    {
      _id: "Medical/dental/vision",
      name: "Medical/dental/vision",
    },
    {
      _id: "Educational/school",
      name: "Educational/school",
    },
    {
      _id: "Development of social/interpersonal skills",
      name: "Development of social/interpersonal skills",
    },
    {
      _id: "Specific skills training",
      name: "Specific skills training",
    },
    {
      _id: "Foster Family Support",
      name: "Foster Family Support",
    },
    {
      _id: "Individual Counseling/Therapy",
      name: "Individual Counseling/Therapy",
    },
    { _id: "Family Counseling/Therapy", name: "Family Counseling/Therapy" },
    { _id: "Group Counseling/Therapy", name: "Group Counseling/Therapy" },
    { _id: "Basic Living Skills", name: "Basic Living Skills" },
    { _id: "Mental Health Consultation", name: "Mental Health Consultation" },
    { _id: "Family Support Services", name: "Family Support Services" },
    { _id: "Case Management Services", name: "Case Management Services" },
    { _id: "Psychoeducation", name: "Psychoeducation" },
    { _id: "Rehabilitation Skills", name: "Rehabilitation Skills" },
    {
      _id: "Psychosocial Rehabilitation Services (PSR)",
      name: "Psychosocial Rehabilitation Services (PSR)",
    },
    {
      _id: "Early Childhood Intervention Services",
      name: "Early Childhood Intervention Services",
    },
    { _id: "Mentoring", name: "Mentoring" },
    {
      _id: "Substance Abuse Specific Treatment",
      name: "Substance Abuse Specific Treatment",
    },
    {
      _id: "Psychiatric Evaluation",
      name: "Psychiatric Evaluation",
    },
    {
      _id: "Psychological Evaluation",
      name: "Psychological Evaluation",
    },
    {
      _id: "Neurological Evaluation",
      name: "Neurological Evaluation",
    },
    {
      _id: "Educational Evaluation",
      name: "Educational Evaluation",
    },
    {
      _id: "Medical Evaluation",
      name: "Medical Evaluation",
    },
    {
      _id: "Speech Evaluation",
      name: "Speech Evaluation",
    },
    {
      _id: "Dental Evaluation",
      name: "Dental Evaluation",
    },
    {
      _id: "Alcohol/Drug Evaluation",
      name: "Alcohol/Drug Evaluation",
    },
    {
      _id: "Vocational Evaluation",
      name: "Vocational Evaluation",
    },
    {
      _id: "Occupational Evaluation",
      name: "Occupational Evaluation",
    },
    {
      _id: "Visual/Auditory Evaluation",
      name: "Visual/Auditory Evaluation",
    },
    {
      _id: "Nutritional Evaluation",
      name: "Nutritional Evaluation",
    },
    {
      _id: "Other",
      name: "Other",
    },
  ];

  const outcomeOptions = [
    {
      _id: "Successful",
      name: "Successful",
    },
    {
      _id: "Unsuccessful",
      name: "Unsuccessful",
    },
    {
      _id: "Did not complete",
      name: "Did not complete",
    },
    {
      _id: "Other",
      name: "Other",
    },
  ];

  const substanceTypeOptions = [
    {
      _id: "Tobacco Related Products",
      name: "Tobacco Related Products",
    },
    {
      _id: "Alcohol",
      name: "Alcohol",
    },
    {
      _id: "Illegal Drugs",
      name: "Illegal Drugs",
    },
    {
      _id: "Prescription Drugs",
      name: "Prescription Drugs",
    },
    {
      _id: "Synthetic Drugs/Substances",
      name: "Synthetic Drugs/Substances",
    },
  ];

  const currentPastOptions = [
    {
      _id: "Current",
      name: "Current",
    },
    {
      _id: "Past",
      name: "Past",
    },
  ];

  const treatmentTypeOptions = [
    {
      _id: "Outpatient",
      name: "Outpatient",
    },
    {
      _id: "Intensive Outpatient",
      name: "Intensive Outpatient",
    },
    {
      _id: "Residential",
      name: "Residential",
    },
    {
      _id: "Detox",
      name: "Detox",
    },
    {
      _id: "Hospital",
      name: "Hospital",
    },
    {
      _id: "Court-Ordered Treatment",
      name: "Court-Ordered Treatment",
    },
    {
      _id: "Other",
      name: "Other",
    },
  ];

  const awarenessOptions = [
    {
      _id: "Yes",
      name: "Yes",
    },
    {
      _id: "No",
      name: "No",
    },
    {
      _id: "Aware but No Recall/Memories",
      name: "Aware but No Recall/Memories",
    },
  ];

  const assessmentNeedOptions = [
    { _id: "Anxiety", name: "Anxiety" },
    { _id: "Alcohol/Drug Use", name: "Alcohol/Drug Use" },
    { _id: "Depression", name: "Depression" },
    { _id: "Neurological", name: "Neurological" },
    { _id: "Family Functioning", name: "Family Functioning" },
    { _id: "Human Trafficking", name: "Human Trafficking" },
    { _id: "Stages of Change", name: "Stages of Change" },
    { _id: "Trauma", name: "Trauma" },
    { _id: "Other", name: "Other" },
  ];

  const safetyPlanStatusOptions = [
    { _id: "None", name: "None" },
    { _id: "Active/Effective", name: "Active/Effective" },
    { _id: "Ineffective", name: "Ineffective" },
    { _id: "Updates Completed", name: "Updates Completed" },
    { _id: "Not Needed", name: "Not Needed" },
    { _id: "Will be completed", name: "Will be completed" },
  ];

  const buildClinicalNarrativeSummary = () => {
    if (!formDisabled) {
      let summary = "";

      // Presenting Problem & Strengths
      if (
        data.PresentingProblem["@value"] &&
        data.PresentingProblem["@value"] !== ""
      ) {
        summary += `Circumstances Leading to Seeking Therapy: ${data.PresentingProblem["@value"]}\r\n\r\n`;
      }

      if (
        data.CulturalGroup["@value"] !== undefined &&
        data.CulturalGroup["@value"] !== ""
      ) {
        summary += `Identified cultural group: ${data.PresentingProblem["@value"]}\r\n\r\n`;
      }

      //#region Mental Status
      let mentalStatusSummary = "";
      let appearanceSummary = "";
      let behaviorSummary = "";
      let speechSummary = "";
      let moodSummary = "";
      let affectSummary = "";
      let perceptionSummary = "";
      let cognitionSummary = "";
      let insightSummary = "";

      if (
        data.AppropriateAppearance["@value"] &&
        data.AppropriateAppearance["@value"] === "true"
      )
        appearanceSummary += "Appropriate, ";

      if (data.Neat["@value"] && data.Neat["@value"] === "true")
        appearanceSummary += "Neat, ";

      if (data.Disheveled["@value"] && data.Disheveled["@value"] === "true")
        appearanceSummary += "Disheveled, ";

      if (
        data.BizarreAppearance["@value"] &&
        data.BizarreAppearance["@value"] === "true"
      )
        appearanceSummary += "Bizarre, ";

      if (
        data.OtherAppearance["@value"] &&
        data.OtherAppearance["@value"] === "true" &&
        data.OtherAppearanceDetails["@value"] &&
        data.OtherAppearanceDetails["@value"] !== ""
      )
        appearanceSummary += `Other: ${data.OtherAppearanceDetails["@value"]}, `;

      if (appearanceSummary !== "")
        mentalStatusSummary += `\r\nAppearance: ${appearanceSummary.slice(
          0,
          -2
        )}; `;

      if (data.Cooperative["@value"] && data.Cooperative["@value"] === "true")
        behaviorSummary += "Cooperative, ";

      if (data.Guarded["@value"] && data.Guarded["@value"] === "true")
        behaviorSummary += "Guarded, ";

      if (data.Hyperactive["@value"] && data.Hyperactive["@value"] === "true")
        behaviorSummary += "Hyperactive, ";

      if (data.Agitated["@value"] && data.Agitated["@value"] === "true")
        behaviorSummary += "Agitated, ";

      if (data.Paranoid["@value"] && data.Paranoid["@value"] === "true")
        behaviorSummary += "Paranoid, ";

      if (data.Tics["@value"] && data.Tics["@value"] === "true")
        behaviorSummary += "Tics, ";

      if (data.Impulsive["@value"] && data.Impulsive["@value"] === "true")
        behaviorSummary += "Impulsive, ";

      if (data.Aggressive["@value"] && data.Aggressive["@value"] === "true")
        behaviorSummary += "Aggressive, ";

      if (data.Withdrawn["@value"] && data.Withdrawn["@value"] === "true")
        behaviorSummary += "Withdrawn, ";

      if (
        data.BizarreBehavior["@value"] &&
        data.BizarreBehavior["@value"] === "true"
      )
        behaviorSummary += "Bizarre, ";

      if (data.Rigidity["@value"] && data.Rigidity["@value"] === "true")
        behaviorSummary += "Rigidity, ";

      if (
        data.OtherBehavior["@value"] &&
        data.OtherBehavior["@value"] === "true" &&
        data.OtherBehaviorDetails["@value"] &&
        data.OtherBehaviorDetails["@value"] !== ""
      )
        behaviorSummary += `Other: ${data.OtherBehaviorDetails["@value"]}, `;

      if (behaviorSummary !== "")
        mentalStatusSummary += `\r\nBehavior: ${behaviorSummary.slice(
          0,
          -2
        )}; `;

      if (data.Normal["@value"] && data.Normal["@value"] === "true")
        speechSummary += "Normal, ";

      if (data.Slurred["@value"] && data.Slurred["@value"] === "true")
        speechSummary += "Slurred, ";

      if (data.Pressured["@value"] && data.Pressured["@value"] === "true")
        speechSummary += "Pressured, ";

      if (data.Tangential["@value"] && data.Tangential["@value"] === "true")
        speechSummary += "Tangential, ";

      if (data.Incoherent["@value"] && data.Incoherent["@value"] === "true")
        speechSummary += "Incoherent, ";

      if (data.Stutters["@value"] && data.Stutters["@value"] === "true")
        speechSummary += "Stutters, ";

      if (
        data.OtherSpeech["@value"] &&
        data.OtherSpeech["@value"] === "true" &&
        data.OtherSpeechDetails["@value"] &&
        data.OtherSpeechDetails["@value"] !== ""
      )
        speechSummary += `Other: ${data.OtherSpeechDetails["@value"]}, `;

      if (speechSummary !== "")
        mentalStatusSummary += `\r\nSpeech: ${speechSummary.slice(0, -2)}; `;

      if (data.Euthymic["@value"] && data.Euthymic["@value"] === "true")
        moodSummary += "Euthymic, ";

      if (data.Anxious["@value"] && data.Anxious["@value"] === "true")
        moodSummary += "Anxious, ";

      if (data.Angry["@value"] && data.Angry["@value"] === "true")
        moodSummary += "Angry, ";

      if (data.Depressed["@value"] && data.Depressed["@value"] === "true")
        moodSummary += "Depressed, ";

      if (data.Euphoric["@value"] && data.Euphoric["@value"] === "true")
        moodSummary += "Euphoric, ";

      if (data.Irritable["@value"] && data.Irritable["@value"] === "true")
        moodSummary += "Irritable, ";

      if (data.Fearful["@value"] && data.Fearful["@value"] === "true")
        moodSummary += "Fearful, ";

      if (
        data.OtherMood["@value"] &&
        data.OtherMood["@value"] === "true" &&
        data.OtherMoodDetails["@value"] &&
        data.OtherMoodDetails["@value"] !== ""
      )
        moodSummary += `Other: ${data.OtherMoodDetails["@value"]}, `;

      if (moodSummary !== "")
        mentalStatusSummary += `\r\nMood: ${moodSummary.slice(0, -2)}; `;

      if (data.FullRange["@value"] && data.FullRange["@value"] === "true")
        affectSummary += "Full range, ";

      if (data.Restricted["@value"] && data.Restricted["@value"] === "true")
        affectSummary += "Restricted, ";

      if (data.Blunted["@value"] && data.Blunted["@value"] === "true")
        affectSummary += "Blunted, ";

      if (data.Flat["@value"] && data.Flat["@value"] === "true")
        affectSummary += "Flat, ";

      if (
        data.Inappropriate["@value"] &&
        data.Inappropriate["@value"] === "true"
      )
        affectSummary += "Inappropriate, ";

      if (
        data.OtherAffect["@value"] &&
        data.OtherAffect["@value"] === "true" &&
        data.OtherAffectDetails["@value"] &&
        data.OtherAffectDetails["@value"] !== ""
      )
        affectSummary += `Other: ${data.OtherAffectDetails["@value"]}, `;

      if (affectSummary !== "")
        mentalStatusSummary += `\r\nAffect: ${affectSummary.slice(0, -2)}; `;

      if (data.Logical["@value"] && data.Logical["@value"] === "true")
        perceptionSummary += "Logical, ";

      if (
        data.Preoccupations["@value"] &&
        data.Preoccupations["@value"] === "true"
      )
        perceptionSummary += "Preoccupations, ";

      if (data.Delusions["@value"] && data.Delusions["@value"] === "true")
        perceptionSummary += "Delusions, ";

      if (
        data.Hallucinations["@value"] &&
        data.Hallucinations["@value"] === "true"
      )
        perceptionSummary += "Hallucinations, ";

      if (data.Racing["@value"] && data.Racing["@value"] === "true")
        perceptionSummary += "Racing, ";

      if (data.Disorganized["@value"] && data.Disorganized["@value"] === "true")
        perceptionSummary += "Disorganized, ";

      if (data.Concrete["@value"] && data.Concrete["@value"] === "true")
        perceptionSummary += "Concrete, ";

      if (
        data.SuicidalIdeation["@value"] &&
        data.SuicidalIdeation["@value"] === "true"
      )
        perceptionSummary += "Suicidal Ideation, ";

      if (
        data.HomicidalIdeation["@value"] &&
        data.HomicidalIdeation["@value"] === "true"
      )
        perceptionSummary += "Homicidal Ideation, ";

      if (
        data.OtherPerception["@value"] &&
        data.OtherPerception["@value"] === "true" &&
        data.OtherPerceptionDetails["@value"] &&
        data.OtherPerceptionDetails["@value"] !== ""
      )
        perceptionSummary += `Other: ${data.OtherPerceptionDetails["@value"]}, `;

      if (perceptionSummary !== "")
        mentalStatusSummary += `\r\nThought Process/Perception: ${perceptionSummary.slice(
          0,
          -2
        )}; `;

      if (data.NoImpairment["@value"] && data.NoImpairment["@value"] === "true")
        cognitionSummary += "No Impairment, ";

      if (
        data.ShortAttentionSpan["@value"] &&
        data.ShortAttentionSpan["@value"] === "true"
      )
        cognitionSummary += "Short Attention Span, ";

      if (
        data.DifficultyConcentrating["@value"] &&
        data.DifficultyConcentrating["@value"] === "true"
      )
        cognitionSummary += "Difficulty Concentrating, ";

      if (
        data.Distractibility["@value"] &&
        data.Distractibility["@value"] === "true"
      )
        cognitionSummary += "Distractibility, ";

      if (
        data.MemoryImpairment["@value"] &&
        data.MemoryImpairment["@value"] === "true"
      )
        cognitionSummary += "Memory Impairment, ";

      if (
        data.LowIntelligence["@value"] &&
        data.LowIntelligence["@value"] === "true"
      )
        cognitionSummary += "Low Intelligence, ";

      if (
        data.OtherCognition["@value"] &&
        data.OtherCognition["@value"] === "true" &&
        data.OtherCognitionDetails["@value"] &&
        data.OtherCognitionDetails["@value"] !== ""
      )
        cognitionSummary += `Other: ${data.OtherCognitionDetails["@value"]}, `;

      if (cognitionSummary !== "")
        mentalStatusSummary += `\r\nCognition/Intellectual Functioning: ${cognitionSummary.slice(
          0,
          -2
        )}; `;

      if (
        data.AppropriateInsight["@value"] &&
        data.AppropriateInsight["@value"] === "true"
      )
        insightSummary += "Appropriate, ";

      if (data.Fair["@value"] && data.Fair["@value"] === "true")
        insightSummary += "Fair, ";

      if (data.Impaired["@value"] && data.Impaired["@value"] === "true")
        insightSummary += "Impaired, ";

      if (
        data.OtherInsight["@value"] &&
        data.OtherInsight["@value"] === "true" &&
        data.OtherInsightDetails["@value"] &&
        data.OtherInsightDetails["@value"] !== ""
      )
        insightSummary += `Other: ${data.OtherInsightDetails["@value"]}, `;

      if (insightSummary !== "")
        mentalStatusSummary += `\r\nInsight/Judgment: ${insightSummary.slice(
          0,
          -2
        )}; `;

      if (
        data.MentalStatusComments["@value"] &&
        data.MentalStatusComments["@value"] !== ""
      )
        mentalStatusSummary += `\r\nComments/Observations: ${data.MentalStatusComments["@value"]}; `;

      if (mentalStatusSummary !== "")
        summary += `Mental Status:${mentalStatusSummary.trimEnd()}\r\n\r\n`;
      //#endregion

      //#region Symptom Checklist
      let symptomSummary = "";

      if (
        data.TraumaSymptoms !== undefined &&
        data.TraumaSymptoms["@value"] &&
        data.TraumaSymptoms["@value"] === "true"
      ) {
        let traumaSymptoms =
          "Symptoms of Trauma and Other Stressor-Related Disorders: ";

        if (
          data.TraumaticExposure !== undefined &&
          data.TraumaticExposure["@value"] &&
          data.TraumaticExposure["@value"] === "true"
        )
          traumaSymptoms += "exposure to a traumatic event, ";

        if (
          data.AvoidantTrauma !== undefined &&
          data.AvoidantTrauma["@value"] &&
          data.AvoidantTrauma["@value"] === "true"
        )
          traumaSymptoms += "persistent avoidant behavior, ";

        if (
          data.HypervigilanceTrauma !== undefined &&
          data.HypervigilanceTrauma["@value"] &&
          data.HypervigilanceTrauma["@value"] === "true"
        )
          traumaSymptoms += "hypervigilance, ";

        if (
          data.IntrusiveDreams !== undefined &&
          data.IntrusiveDreams["@value"] &&
          data.IntrusiveDreams["@value"] === "true"
        )
          traumaSymptoms += "intrusive dreams/memories, ";

        if (
          data.NegativeThoughts !== undefined &&
          data.NegativeThoughts["@value"] &&
          data.NegativeThoughts["@value"] === "true"
        )
          traumaSymptoms += "negative thoughts and mood, ";

        if (
          data.CocentrationProblems !== undefined &&
          data.CocentrationProblems["@value"] &&
          data.CocentrationProblems["@value"] === "true"
        )
          traumaSymptoms += "problems with concentration, ";

        if (
          data.RecklessBehaviorTrauma !== undefined &&
          data.RecklessBehaviorTrauma["@value"] &&
          data.RecklessBehaviorTrauma["@value"] === "true"
        )
          traumaSymptoms += "irritable or reckless behavior, ";

        if (
          data.SleepDisturbancesTrauma !== undefined &&
          data.SleepDisturbancesTrauma["@value"] &&
          data.SleepDisturbancesTrauma["@value"] === "true"
        )
          traumaSymptoms += "sleep disturbances, ";

        if (
          data.OtherTrauma !== undefined &&
          data.OtherTraumaDetails !== undefined &&
          data.OtherTrauma["@value"] &&
          data.OtherTrauma["@value"] === "true" &&
          data.OtherTraumaDetails["@value"] &&
          data.OtherTraumaDetails["@value"] !== ""
        )
          traumaSymptoms += `other Trauma symptom(s) - ${data.OtherTraumaDetails["@value"]}, `;

        if (
          data.TraumaSymptomsFrequency !== undefined &&
          data.TraumaSymptomsFrequency["@value"] &&
          data.TraumaSymptomsFrequency["@value"] !== ""
        )
          traumaSymptoms += `frequency - ${data.TraumaSymptomsFrequency["@value"]}, `;

        if (
          data.TraumaSymptomsIntensity !== undefined &&
          data.TraumaSymptomsIntensity["@value"] &&
          data.TraumaSymptomsIntensity["@value"] !== ""
        )
          traumaSymptoms += `intensity - ${data.TraumaSymptomsIntensity["@value"]}, `;

        if (
          data.TraumaSymptomsDuration !== undefined &&
          data.TraumaSymptomsDuration["@value"] &&
          data.TraumaSymptomsDuration["@value"] !== ""
        )
          traumaSymptoms += `duration - ${data.TraumaSymptomsDuration["@value"]}, `;

        if (
          data.TraumaSymptomsOnset !== undefined &&
          data.TraumaSymptomsOnset["@value"] &&
          data.TraumaSymptomsOnset["@value"] !== ""
        )
          traumaSymptoms += `onset - ${data.TraumaSymptomsOnset["@value"]}, `;

        console.log("traumaSymptoms: ", traumaSymptoms);
        symptomSummary += `${traumaSymptoms.slice(0, -2)}.\r\n`;
      }
      if (
        data.AnxietyDisorder !== undefined &&
        data.AnxietyDisorder["@value"] &&
        data.AnxietyDisorder["@value"] === "true"
      ) {
        let anxietySymptoms = "Symptoms of Anxiety: ";

        if (
          data.AnxietyDisorderExcessWorry !== undefined &&
          data.AnxietyDisorderExcessWorry["@value"] &&
          data.AnxietyDisorderExcessWorry["@value"] === "true"
        )
          anxietySymptoms += "excess worry, ";

        if (
          data.AnxietyDisorderEasilyFatigued !== undefined &&
          data.AnxietyDisorderEasilyFatigued["@value"] &&
          data.AnxietyDisorderEasilyFatigued["@value"] === "true"
        )
          anxietySymptoms += "easily fatigued, ";

        if (
          data.MuscleTension !== undefined &&
          data.MuscleTension["@value"] &&
          data.MuscleTension["@value"] === "true"
        )
          anxietySymptoms += "muscle tension, ";

        if (
          data.Overworried !== undefined &&
          data.Overworried["@value"] &&
          data.Overworried["@value"] === "true"
        )
          anxietySymptoms += "difficulty controlling worry, ";

        if (
          data.AnxietyDisorderDifficultyConcentrating !== undefined &&
          data.AnxietyDisorderDifficultyConcentrating["@value"] &&
          data.AnxietyDisorderDifficultyConcentrating["@value"] === "true"
        )
          anxietySymptoms += "difficulty concentrating, ";

        if (
          data.SleepDisturbancesAnxiety !== undefined &&
          data.SleepDisturbancesAnxiety["@value"] &&
          data.SleepDisturbancesAnxiety["@value"] === "true"
        )
          anxietySymptoms += "sleep difficulties, ";

        if (
          data.RestlessAnxiety !== undefined &&
          data.RestlessAnxiety["@value"] &&
          data.RestlessAnxiety["@value"] === "true"
        )
          anxietySymptoms += "restless, ";

        if (
          data.Irritability !== undefined &&
          data.Irritability["@value"] &&
          data.Irritability["@value"] === "true"
        )
          anxietySymptoms += "irritability, ";

        if (
          data.OtherAnxiety !== undefined &&
          data.OtherAnxietyDetails !== undefined &&
          data.OtherAnxiety["@value"] &&
          data.OtherAnxiety["@value"] === "true" &&
          data.OtherAnxietyDetails["@value"] &&
          data.OtherAnxietyDetails["@value"] !== ""
        )
          anxietySymptoms += `other Anxiety Disorder symptom(s) - ${data.OtherAnxietyDetails["@value"]}, `;

        if (
          data.AnxietyDisorderFrequency !== undefined &&
          data.AnxietyDisorderFrequency["@value"] &&
          data.AnxietyDisorderFrequency["@value"] !== ""
        )
          anxietySymptoms += `frequency - ${data.AnxietyDisorderFrequency["@value"]}, `;

        if (
          data.AnxietyDisorderIntensity !== undefined &&
          data.AnxietyDisorderIntensity["@value"] &&
          data.AnxietyDisorderIntensity["@value"] !== ""
        )
          anxietySymptoms += `intensity - ${data.AnxietyDisorderIntensity["@value"]}, `;

        if (
          data.AnxietyDisorderDuration !== undefined &&
          data.AnxietyDisorderDuration["@value"] &&
          data.AnxietyDisorderDuration["@value"] !== ""
        )
          anxietySymptoms += `duration - ${data.AnxietyDisorderDuration["@value"]}, `;

        console.log("anxietySymptoms: ", anxietySymptoms);
        symptomSummary += `${anxietySymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.DepressionSymptoms !== undefined &&
        data.DepressionSymptoms["@value"] &&
        data.DepressionSymptoms["@value"] === "true"
      ) {
        let depressionSymptoms = "Symptoms of Depression: ";

        if (
          data.DepressedMood !== undefined &&
          data.DepressedMood["@value"] &&
          data.DepressedMood["@value"] === "true"
        )
          depressionSymptoms +=
            "depressed mood most of the day, nearly every day, ";

        if (
          data.SleepChanges !== undefined &&
          data.SleepChanges["@value"] &&
          data.SleepChanges["@value"] === "true"
        )
          depressionSymptoms += "insomnia or hypersomnia, ";

        if (data.Guilt["@value"] && data.Guilt["@value"] === "true")
          depressionSymptoms += "feelings of worthlessness/guilt, ";

        if (
          data.DiminishedInterest !== undefined &&
          data.DiminishedInterest["@value"] &&
          data.DiminishedInterest["@value"] === "true"
        )
          depressionSymptoms +=
            "diminished interest or pleasure in most daily activities, ";

        if (
          data.PurposelessMovement !== undefined &&
          data.PurposelessMovement["@value"] &&
          data.PurposelessMovement["@value"] === "true"
        )
          depressionSymptoms += "purposeless movement, ";

        if (
          data.DifficultyConcentratingDepression !== undefined &&
          data.DifficultyConcentratingDepression["@value"] &&
          data.DifficultyConcentratingDepression["@value"] === "true"
        )
          depressionSymptoms += "inability to think/concentrate, ";

        if (
          data.WeightChanges !== undefined &&
          data.WeightChanges["@value"] &&
          data.WeightChanges["@value"] === "true"
        )
          depressionSymptoms += "significant weight loss or weight gain, ";

        if (
          data.FatigueDepression !== undefined &&
          data.FatigueDepression["@value"] &&
          data.FatigueDepression["@value"] === "true"
        )
          depressionSymptoms += "fatigue, ";

        if (
          data.ThoughtsOfDeath !== undefined &&
          data.ThoughtsOfDeath["@value"] &&
          data.ThoughtsOfDeath["@value"] === "true"
        )
          depressionSymptoms += "recurrent thoughts of death, ";

        if (
          data.OtherDepression !== undefined &&
          data.OtherDepressionDetails !== undefined &&
          data.OtherDepression["@value"] &&
          data.OtherDepression["@value"] === "true" &&
          data.OtherDepressionDetails["@value"] &&
          data.OtherDepressionDetails["@value"] !== ""
        )
          depressionSymptoms += `other Depression symptom(s) - ${data.OtherDepressionDetails["@value"]}, `;

        if (
          data.DepressionFrequency !== undefined &&
          data.DepressionFrequency["@value"] &&
          data.DepressionFrequency["@value"] !== ""
        )
          depressionSymptoms += `frequency - ${data.DepressionFrequency["@value"]}, `;

        if (
          data.DepressionIntensity !== undefined &&
          data.DepressionIntensity["@value"] &&
          data.DepressionIntensity["@value"] !== ""
        )
          depressionSymptoms += `intensity - ${data.DepressionIntensity["@value"]}, `;

        if (
          data.DepressionDuration !== undefined &&
          data.DepressionDuration["@value"] &&
          data.DepressionDuration["@value"] !== ""
        )
          depressionSymptoms += `duration - ${data.DepressionDuration["@value"]}, `;

        console.log("depressionSymptoms: ", depressionSymptoms);
        symptomSummary += `${depressionSymptoms.slice(0, -2)}..\r\n`;
      }

      if (
        data.AttentionHyperactivity !== undefined &&
        data.AttentionHyperactivity["@value"] &&
        data.AttentionHyperactivity["@value"] === "true"
      ) {
        let attentionSymptoms = "Symptoms of Attention and Hyperactivity: ";

        if (
          data.AttentionHyperactivityEasilyDistracted !== undefined &&
          data.AttentionHyperactivityEasilyDistracted["@value"] &&
          data.AttentionHyperactivityEasilyDistracted["@value"] === "true"
        )
          attentionSymptoms += "easily distracted, ";

        if (
          data.DifficultyFollowThrough !== undefined &&
          data.DifficultyFollowThrough["@value"] &&
          data.DifficultyFollowThrough["@value"] === "true"
        )
          attentionSymptoms += "difficulty following-through, ";

        if (
          data.Forgetful !== undefined &&
          data.Forgetful["@value"] &&
          data.Forgetful["@value"] === "true"
        )
          attentionSymptoms += "forgetful, ";

        if (
          data.DifficultyListening !== undefined &&
          data.DifficultyListening["@value"] &&
          data.DifficultyListening["@value"] === "true"
        )
          attentionSymptoms += "difficulty listening, ";

        if (
          data.DisorganizedAttention !== undefined &&
          data.DisorganizedAttention["@value"] &&
          data.DisorganizedAttention["@value"] === "true"
        )
          attentionSymptoms += "disorganized in tasks and activities, ";

        if (
          data.Fidgety !== undefined &&
          data.Fidgety["@value"] &&
          data.Fidgety["@value"] === "true"
        )
          attentionSymptoms += "fidgety, ";

        if (
          data.LackOfDetails !== undefined &&
          data.LackOfDetails["@value"] &&
          data.LackOfDetails["@value"] === "true"
        )
          attentionSymptoms += "lack of attention to detail, ";

        if (
          data.LosesThings !== undefined &&
          data.LosesThings["@value"] &&
          data.LosesThings["@value"] === "true"
        )
          attentionSymptoms += "loses things, ";

        if (
          data.InterruptsOthers !== undefined &&
          data.InterruptsOthers["@value"] &&
          data.InterruptsOthers["@value"] === "true"
        )
          attentionSymptoms += "interrupts others, ";

        if (
          data.UnableToSitStill !== undefined &&
          data.UnableToSitStill["@value"] &&
          data.UnableToSitStill["@value"] === "true"
        )
          attentionSymptoms += "unable to sit still, ";

        if (
          data.ExcessiveTalking !== undefined &&
          data.ExcessiveTalking["@value"] &&
          data.ExcessiveTalking["@value"] === "true"
        )
          attentionSymptoms += "excessive talking, ";

        if (
          data.DifficultyWaiting !== undefined &&
          data.DifficultyWaiting["@value"] &&
          data.DifficultyWaiting["@value"] === "true"
        )
          attentionSymptoms += "difficulty waiting for their turn, ";

        if (
          data.OtherAttention !== undefined &&
          data.OtherAttentionDetails !== undefined &&
          data.OtherAttention["@value"] &&
          data.OtherAttention["@value"] === "true" &&
          data.OtherAttentionDetails["@value"] &&
          data.OtherAttentionDetails["@value"] !== ""
        )
          attentionSymptoms += `other Attention/Hyperactivity symptom(s) - ${data.OtherAttentionDetails["@value"]}, `;

        if (
          data.AttentionHyperactivityFrequency !== undefined &&
          data.AttentionHyperactivityFrequency["@value"] &&
          data.AttentionHyperactivityFrequency["@value"] !== ""
        )
          attentionSymptoms += `frequency - ${data.AttentionHyperactivityFrequency["@value"]}, `;

        if (
          data.AttentionHyperactivityIntensity !== undefined &&
          data.AttentionHyperactivityIntensity["@value"] &&
          data.AttentionHyperactivityIntensity["@value"] !== ""
        )
          attentionSymptoms += `intensity - ${data.AttentionHyperactivityIntensity["@value"]}, `;

        if (
          data.AttentionHyperactivityDuration !== undefined &&
          data.AttentionHyperactivityDuration["@value"] &&
          data.AttentionHyperactivityDuration["@value"] !== ""
        )
          attentionSymptoms += `duration - ${data.AttentionHyperactivityDuration["@value"]}, `;

        console.log("attentionSymptoms: ", attentionSymptoms);
        symptomSummary += `${attentionSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.SU !== undefined &&
        data.SU["@value"] &&
        data.SU["@value"] === "true"
      ) {
        let suSymptoms = "Symptoms of Substance Use Disorders: ";

        if (
          data.PatternOfSubstanceUse !== undefined &&
          data.PatternOfSubstanceUse["@value"] &&
          data.PatternOfSubstanceUse["@value"] === "true"
        )
          suSymptoms +=
            "takes substances in large amounts and/or over long periods of time, ";

        if (
          data.DesiresCutDownUse !== undefined &&
          data.DesiresCutDownUse["@value"] &&
          data.DesiresCutDownUse["@value"] === "true"
        )
          suSymptoms += "persistent desire to cut down on substance use, ";

        if (
          data.TimeSpentObtaining !== undefined &&
          data.TimeSpentObtaining["@value"] &&
          data.TimeSpentObtaining["@value"] === "true"
        )
          suSymptoms +=
            "spends a significant amount of time obtaining substance(s), ";

        if (
          data.Cravings !== undefined &&
          data.Cravings["@value"] &&
          data.Cravings["@value"] === "true"
        )
          suSymptoms += "cravings for substances, ";

        if (
          data.FailureOfResponsibilities !== undefined &&
          data.FailureOfResponsibilities["@value"] &&
          data.FailureOfResponsibilities["@value"] === "true"
        )
          suSymptoms +=
            "failure to fulfill major roles at work, school, or home, ";

        if (
          data.NegativeImpact !== undefined &&
          data.NegativeImpact["@value"] &&
          data.NegativeImpact["@value"] === "true"
        )
          suSymptoms += "use consistently negatively impacts relationships, ";

        if (
          data.AbandonsActivities !== undefined &&
          data.AbandonsActivities["@value"] &&
          data.AbandonsActivities["@value"] === "true"
        )
          suSymptoms += "giving up/reducing previously enjoyed activities, ";

        if (data.Tolerance["@value"] && data.Tolerance["@value"] === "true")
          suSymptoms += "tolerance, ";

        if (
          data.SUWithdrawal !== undefined &&
          data.SUWithdrawal["@value"] &&
          data.SUWithdrawal["@value"] === "true"
        )
          suSymptoms += "withdrawal, ";

        if (
          data.SUOther !== undefined &&
          data.SUOther["@value"] &&
          data.SUOther["@value"] === "true" &&
          data.SUOtherDetails["@value"] &&
          data.SUOtherDetails["@value"] !== ""
        )
          suSymptoms += `other Substance Use Disorder symptom(s) - ${data.SUOtherDetails["@value"]}, `;

        if (
          data.SubstanceUseFrequency !== undefined &&
          data.SubstanceUseFrequency["@value"] &&
          data.SubstanceUseFrequency["@value"] !== ""
        )
          suSymptoms += `frequency - ${data.SubstanceUseFrequency["@value"]}, `;

        if (
          data.SubstanceUseIntensity !== undefined &&
          data.SubstanceUseIntensity["@value"] &&
          data.SubstanceUseIntensity["@value"] !== ""
        )
          suSymptoms += `intensity - ${data.SubstanceUseIntensity["@value"]}, `;

        if (
          data.SubstanceUseDuration !== undefined &&
          data.SubstanceUseDuration["@value"] &&
          data.SubstanceUseDuration["@value"] !== ""
        )
          suSymptoms += `duration - ${data.SubstanceUseDuration["@value"]}, `;

        console.log("suSymptoms: ", suSymptoms);
        symptomSummary += `${suSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.DMDD !== undefined &&
        data.DMDD["@value"] &&
        data.DMDD["@value"] === "true"
      ) {
        let dmddSymptoms =
          "Symptoms of Disruptive Mood Dysregulation Disorder: ";

        if (
          data.Temper !== undefined &&
          data.Temper["@value"] &&
          data.Temper["@value"] === "true"
        )
          dmddSymptoms += "severe and recurrent temper outbursts, ";

        if (
          data.OutsideOutbursts !== undefined &&
          data.OutsideOutbursts["@value"] &&
          data.OutsideOutbursts["@value"] === "true"
        )
          dmddSymptoms += "outbursts are outside of developmental level, ";

        if (
          data.FrequentOutbursts !== undefined &&
          data.FrequentOutbursts["@value"] &&
          data.FrequentOutbursts["@value"] === "true"
        )
          dmddSymptoms += "outbursts occur three or more times per week, ";

        if (
          data.DMDDIrritable !== undefined &&
          data.DMDDIrritable["@value"] &&
          data.DMDDIrritable["@value"] === "true"
        )
          dmddSymptoms += "irritable/angry mood most of the day, ";

        if (
          data.OutburstsAroundOthers !== undefined &&
          data.OutburstsAroundOthers["@value"] &&
          data.OutburstsAroundOthers["@value"] === "true"
        )
          dmddSymptoms += "symptoms occur in home, school and/or with peers, ";

        if (
          data.DMDDOther !== undefined &&
          data.DMDDOther["@value"] &&
          data.DMDDOther["@value"] === "true" &&
          data.DMDDOtherDetails["@value"] &&
          data.DMDDOtherDetails["@value"] !== ""
        )
          dmddSymptoms += `other DMDD symptom(s) - ${data.DMDDOtherDetails["@value"]}, `;

        if (
          data.DMDDFrequency !== undefined &&
          data.DMDDFrequency["@value"] &&
          data.DMDDFrequency["@value"] !== ""
        )
          dmddSymptoms += `frequency - ${data.DMDDFrequency["@value"]}, `;

        if (
          data.DMDDIntensity !== undefined &&
          data.DMDDIntensity["@value"] &&
          data.DMDDIntensity["@value"] !== ""
        )
          dmddSymptoms += `intensity - ${data.DMDDIntensity["@value"]}, `;

        if (
          data.DMDDDuration !== undefined &&
          data.DMDDDuration["@value"] &&
          data.DMDDDuration["@value"] !== ""
        )
          dmddSymptoms += `duration - ${data.DMDDDuration["@value"]}, `;

        console.log("dmddSymptoms: ", dmddSymptoms);
        symptomSummary += `${dmddSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.ConductDisorder !== undefined &&
        data.ConductDisorder["@value"] &&
        data.ConductDisorder["@value"] === "true"
      ) {
        let conductDisorderSymptoms = "Symptoms of Conduct Disorder: ";

        if (data.Aggression["@value"] && data.Aggression["@value"] === "true")
          conductDisorderSymptoms += "aggression to people and/or animals, ";

        if (
          data.DestructionOfProperty !== undefined &&
          data.DestructionOfProperty["@value"] &&
          data.DestructionOfProperty["@value"] === "true"
        )
          conductDisorderSymptoms += "destruction of Property, ";

        if (
          data.Theft !== undefined &&
          data.Theft["@value"] &&
          data.Theft["@value"] === "true"
        )
          conductDisorderSymptoms += "deceitfulness or theft, ";

        if (
          data.SeriousViolations !== undefined &&
          data.SeriousViolations["@value"] &&
          data.SeriousViolations["@value"] === "true"
        )
          conductDisorderSymptoms += "serious violations of rules, ";

        if (
          data.OtherConductDisorder !== undefined &&
          data.OtherConductDisorderDetails !== undefined &&
          data.OtherConductDisorder["@value"] &&
          data.OtherConductDisorder["@value"] === "true" &&
          data.OtherConductDisorderDetails["@value"] &&
          data.OtherConductDisorderDetails["@value"] !== ""
        )
          conductDisorderSymptoms += `other Conduct Disorder symptom(s) - ${data.OtherConductDisorderDetails["@value"]}, `;

        if (
          data.ConductDisorderFrequency !== undefined &&
          data.ConductDisorderFrequency["@value"] &&
          data.ConductDisorderFrequency["@value"] !== ""
        )
          conductDisorderSymptoms += `frequency - ${data.ConductDisorderFrequency["@value"]}, `;

        if (
          data.ConductDisorderIntensity !== undefined &&
          data.ConductDisorderIntensity["@value"] &&
          data.ConductDisorderIntensity["@value"] !== ""
        )
          conductDisorderSymptoms += `intensity - ${data.ConductDisorderIntensity["@value"]}, `;

        if (
          data.ConductDisorderDuration !== undefined &&
          data.ConductDisorderDuration["@value"] &&
          data.ConductDisorderDuration["@value"] !== ""
        )
          conductDisorderSymptoms += `duration - ${data.ConductDisorderDuration["@value"]}, `;

        console.log("conductDisorderSymptoms: ", conductDisorderSymptoms);
        symptomSummary += `${conductDisorderSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.ODD !== undefined &&
        data.ODD["@value"] &&
        data.ODD["@value"] === "true"
      ) {
        let oddSymptoms = "Symptoms of Oppositional Defiance Disorder (ODD): ";

        if (
          data.IrritableODD !== undefined &&
          data.IrritableODD["@value"] &&
          data.IrritableODD["@value"] === "true"
        )
          oddSymptoms += "angry/irritable mood, ";

        if (
          data.Argumentative !== undefined &&
          data.Argumentative["@value"] &&
          data.Argumentative["@value"] === "true"
        )
          oddSymptoms += "argumentative/defiant, ";

        if (
          data.Vindictive !== undefined &&
          data.Vindictive["@value"] &&
          data.Vindictive["@value"] === "true"
        )
          oddSymptoms += "vindictive, ";

        if (
          data.OtherODD !== undefined &&
          data.OtherODDDetails !== undefined &&
          data.OtherODD["@value"] &&
          data.OtherODD["@value"] === "true" &&
          data.OtherODDDetails["@value"] &&
          data.OtherODDDetails["@value"] !== ""
        )
          oddSymptoms += `other ODD symptom(s) - ${data.OtherODDDetails["@value"]}, `;

        if (
          data.ODDFrequency !== undefined &&
          data.ODDFrequency["@value"] &&
          data.ODDFrequency["@value"] !== ""
        )
          oddSymptoms += `frequency - ${data.ODDFrequency["@value"]}, `;

        if (
          data.ODDIntensity !== undefined &&
          data.ODDIntensity["@value"] &&
          data.ODDIntensity["@value"] !== ""
        )
          oddSymptoms += `intensity - ${data.ODDIntensity["@value"]}, `;

        if (
          data.ODDDuration !== undefined &&
          data.ODDDuration["@value"] &&
          data.ODDDuration["@value"] !== ""
        )
          oddSymptoms += `duration - ${data.ODDDuration["@value"]}, `;

        console.log("oddSymptoms: ", oddSymptoms);
        symptomSummary += `${oddSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.OCD !== undefined &&
        data.OCD["@value"] &&
        data.OCD["@value"] === "true"
      ) {
        let ocdSymptoms = "Symptoms of Obsessive-Compulsive Disorder (OCD): ";

        if (
          data.OCDPresence !== undefined &&
          data.OCDPresence["@value"] &&
          data.OCDPresence["@value"] === "true"
        )
          ocdSymptoms += "presence of obsessions, compulsions, or both, ";

        if (
          data.Obsessions !== undefined &&
          data.Obsessions["@value"] &&
          data.Obsessions["@value"] === "true"
        )
          ocdSymptoms += "obsessions, ";

        if (
          data.TimeConsuming !== undefined &&
          data.TimeConsuming["@value"] &&
          data.TimeConsuming["@value"] === "true"
        )
          ocdSymptoms +=
            "obsessions or compulsions are time consuming and cause distress/impairment, ";

        if (
          data.Compulsions !== undefined &&
          data.Compulsions["@value"] &&
          data.Compulsions["@value"] === "true"
        )
          ocdSymptoms += "compulsions/repetitive behaviors or mental acts, ";

        if (
          data.ReduceAnxiety !== undefined &&
          data.ReduceAnxiety["@value"] &&
          data.ReduceAnxiety["@value"] === "true"
        )
          ocdSymptoms +=
            "behaviors and mental acts done to prevent or reduce anxiety or distress, ";

        if (
          data.SuppressUrges !== undefined &&
          data.SuppressUrges["@value"] &&
          data.SuppressUrges["@value"] === "true"
        )
          ocdSymptoms +=
            "attempts to ignore or suppress thoughts, urges or images with other thought or action, ";

        if (
          data.OCDOther !== undefined &&
          data.OCDOther["@value"] &&
          data.OCDOther["@value"] === "true" &&
          data.OCDOtherDetails["@value"] &&
          data.OCDOtherDetails["@value"] !== ""
        )
          ocdSymptoms += `other OCD symptom(s) - ${data.OCDOtherDetails["@value"]}, `;

        if (
          data.OCDFrequency !== undefined &&
          data.OCDFrequency["@value"] &&
          data.OCDFrequency["@value"] !== ""
        )
          ocdSymptoms += `frequency - ${data.OCDFrequency["@value"]}, `;

        if (
          data.OCDIntensity !== undefined &&
          data.OCDIntensity["@value"] &&
          data.OCDIntensity["@value"] !== ""
        )
          ocdSymptoms += `intensity - ${data.OCDIntensity["@value"]}, `;

        if (
          data.OCDDuration !== undefined &&
          data.OCDDuration["@value"] &&
          data.OCDDuration["@value"] !== ""
        )
          ocdSymptoms += `duration - ${data.OCDDuration["@value"]}, `;

        console.log("ocdSymptoms: ", ocdSymptoms);
        symptomSummary += `${ocdSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.PanicDisorder !== undefined &&
        data.PanicDisorder["@value"] &&
        data.PanicDisorder["@value"] === "true"
      ) {
        let panicDisorderSymptoms = "Symptoms of Panic Disorder: ";

        if (
          data.PoundingHeart !== undefined &&
          data.PoundingHeart["@value"] &&
          data.PoundingHeart["@value"] === "true"
        )
          panicDisorderSymptoms +=
            "palpitations, pounding heart, accelerated heart rate, ";

        if (
          data.PanicDisorderSweating !== undefined &&
          data.PanicDisorderSweating["@value"] &&
          data.PanicDisorderSweating["@value"] === "true"
        )
          panicDisorderSymptoms += "sweating, ";

        if (
          data.Trembling !== undefined &&
          data.Trembling["@value"] &&
          data.Trembling["@value"] === "true"
        )
          panicDisorderSymptoms += "trembling/shaking, ";

        if (
          data.ShortnessOfBreath !== undefined &&
          data.ShortnessOfBreath["@value"] &&
          data.ShortnessOfBreath["@value"] === "true"
        )
          panicDisorderSymptoms += "shortness of breath, ";

        if (
          data.Choking !== undefined &&
          data.Choking["@value"] &&
          data.Choking["@value"] === "true"
        )
          panicDisorderSymptoms += "feelings of choking, ";

        if (
          data.ChestPain !== undefined &&
          data.ChestPain["@value"] &&
          data.ChestPain["@value"] === "true"
        )
          panicDisorderSymptoms += "chest pain/discomfort, ";

        if (
          data.PanicDisorderNausea !== undefined &&
          data.PanicDisorderNausea["@value"] &&
          data.PanicDisorderNausea["@value"] === "true"
        )
          panicDisorderSymptoms += "nausea/abdominal discomfort, ";

        if (
          data.Dizzy !== undefined &&
          data.Dizzy["@value"] &&
          data.Dizzy["@value"] === "true"
        )
          panicDisorderSymptoms += "dizzy/faint, ";

        if (
          data.Chills !== undefined &&
          data.Chills["@value"] &&
          data.Chills["@value"] === "true"
        )
          panicDisorderSymptoms += "chills, ";

        if (
          data.Derealization !== undefined &&
          data.Derealization["@value"] &&
          data.Derealization["@value"] === "true"
        )
          panicDisorderSymptoms += "derealization or depersonalization, ";

        if (
          data.FearOfCrazy !== undefined &&
          data.FearOfCrazy["@value"] &&
          data.FearOfCrazy["@value"] === "true"
        )
          panicDisorderSymptoms += "fear of losing control/going crazy, ";

        if (
          data.FearOfDying !== undefined &&
          data.FearOfDying["@value"] &&
          data.FearOfDying["@value"] === "true"
        )
          panicDisorderSymptoms += "fear of dying, ";

        if (
          data.PanicDisorderOther !== undefined &&
          data.PanicDisorderOther["@value"] &&
          data.PanicDisorderOther["@value"] === "true" &&
          data.PanicDisorderOtherDetails["@value"] &&
          data.PanicDisorderOtherDetails["@value"] !== ""
        )
          panicDisorderSymptoms += `other Panic Disorder symptom(s) - ${data.PanicDisorderOtherDetails["@value"]}, `;

        if (
          data.PanicDisorderFrequency !== undefined &&
          data.PanicDisorderFrequency["@value"] &&
          data.PanicDisorderFrequency["@value"] !== ""
        )
          panicDisorderSymptoms += `frequency - ${data.PanicDisorderFrequency["@value"]}, `;

        if (
          data.PanicDisorderIntensity !== undefined &&
          data.PanicDisorderIntensity["@value"] &&
          data.PanicDisorderIntensity["@value"] !== ""
        )
          panicDisorderSymptoms += `intensity - ${data.PanicDisorderIntensity["@value"]}, `;

        if (
          data.PanicDisorderDuration !== undefined &&
          data.PanicDisorderDuration["@value"] &&
          data.PanicDisorderDuration["@value"] !== ""
        )
          panicDisorderSymptoms += `duration - ${data.PanicDisorderDuration["@value"]}, `;

        console.log("panicDisorderSymptoms: ", panicDisorderSymptoms);
        symptomSummary += `${panicDisorderSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.Phobias !== undefined &&
        data.Phobias["@value"] &&
        data.Phobias["@value"] === "true"
      ) {
        let phobiaSymptoms = "Symptoms of Phobias: ";

        if (
          data.PhobiasSpecificFear !== undefined &&
          data.PhobiasSpecificFear["@value"] &&
          data.PhobiasSpecificFear["@value"] === "true"
        )
          phobiaSymptoms +=
            "marked fear/anxiety about a specific object or situation, ";

        if (
          data.ImmediateFear !== undefined &&
          data.ImmediateFear["@value"] &&
          data.ImmediateFear["@value"] === "true"
        )
          phobiaSymptoms +=
            "object or situation provokes immediate fear or anxiety, ";

        if (
          data.PhobiasAvoidance !== undefined &&
          data.PhobiasAvoidance["@value"] &&
          data.PhobiasAvoidance["@value"] === "true"
        )
          phobiaSymptoms += "avoids objective/situation, ";

        if (
          data.FearExaggerated !== undefined &&
          data.FearExaggerated["@value"] &&
          data.FearExaggerated["@value"] === "true"
        )
          phobiaSymptoms += "fear and anxiety is out of proportion, ";

        if (
          data.PhobiasOther !== undefined &&
          data.PhobiasOther["@value"] &&
          data.PhobiasOther["@value"] === "true" &&
          data.PhobiasOtherDetails["@value"] &&
          data.PhobiasOtherDetails["@value"] !== ""
        )
          phobiaSymptoms += `other Phobia symptom(s) - ${data.PhobiasOtherDetails["@value"]}, `;

        if (
          data.PhobiasFrequency !== undefined &&
          data.PhobiasFrequency["@value"] &&
          data.PhobiasFrequency["@value"] !== ""
        )
          phobiaSymptoms += `frequency - ${data.PhobiasFrequency["@value"]}, `;

        if (
          data.PhobiasIntensity !== undefined &&
          data.PhobiasIntensity["@value"] &&
          data.PhobiasIntensity["@value"] !== ""
        )
          phobiaSymptoms += `intensity - ${data.PhobiasIntensity["@value"]}, `;

        if (
          data.PhobiasDuration !== undefined &&
          data.PhobiasDuration["@value"] &&
          data.PhobiasDuration["@value"] !== ""
        )
          phobiaSymptoms += `duration - ${data.PhobiasDuration["@value"]}, `;

        console.log("phobiaSymptoms: ", phobiaSymptoms);
        symptomSummary += `${phobiaSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.DI !== undefined &&
        data.DI["@value"] &&
        data.DI["@value"] === "true"
      ) {
        let diSymptoms = "Developmental Impairments: ";

        if (
          data.DILowIQ !== undefined &&
          data.DILowIQ["@value"] &&
          data.DILowIQ["@value"] === "true"
        )
          diSymptoms += "low intelligence/IQ, ";

        if (
          data.DISensoryImpairments !== undefined &&
          data.DISensoryImpairments["@value"] &&
          data.DISensoryImpairments["@value"] === "true"
        )
          diSymptoms += "sensory impairments, ";

        if (
          data.DISpeechDelays !== undefined &&
          data.DISpeechDelays["@value"] &&
          data.DISpeechDelays["@value"] === "true"
        )
          diSymptoms += "language/speech delays, ";

        if (
          data.DIMotorCoordinationDelays !== undefined &&
          data.DIMotorCoordinationDelays["@value"] &&
          data.DIMotorCoordinationDelays["@value"] === "true"
        )
          diSymptoms += "motor coordination delays, ";

        if (
          data.DILearningDisorder !== undefined &&
          data.DILearningDisorder["@value"] &&
          data.DILearningDisorder["@value"] === "true"
        )
          diSymptoms += "specific learning disorder, ";

        if (
          data.DISpatialDelays !== undefined &&
          data.DISpatialDelays["@value"] &&
          data.DISpatialDelays["@value"] === "true"
        )
          diSymptoms += "visio-spatial skill delays, ";

        if (
          data.DevelopmentalImpairmentsOther !== undefined &&
          data.DevelopmentalImpairmentsOther["@value"] &&
          data.DevelopmentalImpairmentsOther["@value"] === "true" &&
          data.DevelopmentalImpairmentsOtherDetails["@value"] &&
          data.DevelopmentalImpairmentsOtherDetails["@value"] !== ""
        )
          diSymptoms += `other Developmental Impairment symptom(s) - ${data.DevelopmentalImpairmentsOtherDetails["@value"]}, `;

        if (
          data.DevelopmentalImpairmentsDetails !== undefined &&
          data.DevelopmentalImpairmentsDetails["@value"] &&
          data.DevelopmentalImpairmentsDetails["@value"] !== ""
        )
          diSymptoms += `Description of symptoms - ${data.DevelopmentalImpairmentsDetails["@value"]}, `;

        console.log("diSymptoms: ", diSymptoms);
        symptomSummary += `${diSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.OtherSymptoms !== undefined &&
        data.OtherSymptoms["@value"] &&
        data.OtherSymptoms["@value"] === "true"
      ) {
        let otherSymptoms = "Other Symptoms: ";

        if (
          data.OtherSymptomsDetails !== undefined &&
          data.OtherSymptomsDetails["@value"] &&
          data.OtherSymptomsDetails["@value"] !== ""
        )
          otherSymptoms += `${data.OtherSymptomsDetails["@value"]}, `;

        if (
          data.OtherSymptomsFrequency !== undefined &&
          data.OtherSymptomsFrequency["@value"] &&
          data.OtherSymptomsFrequency["@value"] !== ""
        )
          otherSymptoms += `frequency - ${data.OtherSymptomsFrequency["@value"]}, `;

        if (
          data.OtherSymptomsIntensity !== undefined &&
          data.OtherSymptomsIntensity["@value"] &&
          data.OtherSymptomsIntensity["@value"] !== ""
        )
          otherSymptoms += `intensity - ${data.OtherSymptomsIntensity["@value"]}, `;

        if (
          data.OtherSymptomsDuration !== undefined &&
          data.OtherSymptomsDuration["@value"] &&
          data.OtherSymptomsDuration["@value"] !== ""
        )
          otherSymptoms += `duration - ${data.OtherSymptomsDuration["@value"]}, `;

        console.log("otherSymptoms: ", otherSymptoms);
        symptomSummary += `${otherSymptoms.slice(0, -2)}.\r\n`;
      }

      if (symptomSummary !== "")
        summary += `Symptom Checklist:\r\n${symptomSummary.trimEnd()}\r\n\r\n`;
      //#endregion

      summary = summary.trimEnd();

      const obj = { ...data };
      obj.ClinicalNarrativeSummary["@value"] = summary;
      setData(obj);
    }
  };

  const customValidate = (errs) => {
    if (
      (!data.ISClient["@value"] || data.ISClient["@value"] === "false") &&
      (!data.ISParent["@value"] || data.ISParent["@value"] === "false") &&
      (!data.ISGuardian["@value"] || data.ISGuardian["@value"] === "false") &&
      (!data.ISCaregiver["@value"] || data.ISCaregiver["@value"] === "false") &&
      (!data.ISPhysician["@value"] || data.ISPhysician["@value"] === "false") &&
      (!data.ISRecords["@value"] || data.ISRecords["@value"] === "false") &&
      (!data.ISServiceProvider["@value"] ||
        data.ISServiceProvider["@value"] === "false") &&
      (!data.ISSchool["@value"] || data.ISSchool["@value"] === "false") &&
      (!data.ISCourt["@value"] || data.ISCourt["@value"] === "false") &&
      (!data.ISCPS["@value"] || data.ISCPS["@value"] === "false") &&
      (!data.ISOther["@value"] || data.ISOther["@value"] === "false")
    ) {
      if (errs)
        errs.InformationSources =
          "At least one Information Source is required.";
      else
        errs = {
          InformationSources: "At least one Information Source is required.",
        };
    }

    if (data.FamilyHistoryUnknown["@value"] !== "true") {
      const familyHistoryDetails = JSON.parse(
        data.FamilyHistoryDetails["@value"]
      );

      if (familyHistoryDetails.length === 0) {
        if (errs) errs.FamilyHistoryDetails = "Family history is required.";
        else
          errs = {
            FamilyHistoryDetails: "Family history is required.",
          };
      } else if (
        familyHistoryDetails.filter((item) => item.FamilyMember === "").length >
        0
      ) {
        if (errs)
          errs.FamilyHistoryDetails = "Every row must have a Family Member.";
        else
          errs = {
            FamilyHistoryDetails: "Every row must have a Family Member.",
          };
      }
    }

    if (data.NoMedications["@value"] !== "true") {
      const currentMedicationDetails = JSON.parse(
        data.CurrentMedicationDetails["@value"]
      );

      if (currentMedicationDetails.length === 0) {
        if (errs)
          errs.CurrentMedicationDetails = "Medication details are required.";
        else
          errs = {
            CurrentMedicationDetails: "Medication details are required.",
          };
      } else if (
        currentMedicationDetails.filter((item) => item.Name === "").length > 0
      ) {
        if (errs)
          errs.CurrentMedicationDetails = "Every Medication must have a Name.";
        else
          errs = {
            CurrentMedicationDetails: "Every Medication must have a Name.",
          };
      }
    }

    const traumaHistory = JSON.parse(data.TraumaHistory["@value"]);

    if (traumaHistory.length === 0) {
      if (errs) errs.TraumaHistory = "Trauma history is required.";
      else
        errs = {
          TraumaHistory: "Trauma history is required.",
        };
    } else {
      const historyWithoutAllFields = traumaHistory.filter(
        (detail) =>
          detail.Trauma === undefined ||
          detail.Trauma === "" ||
          detail.Status === undefined ||
          detail.Status === "" ||
          detail.Comments === undefined ||
          detail.Comments === ""
      );

      if (historyWithoutAllFields.length > 0) {
        if (errs)
          errs.TraumaHistory =
            "All fields are required for each trauma detail.";
        else
          errs = {
            TraumaHistory: "All fields are required for each trauma detail.",
          };
      } else {
        delete errs.TraumaHistory;
      }
    }

    const diagnosisDetails = JSON.parse(data.DiagnosisDetails["@value"]);

    if (diagnosisDetails.length === 0) {
      if (errs) errs.DiagnosisDetails = "Diagnosis details are required.";
      else
        errs = {
          DiagnosisDetails: "Diagnosis details are required.",
        };
    } else {
      const diagnosisWithoutAllFields = diagnosisDetails.filter(
        (detail) => detail.Code === undefined || detail.Code === ""
      );

      if (diagnosisWithoutAllFields.length > 0) {
        if (errs)
          errs.DiagnosisDetails = "All fields are required for each diagnosis.";
        else
          errs = {
            DiagnosisDetails: "All fields are required for each diagnosis.",
          };
      } else {
        delete errs.DiagnosisDetails;
      }
    }

    const treatmentRecommendations = JSON.parse(
      data.TreatmentRecommendations["@value"]
    );

    if (treatmentRecommendations.length === 0) {
      if (errs)
        errs.TreatmentRecommendations =
          "Treatment recommendations are required.";
      else
        errs = {
          TreatmentRecommendations: "Treatment recommendations are required.",
        };
    } else if (
      treatmentRecommendations.filter((item) => item.Recommendation === "")
        .length > 0
    ) {
      if (errs)
        errs.TreatmentRecommendations =
          "Recommendations are required for each row.";
      else
        errs = {
          TreatmentRecommendations:
            "Recommendations are required for each row.",
        };
    }

    if (
      (!data.DistressingMemories["@value"] ||
        data.DistressingMemories["@value"] === "false") &&
      (!data.LossOfMemories["@value"] ||
        data.LossOfMemories["@value"] === "false") &&
      (!data.Nightmares["@value"] || data.Nightmares["@value"] === "false") &&
      (!data.Flashbacks["@value"] || data.Flashbacks["@value"] === "false") &&
      (!data.RepetitivePlayOfEvents["@value"] ||
        data.RepetitivePlayOfEvents["@value"] === "false") &&
      (!data.Dissocation["@value"] || data.Dissocation["@value"] === "false") &&
      (!data.BlockOut["@value"] || data.BlockOut["@value"] === "false") &&
      (!data.Hyperarousal["@value"] ||
        data.Hyperarousal["@value"] === "false") &&
      (!data.Avoidant["@value"] || data.Avoidant["@value"] === "false") &&
      (!data.Detachment["@value"] || data.Detachment["@value"] === "false") &&
      (!data.PsychologicalDistress["@value"] ||
        data.PsychologicalDistress["@value"] === "false") &&
      (!data.NegativeCognitions["@value"] ||
        data.NegativeCognitions["@value"] === "false") &&
      (!data.Hypervigilance["@value"] ||
        data.Hypervigilance["@value"] === "false") &&
      (!data.SleepDisturbances["@value"] ||
        data.SleepDisturbances["@value"] === "false") &&
      (!data.DifficultyConcentratingSymptom["@value"] ||
        data.DifficultyConcentratingSymptom["@value"] === "false") &&
      (!data.EmotionalDysregulation["@value"] ||
        data.EmotionalDysregulation["@value"] === "false") &&
      (!data.DiminishedInterests["@value"] ||
        data.DiminishedInterests["@value"] === "false") &&
      (!data.SocialWithdrawalSymptom["@value"] ||
        data.SocialWithdrawalSymptom["@value"] === "false") &&
      (!data.PhysiologicalResponses["@value"] ||
        data.PhysiologicalResponses["@value"] === "false") &&
      (!data.DevelopmentalRegression["@value"] ||
        data.DevelopmentalRegression["@value"] === "false") &&
      (!data.UnhealthyBoundaries["@value"] ||
        data.UnhealthyBoundaries["@value"] === "false") &&
      (!data.OtherTraumaSymptoms["@value"] ||
        data.OtherTraumaSymptoms["@value"] === "false") &&
      (!data.NoSymptoms["@value"] || data.NoSymptoms["@value"] === "false")
    ) {
      if (errs)
        errs.TraumaSymptomsResponse =
          "At least one Trauma Symptom/Response is required.";
      else
        errs = {
          TraumaSymptomsResponse:
            "At least one Trauma Symptom/Response is required.",
        };
    }

    if (
      (!data.SocialSupports["@value"] ||
        data.SocialSupports["@value"] === "false") &&
      (!data.InterpersonalSkills["@value"] ||
        data.InterpersonalSkills["@value"] === "false") &&
      (!data.ValidatingExperiences["@value"] ||
        data.ValidatingExperiences["@value"] === "false") &&
      (!data.Adaptability["@value"] ||
        data.Adaptability["@value"] === "false") &&
      (!data.SenseOfMastery["@value"] ||
        data.SenseOfMastery["@value"] === "false") &&
      (!data.PositiveOutlook["@value"] ||
        data.PositiveOutlook["@value"] === "false") &&
      (!data.Resources["@value"] || data.Resources["@value"] === "false") &&
      (!data.SecureAttachments["@value"] ||
        data.SecureAttachments["@value"] === "false") &&
      (!data.CaregiverStability["@value"] ||
        data.CaregiverStability["@value"] === "false") &&
      (!data.PlacementStability["@value"] ||
        data.PlacementStability["@value"] === "false") &&
      (!data.TiesToExtendedFamily["@value"] ||
        data.TiesToExtendedFamily["@value"] === "false") &&
      (!data.StrongCulturalIdentity["@value"] ||
        data.StrongCulturalIdentity["@value"] === "false") &&
      (!data.StrongSpiritualBeliefs["@value"] ||
        data.StrongSpiritualBeliefs["@value"] === "false") &&
      (!data.Pets["@value"] || data.Pets["@value"] === "false") &&
      (!data.OtherTraumaProtectiveFactors["@value"] ||
        data.OtherTraumaProtectiveFactors["@value"] === "false") &&
      (!data.NoTraumaProtectiveFactors["@value"] ||
        data.NoTraumaProtectiveFactors["@value"] === "false")
    ) {
      if (errs)
        errs.TraumaProtectiveFactors =
          "At least one Protective Factor is required.";
      else
        errs = {
          TraumaProtectiveFactors:
            "At least one Protective Factor is required.",
        };
    }

    if (
      (!data.AppropriateAppearance["@value"] ||
        data.AppropriateAppearance["@value"] === "false") &&
      (!data.Neat["@value"] || data.Neat["@value"] === "false") &&
      (!data.Disheveled["@value"] || data.Disheveled["@value"] === "false") &&
      (!data.BizarreAppearance["@value"] ||
        data.BizarreAppearance["@value"] === "false") &&
      (!data.OtherAppearance["@value"] ||
        data.OtherAppearance["@value"] === "false")
    ) {
      if (errs) errs.MSAppearance = "At least one value is required.";
      else
        errs = {
          MSAppearance: "At least one value is required.",
        };
    }

    if (
      (!data.Cooperative["@value"] || data.Cooperative["@value"] === "false") &&
      (!data.Guarded["@value"] || data.Guarded["@value"] === "false") &&
      (!data.Hyperactive["@value"] || data.Hyperactive["@value"] === "false") &&
      (!data.Agitated["@value"] || data.Agitated["@value"] === "false") &&
      (!data.Paranoid["@value"] || data.Paranoid["@value"] === "false") &&
      (!data.Tics["@value"] || data.Tics["@value"] === "false") &&
      (!data.Impulsive["@value"] || data.Impulsive["@value"] === "false") &&
      (!data.Aggressive["@value"] || data.Aggressive["@value"] === "false") &&
      (!data.Withdrawn["@value"] || data.Withdrawn["@value"] === "false") &&
      (!data.BizarreBehavior["@value"] ||
        data.BizarreBehavior["@value"] === "false") &&
      (!data.Rigidity["@value"] || data.Rigidity["@value"] === "false") &&
      (!data.OtherBehavior["@value"] ||
        data.OtherBehavior["@value"] === "false")
    ) {
      if (errs) errs.MSBehavior = "At least one value is required.";
      else
        errs = {
          MSBehavior: "At least one value is required.",
        };
    }

    if (
      (!data.Normal["@value"] || data.Normal["@value"] === "false") &&
      (!data.Slurred["@value"] || data.Slurred["@value"] === "false") &&
      (!data.Pressured["@value"] || data.Pressured["@value"] === "false") &&
      (!data.Tangential["@value"] || data.Tangential["@value"] === "false") &&
      (!data.Incoherent["@value"] || data.Incoherent["@value"] === "false") &&
      (!data.Stutters["@value"] || data.Stutters["@value"] === "false") &&
      (!data.OtherSpeech["@value"] || data.OtherSpeech["@value"] === "false")
    ) {
      if (errs) errs.MSSpeech = "At least one value is required.";
      else
        errs = {
          MSSpeech: "At least one value is required.",
        };
    }

    if (
      (!data.Euthymic["@value"] || data.Euthymic["@value"] === "false") &&
      (!data.Anxious["@value"] || data.Anxious["@value"] === "false") &&
      (!data.Angry["@value"] || data.Angry["@value"] === "false") &&
      (!data.Depressed["@value"] || data.Depressed["@value"] === "false") &&
      (!data.Euphoric["@value"] || data.Euphoric["@value"] === "false") &&
      (!data.Irritable["@value"] || data.Irritable["@value"] === "false") &&
      (!data.Fearful["@value"] || data.Fearful["@value"] === "false") &&
      (!data.OtherMood["@value"] || data.OtherMood["@value"] === "false")
    ) {
      if (errs) errs.MSMood = "At least one value is required.";
      else
        errs = {
          MSMood: "At least one value is required.",
        };
    }

    if (
      (!data.FullRange["@value"] || data.FullRange["@value"] === "false") &&
      (!data.Restricted["@value"] || data.Restricted["@value"] === "false") &&
      (!data.Blunted["@value"] || data.Blunted["@value"] === "false") &&
      (!data.Flat["@value"] || data.Flat["@value"] === "false") &&
      (!data.Inappropriate["@value"] ||
        data.Inappropriate["@value"] === "false") &&
      (!data.OtherAffect["@value"] || data.OtherAffect["@value"] === "false")
    ) {
      if (errs) errs.MSAffect = "At least one value is required.";
      else
        errs = {
          MSAffect: "At least one value is required.",
        };
    }

    if (
      (!data.Logical["@value"] || data.Logical["@value"] === "false") &&
      (!data.Preoccupations["@value"] ||
        data.Preoccupations["@value"] === "false") &&
      (!data.Delusions["@value"] || data.Delusions["@value"] === "false") &&
      (!data.Hallucinations["@value"] ||
        data.Hallucinations["@value"] === "false") &&
      (!data.Racing["@value"] || data.Racing["@value"] === "false") &&
      (!data.Disorganized["@value"] ||
        data.Disorganized["@value"] === "false") &&
      (!data.Concrete["@value"] || data.Concrete["@value"] === "false") &&
      (!data.SuicidalIdeation["@value"] ||
        data.SuicidalIdeation["@value"] === "false") &&
      (!data.HomicidalIdeation["@value"] ||
        data.HomicidalIdeation["@value"] === "false") &&
      (!data.OtherPerception["@value"] ||
        data.OtherPerception["@value"] === "false")
    ) {
      if (errs) errs.MSPerception = "At least one value is required.";
      else
        errs = {
          MSPerception: "At least one value is required.",
        };
    }

    if (
      (!data.NoImpairment["@value"] ||
        data.NoImpairment["@value"] === "false") &&
      (!data.ShortAttentionSpan["@value"] ||
        data.ShortAttentionSpan["@value"] === "false") &&
      (!data.DifficultyConcentrating["@value"] ||
        data.DifficultyConcentrating["@value"] === "false") &&
      (!data.Distractibility["@value"] ||
        data.Distractibility["@value"] === "false") &&
      (!data.MemoryImpairment["@value"] ||
        data.MemoryImpairment["@value"] === "false") &&
      (!data.LowIntelligence["@value"] ||
        data.LowIntelligence["@value"] === "false") &&
      (!data.OtherCognition["@value"] ||
        data.OtherCognition["@value"] === "false")
    ) {
      if (errs) errs.MSCognition = "At least one value is required.";
      else
        errs = {
          MSCognition: "At least one value is required.",
        };
    }

    if (
      (!data.AppropriateInsight["@value"] ||
        data.AppropriateInsight["@value"] === "false") &&
      (!data.Fair["@value"] || data.Fair["@value"] === "false") &&
      (!data.Impaired["@value"] || data.Impaired["@value"] === "false") &&
      (!data.OtherInsight["@value"] || data.OtherInsight["@value"] === "false")
    ) {
      if (errs) errs.MSInsight = "At least one value is required.";
      else
        errs = {
          MSInsight: "At least one value is required.",
        };
    }

    if (
      (data.SelfAbusiveBehavior["@value"] ||
        data.SuicidalThreats["@value"] ||
        data.CaregiverConcernsOfSelfHarm["@value"] ||
        data.OtherConcernsOfSelfHarm["@value"] ||
        data.CurrentSelfHarmRisk["@value"]) &&
      (data.SelfAbusiveBehavior["@value"] !== "None/Never" ||
        data.SuicidalThreats["@value"] !== "None/Never" ||
        data.SuicideAttempt["@value"] !== "None/Never" ||
        data.CaregiverConcernsOfSelfHarm["@value"] !== "No" ||
        (data.OtherConcernsOfSelfHarm["@value"] !== "No" &&
          data.OtherConcernsOfSelfHarm["@value"] !== "None reported") ||
        data.CurrentSelfHarmRisk["@value"] !== "None Noted") &&
      (!data.SelfHarmComments ||
        !data.SelfHarmComments["@value"] ||
        data.SelfHarmComments["@value"] === "")
    ) {
      if (errs)
        errs.SelfHarmComments = `"Current Risk of Harm to Self Comments" is required.`;
      else
        errs = {
          SelfHarmComments: `"Current Risk of Harm to Self Comments" is required.`,
        };
    }

    if (
      (data.ThreatsToHarmOthers["@value"] ||
        data.ThoughtsOfHarm["@value"] ||
        data.ActsOfHarm["@value"] ||
        data.ActsOfHarmAnimals["@value"] ||
        data.ActsInvolvingWeapons["@value"] ||
        data.CaregiverConcernOfOtherHarm["@value"] ||
        data.OtherConcernsOfOtherHarm["@value"] ||
        data.CurrentOtherHarmRisk["@value"]) &&
      (data.ThreatsToHarmOthers["@value"] !== "None/Never" ||
        data.ThoughtsOfHarm["@value"] !== "None/Never" ||
        data.ActsOfHarm["@value"] !== "None/Never" ||
        data.ActsOfHarmAnimals["@value"] !== "None/Never" ||
        data.ActsInvolvingWeapons["@value"] !== "None/Never" ||
        data.CaregiverConcernOfOtherHarm["@value"] !== "No" ||
        (data.OtherConcernsOfOtherHarm["@value"] !== "No" &&
          data.OtherConcernsOfOtherHarm["@value"] !== "None reported") ||
        data.CurrentOtherHarmRisk["@value"] !== "None Noted") &&
      (!data.OtherHarmComments ||
        !data.OtherHarmComments["@value"] ||
        data.OtherHarmComments["@value"] === "")
    ) {
      if (errs)
        errs.OtherHarmComments = `"Current Risk of Harm to Others Comments" is required.`;
      else
        errs = {
          OtherHarmComments: `"Current Risk of Harm to Others Comments" is required.`,
        };
    }

    if (
      data.TraumaSymptoms["@value"] === "true" &&
      (!data.TraumaticExposure["@value"] ||
        data.TraumaticExposure["@value"] === "false") &&
      (!data.AvoidantTrauma["@value"] ||
        data.AvoidantTrauma["@value"] === "false") &&
      (!data.HypervigilanceTrauma["@value"] ||
        data.HypervigilanceTrauma["@value"] === "false") &&
      (!data.IntrusiveDreams["@value"] ||
        data.IntrusiveDreams["@value"] === "false") &&
      (!data.NegativeThoughts["@value"] ||
        data.NegativeThoughts["@value"] === "false") &&
      (!data.CocentrationProblems["@value"] ||
        data.CocentrationProblems["@value"] === "false") &&
      (!data.RecklessBehaviorTrauma["@value"] ||
        data.RecklessBehaviorTrauma["@value"] === "false") &&
      (!data.SleepDisturbancesTrauma["@value"] ||
        data.SleepDisturbancesTrauma["@value"] === "false") &&
      (!data.OtherTrauma["@value"] || data.OtherTrauma["@value"] === "false")
    ) {
      if (errs)
        errs.TraumaSymptoms = "At least one Trauma Symptom is required.";
      else
        errs = {
          TraumaSymptoms: "At least one Trauma Symptom is required.",
        };
    }

    if (
      data.AnxietyDisorder["@value"] === "true" &&
      (!data.AnxietyDisorderExcessWorry["@value"] ||
        data.AnxietyDisorderExcessWorry["@value"] === "false") &&
      (!data.AnxietyDisorderEasilyFatigued["@value"] ||
        data.AnxietyDisorderEasilyFatigued["@value"] === "false") &&
      (!data.MuscleTension["@value"] ||
        data.MuscleTension["@value"] === "false") &&
      (!data.Overworried["@value"] || data.Overworried["@value"] === "false") &&
      (!data.AnxietyDisorderDifficultyConcentrating["@value"] ||
        data.AnxietyDisorderDifficultyConcentrating["@value"] === "false") &&
      (!data.SleepDisturbancesAnxiety["@value"] ||
        data.SleepDisturbancesAnxiety["@value"] === "false") &&
      (!data.RestlessAnxiety["@value"] ||
        data.RestlessAnxiety["@value"] === "false") &&
      (!data.Irritability["@value"] ||
        data.Irritability["@value"] === "false") &&
      (!data.OtherAnxiety["@value"] || data.OtherAnxiety["@value"] === "false")
    ) {
      if (errs)
        errs.AnxietyDisorder = "At least one Anxiety Symptom is required.";
      else
        errs = {
          AnxietyDisorder: "At least one Anxiety Symptom is required.",
        };
    }

    if (
      data.DepressionSymptoms["@value"] === "true" &&
      (!data.DepressedMood["@value"] ||
        data.DepressedMood["@value"] === "false") &&
      (!data.SleepChanges["@value"] ||
        data.SleepChanges["@value"] === "false") &&
      (!data.Guilt["@value"] || data.Guilt["@value"] === "false") &&
      (!data.DiminishedInterest["@value"] ||
        data.DiminishedInterest["@value"] === "false") &&
      (!data.PurposelessMovement["@value"] ||
        data.PurposelessMovement["@value"] === "false") &&
      (!data.WeightChanges["@value"] ||
        data.WeightChanges["@value"] === "false") &&
      (!data.FatigueDepression["@value"] ||
        data.FatigueDepression["@value"] === "false") &&
      (!data.ThoughtsOfDeath["@value"] ||
        data.ThoughtsOfDeath["@value"] === "false") &&
      (!data.OtherDepression["@value"] ||
        data.OtherDepression["@value"] === "false")
    ) {
      if (errs)
        errs.Depression = "At least one Depression Symptom is required.";
      else
        errs = {
          Depression: "At least one Depression Symptom is required.",
        };
    }

    if (
      data.AttentionHyperactivity["@value"] === "true" &&
      (!data.AttentionHyperactivityEasilyDistracted["@value"] ||
        data.AttentionHyperactivityEasilyDistracted["@value"] === "false") &&
      (!data.DifficultyFollowThrough["@value"] ||
        data.DifficultyFollowThrough["@value"] === "false") &&
      (!data.Forgetful["@value"] || data.Forgetful["@value"] === "false") &&
      (!data.DifficultyListening["@value"] ||
        data.DifficultyListening["@value"] === "false") &&
      (!data.DisorganizedAttention["@value"] ||
        data.DisorganizedAttention["@value"] === "false") &&
      (!data.Fidgety["@value"] || data.Fidgety["@value"] === "false") &&
      (!data.LackOfDetails["@value"] ||
        data.LackOfDetails["@value"] === "false") &&
      (!data.LosesThings["@value"] || data.LosesThings["@value"] === "false") &&
      (!data.InterruptsOthers["@value"] ||
        data.InterruptsOthers["@value"] === "false") &&
      (!data.UnableToSitStill["@value"] ||
        data.UnableToSitStill["@value"] === "false") &&
      (!data.ExcessiveTalking["@value"] ||
        data.ExcessiveTalking["@value"] === "false") &&
      (!data.DifficultyWaiting["@value"] ||
        data.DifficultyWaiting["@value"] === "false") &&
      (!data.OtherAttention["@value"] ||
        data.OtherAttention["@value"] === "false")
    ) {
      if (errs)
        errs.AttentionHyperactivity =
          "At least one Attention and Hyperactivity Symptom is required.";
      else
        errs = {
          AttentionHyperactivity:
            "At least one Attention and Hyperactivity Symptom is required.",
        };
    }

    if (
      data.SU["@value"] === "true" &&
      (!data.PatternOfSubstanceUse["@value"] ||
        data.PatternOfSubstanceUse["@value"] === "false") &&
      (!data.DesiresCutDownUse["@value"] ||
        data.DesiresCutDownUse["@value"] === "false") &&
      (!data.TimeSpentObtaining["@value"] ||
        data.TimeSpentObtaining["@value"] === "false") &&
      (!data.Cravings["@value"] || data.Cravings["@value"] === "false") &&
      (!data.FailureOfResponsibilities["@value"] ||
        data.FailureOfResponsibilities["@value"] === "false") &&
      (!data.NegativeImpact["@value"] ||
        data.NegativeImpact["@value"] === "false") &&
      (!data.AbandonsActivities["@value"] ||
        data.AbandonsActivities["@value"] === "false") &&
      (!data.Tolerance["@value"] || data.Tolerance["@value"] === "false") &&
      (!data.SUWithdrawal["@value"] ||
        data.SUWithdrawal["@value"] === "false") &&
      (!data.SUOther["@value"] || data.SUOther["@value"] === "false")
    ) {
      if (errs) errs.SU = "At least one Substance Use Symptom is required.";
      else
        errs = {
          SU: "At least one Substance Use Symptom is required.",
        };
    }

    if (
      data.DMDD["@value"] === "true" &&
      (!data.Temper["@value"] || data.Temper["@value"] === "false") &&
      (!data.OutsideOutbursts["@value"] ||
        data.OutsideOutbursts["@value"] === "false") &&
      (!data.FrequentOutbursts["@value"] ||
        data.FrequentOutbursts["@value"] === "false") &&
      (!data.DMDDIrritable["@value"] ||
        data.DMDDIrritable["@value"] === "false") &&
      (!data.OutburstsAroundOthers["@value"] ||
        data.OutburstsAroundOthers["@value"] === "false") &&
      (!data.DMDDOther["@value"] || data.DMDDOther["@value"] === "false")
    ) {
      if (errs)
        errs.DMDD =
          "At least one Disruptive Mood Dysregulation Disorder Symptom is required.";
      else
        errs = {
          DMDD: "At least one Disruptive Mood Dysregulation Disorder Symptom is required.",
        };
    }

    if (
      data.ConductDisorder["@value"] === "true" &&
      (!data.Aggression["@value"] || data.Aggression["@value"] === "false") &&
      (!data.DestructionOfProperty["@value"] ||
        data.DestructionOfProperty["@value"] === "false") &&
      (!data.Theft["@value"] || data.Theft["@value"] === "false") &&
      (!data.SeriousViolations["@value"] ||
        data.SeriousViolations["@value"] === "false") &&
      (!data.OtherConductDisorder["@value"] ||
        data.OtherConductDisorder["@value"] === "false")
    ) {
      if (errs)
        errs.ConductDisorder =
          "At least one Conduct Disorder Symptom is required.";
      else
        errs = {
          ConductDisorder: "At least one Conduct Disorder Symptom is required.",
        };
    }

    if (
      data.ODD["@value"] === "true" &&
      (!data.IrritableODD["@value"] ||
        data.IrritableODD["@value"] === "false") &&
      (!data.Argumentative["@value"] ||
        data.Argumentative["@value"] === "false") &&
      (!data.Vindictive["@value"] || data.Vindictive["@value"] === "false") &&
      (!data.OtherODD["@value"] || data.OtherODD["@value"] === "false")
    ) {
      if (errs)
        errs.ODD =
          "At least one Oppositional Defiance Disorder Symptom is required.";
      else
        errs = {
          ODD: "At least one Oppositional Defiance Disorder Symptom is required.",
        };
    }

    if (
      data.OCD["@value"] === "true" &&
      (!data.OCDPresence["@value"] || data.OCDPresence["@value"] === "false") &&
      (!data.Obsessions["@value"] || data.Obsessions["@value"] === "false") &&
      (!data.TimeConsuming["@value"] ||
        data.TimeConsuming["@value"] === "false") &&
      (!data.Compulsions["@value"] || data.Compulsions["@value"] === "false") &&
      (!data.ReduceAnxiety["@value"] ||
        data.ReduceAnxiety["@value"] === "false") &&
      (!data.SuppressUrges["@value"] ||
        data.SuppressUrges["@value"] === "false") &&
      (!data.OCDOther["@value"] || data.OCDOther["@value"] === "false")
    ) {
      if (errs)
        errs.OCD =
          "At least one Obsessive-Compulsive Disorder Symptom is required.";
      else
        errs = {
          OCD: "At least one Obsessive-Compulsive Disorder Symptom is required.",
        };
    }

    if (
      data.PanicDisorder["@value"] === "true" &&
      (!data.PoundingHeart["@value"] ||
        data.PoundingHeart["@value"] === "false") &&
      (!data.PanicDisorderSweating["@value"] ||
        data.PanicDisorderSweating["@value"] === "false") &&
      (!data.Trembling["@value"] || data.Trembling["@value"] === "false") &&
      (!data.ShortnessOfBreath["@value"] ||
        data.ShortnessOfBreath["@value"] === "false") &&
      (!data.Choking["@value"] || data.Choking["@value"] === "false") &&
      (!data.ChestPain["@value"] || data.ChestPain["@value"] === "false") &&
      (!data.PanicDisorderNausea["@value"] ||
        data.PanicDisorderNausea["@value"] === "false") &&
      (!data.Dizzy["@value"] || data.Dizzy["@value"] === "false") &&
      (!data.Chills["@value"] || data.Chills["@value"] === "false") &&
      (!data.Derealization["@value"] ||
        data.Derealization["@value"] === "false") &&
      (!data.FearOfCrazy["@value"] || data.FearOfCrazy["@value"] === "false") &&
      (!data.FearOfDying["@value"] || data.FearOfDying["@value"] === "false") &&
      (!data.PanicDisorderOther["@value"] ||
        data.PanicDisorderOther["@value"] === "false")
    ) {
      if (errs)
        errs.PanicDisorder = "At least one Panic Disorder Symptom is required.";
      else
        errs = {
          PanicDisorder: "At least one Panic Disorder Symptom is required.",
        };
    }

    if (
      data.Phobias["@value"] === "true" &&
      (!data.PhobiasSpecificFear["@value"] ||
        data.PhobiasSpecificFear["@value"] === "false") &&
      (!data.ImmediateFear["@value"] ||
        data.ImmediateFear["@value"] === "false") &&
      (!data.PhobiasAvoidance["@value"] ||
        data.PhobiasAvoidance["@value"] === "false") &&
      (!data.FearExaggerated["@value"] ||
        data.FearExaggerated["@value"] === "false") &&
      (!data.PhobiasOther["@value"] || data.PhobiasOther["@value"] === "false")
    ) {
      if (errs) errs.Phobias = "At least one Phobia Symptom is required.";
      else
        errs = {
          Phobias: "At least one Phobia Symptom is required.",
        };
    }

    if (
      data.DI["@value"] === "true" &&
      (!data.DILowIQ["@value"] || data.DILowIQ["@value"] === "false") &&
      (!data.DISensoryImpairments["@value"] ||
        data.DISensoryImpairments["@value"] === "false") &&
      (!data.DISpeechDelays["@value"] ||
        data.DISpeechDelays["@value"] === "false") &&
      (!data.DIMotorCoordinationDelays["@value"] ||
        data.DIMotorCoordinationDelays["@value"] === "false") &&
      (!data.DILearningDisorder["@value"] ||
        data.DILearningDisorder["@value"] === "false") &&
      (!data.DISpatialDelays["@value"] ||
        data.DISpatialDelays["@value"] === "false") &&
      (!data.DevelopmentalImpairmentsOther["@value"] ||
        data.DevelopmentalImpairmentsOther["@value"] === "false")
    ) {
      if (errs) errs.DI = "At least one Developmental Impairment is required.";
      else
        errs = {
          DI: "At least one Developmenta lImpairment is required.",
        };
    }

    setErrors(errs);
    return errs;
  };

  const sectionsToNavigate = [
    { _id: "PresentingProblem", name: "Strengths and Presenting Problem" },
    {
      _id: "FamilyHistorySection",
      name: "Family Environment, Family History, &amp; Placement History",
    },
    { _id: "Demographics", name: "Culture/Ethnicity/Religion/Spirituality" },
    { _id: "DevelopmentalHistory", name: "Developmental History" },
    { _id: "Education", name: "Education/Employment" },
    { _id: "Activities", name: "Basic Living Skills" },
    {
      _id: "Friendships",
      name: "Friendship/Social Peer Support/Relationships",
    },
    { _id: "LegalHistory", name: "Legal History" },
    { _id: "MedicalHistory", name: "Medical History/Medications" },
    {
      _id: "MentalHealthTreatmentHistory",
      name: "Mental Health Treatment History",
    },
    { _id: "SubstanceUseHistory", name: "Alcohol/Drug Use History" },
    { _id: "TraumaHistory", name: "Trauma History" },
    { _id: "MentalStatus", name: "Mental Status" },
    { _id: "Lethality", name: "Lethality" },
    { _id: "SymptomChecklist", name: "Symptom Checklist" },
    { _id: "ClinicalNarrative", name: "Clinical Narrative Summary" },
    { _id: "Evaluation", name: "Diagnoses and Evaluation" },
    {
      _id: "TreatmentRecommendations",
      name: "Treatment Recommendations/Needs",
    },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={(e) => {
            preSignForm();
            toggleSignatureModal(e);
          }}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          sectionsToNavigate={sectionsToNavigate}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <Select
                    name="SexAssignedAtBirth"
                    label="Sex Assigned at Birth:"
                    error={errors.SexAssignedAtBirth}
                    value={
                      data.SexAssignedAtBirth !== undefined &&
                      data.SexAssignedAtBirth["@value"]
                    }
                    options={sexAtBirthOptions}
                    onChange={handleChange}
                    identical={compare("SexAssignedAtBirth")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.SexAssignedAtBirth !== undefined &&
                    data.SexAssignedAtBirth["@value"] === "Sex not listed" && (
                      <Input
                        name="SexAssignedAtBirthDetails"
                        label="Please specify:"
                        error={errors.SexAssignedAtBirthDetails}
                        value={
                          data.SexAssignedAtBirthDetails !== undefined
                            ? data.SexAssignedAtBirthDetails["@value"]
                            : ""
                        }
                        onChange={handleChange}
                        identical={compare("SexAssignedAtBirthDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
                <div className="col">
                  <Select
                    name="Pronouns"
                    label="Pronouns:"
                    error={errors.Pronouns}
                    value={
                      data.Pronouns !== undefined && data.Pronouns["@value"]
                    }
                    options={pronounOptions}
                    onChange={handleChange}
                    identical={compare("Pronouns")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.Pronouns !== undefined &&
                    data.Pronouns["@value"] === "Prefer to self-describe" && (
                      <Input
                        name="PronounsDescription"
                        label="Please describe:"
                        error={errors.PronounsDescription}
                        value={
                          data.PronounsDescription !== undefined
                            ? data.PronounsDescription["@value"]
                            : ""
                        }
                        onChange={handleChange}
                        identical={compare("PronounsDescription")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
                <div className="col">
                  <Select
                    name="GenderIdentity"
                    label="Gender Identity:"
                    error={errors.GenderIdentity}
                    value={
                      data.GenderIdentity !== undefined &&
                      data.GenderIdentity["@value"]
                    }
                    options={genderIdentityOptions}
                    onChange={handleChange}
                    identical={compare("GenderIdentity")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.GenderIdentity !== undefined &&
                    data.GenderIdentity["@value"] === "Identity not listed" && (
                      <Input
                        name="GenderIdentityDescription"
                        label="Please specify:"
                        error={errors.GenderIdentityDescription}
                        value={
                          data.GenderIdentityDescription !== undefined
                            ? data.GenderIdentityDescription["@value"]
                            : ""
                        }
                        onChange={handleChange}
                        identical={compare("GenderIdentityDescription")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="DateCompleted"
                    label="Date Completed:"
                    type="date"
                    error={errors.DateCompleted}
                    value={data.DateCompleted["@value"]}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <RadioButton
                    name="PSEType"
                    label="Type of PSD:"
                    error={errors.PSEType}
                    value={data.PSEType["@value"]}
                    options={pseTypeOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.PSEType["@value"] === "Review/Update" && (
                    <Input
                      name="ReviewReason"
                      label="Reason:"
                      error={errors.ReviewReason}
                      value={getFieldValue(data.ReviewReason)}
                      onChange={handleChange}
                      identical={compare("ReviewReason")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <h5>Information Sources:</h5>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISClient"
                      label="Client"
                      checked={data.ISClient["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISParent"
                      label="Parent/Other"
                      checked={data.ISParent["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISFamily"
                      label="Family"
                      checked={data.ISFamily["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISGuardian"
                      label="Guardian"
                      checked={data.ISGuardian["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISCaregiver"
                      label="Non-parent Caregiver"
                      checked={data.ISCaregiver["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISPhysician"
                      label="Physician"
                      checked={data.ISPhysician["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISRecords"
                      label="Records"
                      checked={data.ISRecords["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISServiceProvider"
                      label="Service Provider"
                      checked={data.ISServiceProvider["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISSchool"
                      label="School"
                      checked={data.ISSchool["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISCourt"
                      label="Court"
                      checked={data.ISCourt["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISCPS"
                      label="CPS"
                      checked={data.ISCPS["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISOther"
                      label="Other"
                      checked={data.ISOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ISOther["@value"] === "true" && (
                      <Input
                        name="ISOtherDetails"
                        error={errors.ISOtherDetails}
                        value={data.ISOtherDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("ISOtherDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
              </div>
              {errors.InformationSources !== undefined && (
                <div className="row">
                  <div className="col">
                    <div className="alert alert-danger">
                      {errors.InformationSources}
                    </div>
                  </div>
                </div>
              )}
              <div className="clearfix">
                <div className="float-start">
                  <button
                    className="btn btn-primary mb-2"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="float-end d-flex">
                  <div className="me-2">Clinical Narrative Summary:</div>
                  <ToggleSwitch
                    values={["Show", "Hide"]}
                    selected={showNarrative}
                    onChange={setShowNarrative}
                  />
                </div>
              </div>
              <ExpandDiv
                id="PresentingProblem"
                expandStatus={expandStatus}
                className="mt-2"
                title="Strengths and Presenting Problem"
              >
                <Input
                  name="ReferralReason"
                  label="Reason for Referral:"
                  error={errors.ReferralReason}
                  value={data.ReferralReason["@value"]}
                  onChange={handleChange}
                  identical={compare("ReferralReason")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="ClientAbilities"
                  label="Client strengths, abilities, and skills (according to caregiver and child):"
                  error={errors.ClientAbilities}
                  value={data.ClientAbilities["@value"]}
                  onChange={handleChange}
                  identical={compare("ClientAbilities")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="PresentingProblem"
                  label="Circumstances Leading to Seeking Therapy (according to caregiver and child):"
                  error={errors.PresentingProblem}
                  value={data.PresentingProblem["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("PresentingProblem")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="FamilyHistorySection"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Environment, Family History, &amp; Placement History"
              >
                <h5>Family History</h5>
                <Checkbox
                  name="FamilyHistoryUnknown"
                  label="Family History Unknown"
                  divClassName="ms-3"
                  checked={data.FamilyHistoryUnknown["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.FamilyHistoryUnknown["@value"] !== "true" ? (
                  <FieldTable
                    name="FamilyHistoryDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">Concern/Issue</div>
                        <div className="col py-2">Explanation/Comments</div>
                      </div>
                      {data.FamilyHistoryDetails["@value"] &&
                        JSON.parse(data.FamilyHistoryDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "FamilyHistoryDetail"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "FamilyHistoryDetail"
                                        );
                                      }}
                                    ></i>
                                    <Input
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "FamilyHistoryDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "FamilyHistoryDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <div className="container">
                                    <div className="row">
                                      <div className="col">
                                        <Checkbox
                                          name={`MentalHealth-${index}`}
                                          label="Mental Health"
                                          checked={item.MentalHealth === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col">
                                        <Checkbox
                                          name={`Medical-${index}`}
                                          label="Medical"
                                          checked={item.Medical === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col">
                                        <Checkbox
                                          name={`Disability-${index}`}
                                          label="Disability"
                                          checked={item.Disability === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col">
                                        <Checkbox
                                          name={`SubstanceAbuse-${index}`}
                                          label="Substance Abuse"
                                          checked={
                                            item.SubstanceAbuse === "true"
                                          }
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col">
                                        <Checkbox
                                          name={`Suicidal-${index}`}
                                          label="Suicidal"
                                          checked={item.Suicidal === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col">
                                        <Checkbox
                                          name={`Legal-${index}`}
                                          label="Legal"
                                          checked={item.Legal === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col">
                                        <Checkbox
                                          name={`Trauma-${index}`}
                                          label="Trauma"
                                          checked={item.Trauma === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col">
                                        <Checkbox
                                          name={`Perpetration-${index}`}
                                          label="Perpetration"
                                          checked={item.Perpetration === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col">
                                        <Checkbox
                                          name={`Other-${index}`}
                                          label="Other"
                                          checked={item.Other === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  getFamilyMemberConcerns(index)
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Comments"
                                    value={item.Comments}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Comments
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("FamilyHistoryDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Family Member
                      </button>
                    ) : null}
                    {errors.FamilyHistoryDetails && (
                      <div className="alert alert-danger">
                        {errors.FamilyHistoryDetails}
                      </div>
                    )}
                  </FieldTable>
                ) : null}
                <TextArea
                  name="Supports"
                  label="Natural, community, and professional supports:"
                  error={errors.Supports}
                  value={data.Supports["@value"]}
                  onChange={handleChange}
                  identical={compare("Supports")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="Demographics"
                expandStatus={expandStatus}
                className="mt-2"
                title="Culture/Ethnicity/Religion/Spirituality"
              >
                <div className="container">
                  <div className="row">
                    <div
                      className="col"
                      title="Culture broadly refers to all the ways an individual understands their identity in terms of race, ethnicity, gender, sexual orientation, social class, religion/spirituality and language"
                    >
                      Identified cultural group:
                      <i className="fas fa-info-circle"></i>
                    </div>
                    <div className="col">
                      <Input
                        name="CulturalGroup"
                        error={errors.CulturalGroup}
                        value={data.CulturalGroup["@value"]}
                        onChange={handleChange}
                        identical={compare("CulturalGroup")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Special cultural traditions/celebrations/rituals:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="Traditions"
                        error={errors.Traditions}
                        value={data.Traditions["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.Traditions["@value"] === "Yes" ? (
                        <Input
                          name="TraditionsComments"
                          label="Describe:"
                          error={errors.TraditionsComments}
                          value={data.TraditionsComments["@value"]}
                          onChange={handleChange}
                          identical={compare("TraditionsComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Languages spoken:</div>
                    <div className="col">
                      <Input
                        name="LanguagesSpoken"
                        error={errors.LanguagesSpoken}
                        value={data.LanguagesSpoken["@value"]}
                        onChange={handleChange}
                        identical={compare("LanguagesSpoken")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Negative experiences due to customs/ethnicity/race/SES:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="NegativeCulturalExperience"
                        error={errors.NegativeCulturalExperience}
                        value={data.NegativeCulturalExperience["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.NegativeCulturalExperience["@value"] === "Yes" && (
                        <Input
                          name="NegativeCulturalExperienceComments"
                          label="Describe:"
                          error={errors.NegativeCulturalExperienceComments}
                          value={
                            data.NegativeCulturalExperienceComments["@value"]
                          }
                          onChange={handleChange}
                          identical={compare(
                            "NegativeCulturalExperienceComments"
                          )}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Cultural or Spiritual beliefs that influence
                      medical/mental health decisions:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="MedicalBeliefs"
                        error={errors.MedicalBeliefs}
                        value={data.MedicalBeliefs["@value"]}
                        options={yesNoNoneOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.MedicalBeliefs["@value"] === "Yes" && (
                        <Input
                          name="MedicalBeliefsComments"
                          label="Comments"
                          error={errors.MedicalBeliefsComments}
                          value={data.MedicalBeliefsComments["@value"]}
                          onChange={handleChange}
                          identical={compare("MedicalBeliefsComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Identified religion/belief system:
                    </div>
                    <div className="col">
                      <Input
                        name="Religion"
                        error={errors.Religion}
                        value={data.Religion["@value"]}
                        onChange={handleChange}
                        identical={compare("Religion")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Role of faith in client's life:</div>
                    <div className="col">
                      <Input
                        name="ReligionRole"
                        error={errors.ReligionRole}
                        value={data.ReligionRole["@value"]}
                        onChange={handleChange}
                        identical={compare("ReligionRole")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="DevelopmentalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Developmental History"
              >
                <h5>Mother's Pregnancy History:</h5>
                <div className="d-flex">
                  <Checkbox
                    name="PHUnknown"
                    label="Mother's pregnancy history unknown"
                    divClassName="ms-3"
                    checked={data.PHUnknown["@value"] === "true"}
                    commentsHandler={handleComments}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  {data.PHUnknown["@value"] !== "true" && (
                    <Checkbox
                      name="PHNoProblems"
                      label="No problems reported"
                      divClassName="ms-3"
                      checked={data.PHNoProblems["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  )}
                </div>
                <div className="container">
                  {data.PHNoProblems["@value"] !== "true" &&
                  data.PHUnknown["@value"] !== "true" ? (
                    <>
                      <div className="row">
                        <div className="col">Prenatal Care:</div>
                        <div className="col">
                          <RadioButton
                            name="PHPrenatalCare"
                            error={errors.PHPrenatalCare}
                            value={data.PHPrenatalCare["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Exposure to alcohol, tobacco, or other drugs:
                        </div>
                        <div className="col">
                          <RadioButton
                            name="PHExposure"
                            error={errors.PHExposure}
                            value={data.PHExposure["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHExposure["@value"] === "Yes" ? (
                            <TextArea
                              name="PHExposureComments"
                              label="Describe:"
                              error={errors.PHExposureComments}
                              value={data.PHExposureComments["@value"]}
                              onChange={handleChange}
                              identical={compare("PHExposureComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          History of DV or significant trauma during pregnancy:
                        </div>
                        <div className="col">
                          <RadioButton
                            name="PHTrauma"
                            error={errors.PHTrauma}
                            value={data.PHTrauma["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHTrauma["@value"] === "Yes" ? (
                            <TextArea
                              name="PHTraumaComments"
                              label="Describe:"
                              error={errors.PHTraumaComments}
                              value={data.PHTraumaComments["@value"]}
                              onChange={handleChange}
                              identical={compare("PHTraumaComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Birth complications:</div>
                        <div className="col">
                          <RadioButton
                            name="PHComplications"
                            error={errors.PHComplications}
                            value={data.PHComplications["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHComplications["@value"] === "Yes" ? (
                            <TextArea
                              name="PHComplicationsComments"
                              label="Describe:"
                              error={errors.PHComplicationsComments}
                              value={data.PHComplicationsComments["@value"]}
                              onChange={handleChange}
                              identical={compare("PHComplicationsComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Medical concerns:</div>
                        <div className="col">
                          <RadioButton
                            name="PHConcerns"
                            error={errors.PHConcerns}
                            value={data.PHConcerns["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHConcerns["@value"] === "Yes" ? (
                            <TextArea
                              name="PHConcernsComments"
                              label="Describe:"
                              error={errors.PHConcernsComments}
                              value={data.PHConcernsComments["@value"]}
                              onChange={handleChange}
                              identical={compare("PHConcernsComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
                <h5>Developmental</h5>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      Developmental milestones met on time:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="MilestonesMet"
                        error={errors.MilestonesMet}
                        value={data.MilestonesMet["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.MilestonesMet["@value"] === "No" ? (
                        <Input
                          name="MilestonesMetDetails"
                          label="Describe:"
                          error={errors.MilestonesMetDetails}
                          value={data.MilestonesMetDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MilestonesMetDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Developmental delays or special needs:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="SpecialNeeds"
                        error={errors.SpecialNeeds}
                        value={data.SpecialNeeds["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SpecialNeeds["@value"] === "Yes" && (
                        <TextArea
                          name="SpecialNeedsDetails"
                          label="Describe:"
                          error={errors.SpecialNeedsDetails}
                          value={data.SpecialNeedsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SpecialNeedsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <b>Specialized Services</b> (Speech/Occupational/Physical
                      Therapy/Assistive Devices):
                    </div>
                    <div className="col">
                      <RadioButton
                        name="SpecializedServices"
                        error={errors.SpecializedServices}
                        value={data.SpecializedServices["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SpecializedServices["@value"] === "Yes" && (
                        <Input
                          name="SpecializedServicesDetails"
                          label="Describe:"
                          error={errors.SpecializedServicesDetails}
                          value={data.SpecializedServicesDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SpecializedServicesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Education"
                expandStatus={expandStatus}
                className="mt-2"
                title="Education/Employment"
              >
                <RadioButton
                  name="EducationStatus"
                  error={errors.EducationStatus}
                  value={data.EducationStatus["@value"]}
                  options={educationOptions}
                  onChange={handleEducationStatusChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.EducationStatus["@value"] !==
                  "Client has not started school" && (
                  <>
                    {(data.EducationStatus["@value"] === undefined ||
                      data.EducationStatus["@value"] ===
                        "Currently Enrolled in School/Client is School Aged") && (
                      <h5>Education</h5>
                    )}
                    <div className="container">
                      {(data.EducationStatus["@value"] === undefined ||
                        data.EducationStatus["@value"] ===
                          "Currently Enrolled in School/Client is School Aged") && (
                        <>
                          <div className="row">
                            <div className="col">School Name:</div>
                            <div className="col">
                              <Input
                                name="SchoolName"
                                error={errors.SchoolName}
                                value={data.SchoolName["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("SchoolName")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">Current Grade:</div>
                            <div className="col">
                              <Input
                                name="CurrentGrade"
                                error={errors.CurrentGrade}
                                value={data.CurrentGrade["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("CurrentGrade")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">Regular Classroom:</div>
                            <div className="col">
                              <RadioButton
                                name="RegularClassroom"
                                error={errors.RegularClassroom}
                                value={data.RegularClassroom["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <>Active 504 Plan:</>
                            </div>
                            <div className="col">
                              <RadioButton
                                name="Active504Plan"
                                error={errors.Active504Plan}
                                value={data.Active504Plan["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <>Active IEP:</>
                            </div>
                            <div className="col">
                              <RadioButton
                                name="ActiveIEP"
                                error={errors.ActiveIEP}
                                value={data.ActiveIEP["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.ActiveIEP["@value"] === "Yes" ? (
                                <>What classification:</>
                              ) : null}
                            </div>
                            <div className="col">
                              {data.ActiveIEP["@value"] === "Yes" ? (
                                <Input
                                  name="IEPClassification"
                                  error={errors.IEPClassification}
                                  value={data.IEPClassification["@value"]}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </>
                      )}
                      <div className="row">
                        <div className="col">
                          Summary of Client's School Experience (e.g.,
                          achievements, discipline):
                        </div>
                        <div className="col">
                          <TextArea
                            name="SchoolExperience"
                            error={errors.SchoolExperience}
                            value={data.SchoolExperience["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SchoolExperience")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Employment</h5>
                    <RadioButton
                      name="CurrentlyEmployed"
                      label="Currently Employed:"
                      error={errors.CurrentlyEmployed}
                      value={data.CurrentlyEmployed["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.CurrentlyEmployed["@value"] === "Yes" && (
                      <>
                        <Input
                          name="EmployerAndTitle"
                          label="Name of employer &amp; title"
                          error={errors.EmployerAndTitle}
                          value={data.EmployerAndTitle["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("EmployerAndTitle")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                    {data.CurrentlyEmployed["@value"] !== "N/A" && (
                      <Input
                        name="EmploymentInterests"
                        label="Employment Interests/Skills/Concerns:"
                        error={errors.EmploymentInterests}
                        value={data.EmploymentInterests["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("EmploymentInterests")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <RadioButton
                      name="MilitaryHistory"
                      label="Military History:"
                      error={errors.MilitaryHistory}
                      value={data.MilitaryHistory["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.MilitaryHistory["@value"] === undefined ||
                    data.MilitaryHistory["@value"] === "Yes" ? (
                      <TextArea
                        name="MilitaryComments"
                        label="Comments:"
                        error={errors.MilitaryComments}
                        value={data.MilitaryComments["@value"]}
                        onChange={handleChange}
                        identical={compare("MilitaryComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Activities"
                expandStatus={expandStatus}
                className="mt-2"
                title="Basic Living Skills"
              >
                <Checkbox
                  name="ADLNA"
                  label="N/A - youth is 5 or under"
                  divClassName="ms-3"
                  checked={data.ADLNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.ADLNA["@value"] !== "true" ? (
                  <>
                    <button
                      className="btn btn-link"
                      onClick={() => {
                        const obj = { ...data };

                        obj.MoneyManagement["@value"] = "Age Appropriate";
                        obj.FoodManagement["@value"] = "Age Appropriate";
                        obj.Hygiene["@value"] = "Age Appropriate";
                        obj.Health["@value"] = "Age Appropriate";
                        obj.Housekeeping["@value"] = "Age Appropriate";
                        obj.Transportation["@value"] = "Age Appropriate";
                        obj.SafetySkills["@value"] = "Age Appropriate";
                        obj.ResourceKnowledge["@value"] = "Age Appropriate";
                        obj.TimeManagement["@value"] = "Age Appropriate";
                        obj.Interaction["@value"] = "Age Appropriate";

                        setData(obj);
                      }}
                    >
                      Apply "Age Appropriate" to all
                    </button>
                    <br />
                    Does the client require additional support in the daily
                    functioning and daily activity skills listed below?
                    <div className="container">
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mb-2">
                        <div className="col">
                          <b>Skill</b>
                        </div>
                        <div className="col">
                          <b>Functioning Level</b>
                        </div>
                        <div className="col">
                          <b>Explanation</b>
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Money management:</div>
                        <div className="col">
                          <Select
                            name="MoneyManagement"
                            error={errors.MoneyManagement}
                            value={data.MoneyManagement["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.MoneyManagement["@value"] === "Delayed" ||
                          data.MoneyManagement["@value"] === "Advanced" ? (
                            <Input
                              name="MoneyManagementDetails"
                              error={errors.MoneyManagementDetails}
                              value={data.MoneyManagementDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("MoneyManagementDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Food management:</div>
                        <div className="col">
                          <Select
                            name="FoodManagement"
                            error={errors.FoodManagement}
                            value={data.FoodManagement["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.FoodManagement["@value"] === "Delayed" ||
                          data.FoodManagement["@value"] === "Advanced" ? (
                            <Input
                              name="FoodManagementDetails"
                              error={errors.FoodManagementDetails}
                              value={data.FoodManagementDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("FoodManagementDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">
                          Personal appearance and hygiene:
                        </div>
                        <div className="col">
                          <Select
                            name="Hygiene"
                            error={errors.Hygiene}
                            value={data.Hygiene["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Hygiene["@value"] === "Delayed" ||
                          data.Hygiene["@value"] === "Advanced" ? (
                            <Input
                              name="HygieneDetails"
                              error={errors.HygieneDetails}
                              value={data.HygieneDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("HygieneDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">
                          Health awareness/Personal Health:
                        </div>
                        <div className="col">
                          <Select
                            name="Health"
                            error={errors.Health}
                            value={data.Health["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Health["@value"] === "Delayed" ||
                          data.Health["@value"] === "Advanced" ? (
                            <Input
                              name="HealthDetails"
                              error={errors.HealthDetails}
                              value={data.HealthDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("HealthDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Housekeeping:</div>
                        <div className="col">
                          <Select
                            name="Housekeeping"
                            error={errors.Housekeeping}
                            value={data.Housekeeping["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Housekeeping["@value"] === "Delayed" ||
                          data.Housekeeping["@value"] === "Advanced" ? (
                            <Input
                              name="HousekeepingDetails"
                              error={errors.HousekeepingDetails}
                              value={data.HousekeepingDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("HousekeepingDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Transportation:</div>
                        <div className="col">
                          <Select
                            name="Transportation"
                            error={errors.Transportation}
                            value={data.Transportation["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Transportation["@value"] === "Delayed" ||
                          data.Transportation["@value"] === "Advanced" ? (
                            <Input
                              name="TransportationDetails"
                              error={errors.TransportationDetails}
                              value={data.TransportationDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("TransportationDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Emergency and safety skills:</div>
                        <div className="col">
                          <Select
                            name="SafetySkills"
                            error={errors.SafetySkills}
                            value={data.SafetySkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.SafetySkills["@value"] === "Delayed" ||
                          data.SafetySkills["@value"] === "Advanced" ? (
                            <Input
                              name="SafetySkillsDetails"
                              error={errors.SafetySkillsDetails}
                              value={data.SafetySkillsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("SafetySkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">
                          Knowledge of community resources:
                        </div>
                        <div className="col">
                          <Select
                            name="ResourceKnowledge"
                            error={errors.ResourceKnowledge}
                            value={data.ResourceKnowledge["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.ResourceKnowledge["@value"] === "Delayed" ||
                          data.ResourceKnowledge["@value"] === "Advanced" ? (
                            <Input
                              name="ResourceKnowledgeDetails"
                              error={errors.ResourceKnowledgeDetails}
                              value={data.ResourceKnowledgeDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("ResourceKnowledgeDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Time management:</div>
                        <div className="col">
                          <Select
                            name="TimeManagement"
                            error={errors.TimeManagement}
                            value={data.TimeManagement["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.TimeManagement["@value"] === "Delayed" ||
                          data.TimeManagement["@value"] === "Advanced" ? (
                            <Input
                              name="TimeManagementDetails"
                              error={errors.TimeManagementDetails}
                              value={data.TimeManagementDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("TimeManagementDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Interactions with Others:</div>
                        <div className="col">
                          <Select
                            name="Interaction"
                            error={errors.Interaction}
                            value={data.Interaction["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Interaction["@value"] === "Delayed" ||
                          data.Interaction["@value"] === "Advanced" ? (
                            <Input
                              name="InteractionDetails"
                              error={errors.InteractionDetails}
                              value={data.InteractionDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("InteractionDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="Friendships"
                expandStatus={expandStatus}
                className="mt-2"
                title="Friendship/Social Peer Support/Relationships"
              >
                <button
                  className="btn btn-link"
                  onClick={() => {
                    const obj = { ...data };

                    obj.CoopSkills["@value"] = "Age Appropriate";
                    obj.CommunicationSkills["@value"] = "Age Appropriate";
                    obj.EmotionalUnderstanding["@value"] = "Age Appropriate";
                    obj.ProblemSolving["@value"] = "Age Appropriate";
                    obj.ConflictResolution["@value"] = "Age Appropriate";
                    obj.DecisionMakingSkills["@value"] = "Age Appropriate";

                    setData(obj);
                  }}
                >
                  Apply "Age Appropriate" to all
                </button>
                <div className="container">
                  <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom">
                    <div className="col">
                      <b>Skill</b>
                    </div>
                    <div className="col">
                      <b>Functioning Level</b>
                    </div>
                    <div className="col">
                      <b>Explanation</b>
                    </div>
                  </div>
                  <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                    <div className="col">Cooperative play skills:</div>
                    <div className="col">
                      <Select
                        name="CoopSkills"
                        error={errors.CoopSkills}
                        value={data.CoopSkills["@value"]}
                        options={functioningLevelOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.CoopSkills["@value"] === "Delayed" ||
                      data.CoopSkills["@value"] === "Advanced" ? (
                        <Input
                          name="CoopSkillsDetails"
                          error={errors.CoopSkillsDetails}
                          value={data.CoopSkillsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("CoopSkillsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                    <div className="col">
                      Communication and interaction skills:
                    </div>
                    <div className="col">
                      <Select
                        name="CommunicationSkills"
                        error={errors.CommunicationSkills}
                        value={data.CommunicationSkills["@value"]}
                        options={functioningLevelOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.CommunicationSkills["@value"] === "Delayed" ||
                      data.CommunicationSkills["@value"] === "Advanced" ? (
                        <Input
                          name="CommunicationSkillsDetails"
                          error={errors.CommunicationSkillsDetails}
                          value={data.CommunicationSkillsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("CommunicationSkillsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                    <div className="col">
                      Emotional understanding and self-regulation skills:
                    </div>
                    <div className="col">
                      <Select
                        name="EmotionalUnderstanding"
                        error={errors.EmotionalUnderstanding}
                        value={data.EmotionalUnderstanding["@value"]}
                        options={functioningLevelOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.EmotionalUnderstanding["@value"] === "Delayed" ||
                      data.EmotionalUnderstanding["@value"] === "Advanced" ? (
                        <Input
                          name="EmotionalUnderstandingDetails"
                          error={errors.EmotionalUnderstandingDetails}
                          value={data.EmotionalUnderstandingDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("EmotionalUnderstandingDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                    <div className="col">Social problem-solving skills:</div>
                    <div className="col">
                      <Select
                        name="ProblemSolving"
                        error={errors.ProblemSolving}
                        value={data.ProblemSolving["@value"]}
                        options={functioningLevelOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.ProblemSolving["@value"] === "Delayed" ||
                      data.ProblemSolving["@value"] === "Advanced" ? (
                        <Input
                          name="ProblemSolvingDetails"
                          error={errors.ProblemSolvingDetails}
                          value={data.ProblemSolvingDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("ProblemSolvingDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                    <div className="col">Conflict resolution skills:</div>
                    <div className="col">
                      <Select
                        name="ConflictResolution"
                        error={errors.ConflictResolution}
                        value={data.ConflictResolution["@value"]}
                        options={functioningLevelOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.ConflictResolution["@value"] === "Delayed" ||
                      data.ConflictResolution["@value"] === "Advanced" ? (
                        <Input
                          name="ConflictResolutionDetails"
                          error={errors.ConflictResolutionDetails}
                          value={data.ConflictResolutionDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("ConflictResolutionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                    <div className="col">Decision making skills:</div>
                    <div className="col">
                      <Select
                        name="DecisionMakingSkills"
                        error={errors.DecisionMakingSkills}
                        value={data.DecisionMakingSkills["@value"]}
                        options={functioningLevelOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.DecisionMakingSkills["@value"] === "Delayed" ||
                      data.DecisionMakingSkills["@value"] === "Advanced" ? (
                        <Input
                          name="DecisionMakingSkillsDetails"
                          error={errors.DecisionMakingSkillsDetails}
                          value={data.DecisionMakingSkillsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("DecisionMakingSkillsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                <RadioButton
                  name="Bullied"
                  label="History of being bullied:"
                  error={errors.Bullied}
                  value={data.Bullied["@value"]}
                  options={yesNoNoneNAOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.Bullied["@value"] === "Yes" && (
                  <Input
                    name="BulliedDetails"
                    label="Describe:"
                    error={errors.BulliedDetails}
                    value={data.BulliedDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("BulliedDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="Bully"
                  label="History of bullying others:"
                  error={errors.Bully}
                  value={data.Bully["@value"]}
                  options={yesNoNoneNAOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.Bully["@value"] === "Yes" && (
                  <Input
                    name="BullyDetails"
                    label="Describe:"
                    error={errors.BullyDetails}
                    value={data.BullyDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("BullyDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="AcceptedByPeers"
                  label="Accepted by peer group:"
                  error={errors.AcceptedByPeers}
                  value={data.AcceptedByPeers["@value"]}
                  options={yesNoNoneNAOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.AcceptedByPeers["@value"] === "No" && (
                  <Input
                    name="AcceptedByPeersDetails"
                    label="Describe:"
                    error={errors.AcceptedByPeersDetails}
                    value={data.AcceptedByPeersDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("AcceptedByPeersDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="NegativeAssociations"
                  label="Negative peer group associations:"
                  error={errors.NegativeAssociations}
                  value={data.NegativeAssociations["@value"]}
                  options={yesNoNoneNAOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.NegativeAssociations["@value"] === "Yes" && (
                  <Input
                    name="NegativeAssociationsDetails"
                    label="Describe:"
                    error={errors.NegativeAssociationsDetails}
                    value={data.NegativeAssociationsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("NegativeAssociationsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="LegalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Legal History"
              >
                <RadioButton
                  name="JuvenileJusticeInvolvement"
                  label="Current involvement with Juvenile Justice:"
                  error={errors.JuvenileJusticeInvolvement}
                  value={data.JuvenileJusticeInvolvement["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.JuvenileJusticeInvolvement["@value"] === "Yes" && (
                  <TextArea
                    name="JuvenileJusticeInvolvementDetails"
                    label="Describe:"
                    error={errors.JuvenileJusticeInvolvementDetails}
                    value={data.JuvenileJusticeInvolvementDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("JuvenileJusticeInvolvementDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <Input
                  name="CPSCaseworkerName"
                  label="CPS Caseworker:"
                  error={errors.CPSCaseworkerName}
                  value={data.CPSCaseworkerName["@value"]}
                  onChange={handleChange}
                  identical={compare("CPSCaseworkerName")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="ProbationOfficer"
                  label="Probation/Parole Officer:"
                  error={errors.ProbationOfficer}
                  value={data.ProbationOfficer["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.ProbationOfficer["@value"] === "Yes" && (
                  <Input
                    name="ProbationOfficerName"
                    label="Name:"
                    error={errors.ProbationOfficerName}
                    value={data.ProbationOfficerName["@value"]}
                    onChange={handleChange}
                    identical={compare("ProbationOfficerName")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="GALCASA"
                  label="Guardian ad Litem (GAL) or Court Appointed Special Advocate (CASA):"
                  error={errors.GALCASA}
                  value={data.GALCASA["@value"]}
                  options={yesNoUnknownOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.GALCASA["@value"] === "Yes" && (
                  <Input
                    name="GALCASAName"
                    label="Name:"
                    error={errors.GALCASAName}
                    value={data.GALCASAName["@value"]}
                    onChange={handleChange}
                    identical={compare("GALCASAName")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="MedicalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Medical History/Medications"
              >
                <b>Medical</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <Input
                        name="PCP"
                        label="Primary Care Physician:"
                        error={errors.PCP}
                        value={data.PCP["@value"]}
                        onChange={handleChange}
                        identical={compare("PCP")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <div className="row">
                        <div className="col">
                          <PhoneInput
                            name="PCPPhone"
                            label="Phone:"
                            error={errors.PCPPhone}
                            value={data.PCPPhone["@value"]}
                            onChange={handleChange}
                            identical={compare("PCPPhone")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PCPPhoneUnknown"
                            label="Unknown"
                            checked={
                              data.PCPPhoneUnknown &&
                              data.PCPPhoneUnknown["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ marginTop: "30px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="LastMedicalExamDate"
                        label="Date of last medical exam:"
                        error={errors.LastMedicalExamDate}
                        value={data.LastMedicalExamDate["@value"]}
                        onChange={handleChange}
                        identical={compare("LastMedicalExamDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="LastDentalExamDate"
                        label="Date of last dental exam:"
                        error={errors.LastDentalExamDate}
                        value={data.LastDentalExamDate["@value"]}
                        onChange={handleChange}
                        identical={compare("LastDentalExamDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="UpcomingAppts"
                        label="Upcoming appointments:"
                        error={errors.UpcomingAppts}
                        value={data.UpcomingAppts["@value"]}
                        onChange={handleChange}
                        identical={compare("UpcomingAppts")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="Allergies"
                        label="Allergies"
                        divClassName="ms-3"
                        checked={data.Allergies["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Allergies["@value"] === "true" && (
                        <Input
                          name="AllergiesDetails"
                          label="List:"
                          error={errors.AllergiesDetails}
                          value={data.AllergiesDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("AllergiesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="MedicalConditions"
                        label="Medical conditions/concerns"
                        divClassName="ms-3"
                        checked={data.MedicalConditions["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.MedicalConditions["@value"] === "true" && (
                        <Input
                          name="MedicalConditionsDetails"
                          label="List:"
                          error={errors.MedicalConditionsDetails}
                          value={data.MedicalConditionsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MedicalConditionsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="NutritionalConcerns"
                        label="Nutritional concerns"
                        divClassName="ms-3"
                        checked={data.NutritionalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.NutritionalConcerns["@value"] === "true" && (
                        <Input
                          name="NutritionalConcernsDetails"
                          label="List:"
                          error={errors.NutritionalConcernsDetails}
                          value={data.NutritionalConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("NutritionalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="DentalConcerns"
                        label="Dental concerns"
                        divClassName="ms-3"
                        checked={data.DentalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.DentalConcerns["@value"] === "true" && (
                        <Input
                          name="DentalConcernsDetails"
                          label="List:"
                          error={errors.DentalConcernsDetails}
                          value={data.DentalConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("DentalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <RadioButton
                  name="Undiagnosed"
                  className="mt-2"
                  label="Client has symptoms that suggest an undiagnosed medical illness that may be causing or contributing to mental health symptoms:"
                  error={errors.Undiagnosed}
                  value={data.Undiagnosed["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.Undiagnosed["@value"] === "Yes" && (
                  <TextArea
                    name="UndiagnosedComments"
                    label="Comments:"
                    error={errors.UndiagnosedComments}
                    value={data.UndiagnosedComments["@value"]}
                    onChange={handleChange}
                    identical={compare("UndiagnosedComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <h5>Current Medications</h5>
                <Checkbox
                  name="NoMedications"
                  label="Not currently on medications"
                  divClassName="ms-3"
                  checked={data.NoMedications["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoMedications["@value"] !== "true" ? (
                  <FieldTable
                    name="CurrentMedicationDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Medication</div>
                        <div className="col border-end py-2">Dosage/Use</div>
                        <div className="col py-2">Purpose</div>
                      </div>
                      {data.CurrentMedicationDetails["@value"]
                        ? JSON.parse(
                            data.CurrentMedicationDetails["@value"]
                          ).map((item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <Input
                                      name="Medication"
                                      value={item.Medication}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    {item.Medication}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Dosage"
                                    value={item.Dosage}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedicationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Dosage
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Purpose"
                                    value={item.Purpose}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedicationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Purpose
                                )}
                              </div>
                            </div>
                          ))
                        : null}
                    </div>
                    {!formDisabled ? (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("CurrentMedicationDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Medication
                        </button>
                      </>
                    ) : null}
                    {errors.CurrentMedicationDetails !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.CurrentMedicationDetails}
                          </div>
                        </div>
                      </div>
                    )}
                  </FieldTable>
                ) : null}
                <RadioButton
                  name="PsychotropicMeds"
                  label="Past psychotropic medications:"
                  error={errors.PsychotropicMeds}
                  value={data.PsychotropicMeds["@value"]}
                  options={yesNoNoneOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PsychotropicMeds["@value"] === "Yes" && (
                  <Input
                    name="PsychotropicMedsDetails"
                    label="List:"
                    error={errors.PsychotropicMedsDetails}
                    value={data.PsychotropicMedsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("PsychotropicMedsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <h5>
                  Sexual History to Include Pertinent Sexual Issues/Concerns
                </h5>
                <Checkbox
                  name="SexualHistoryNA"
                  label="N/A - youth is 5 or under"
                  divClassName="ms-3"
                  checked={data.SexualHistoryNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.SexualHistoryNA["@value"] !== "true" && (
                  <>
                    <div className="row">
                      <div className="col">
                        <Select
                          name="SexualOrientation"
                          label="Sexual orientation:"
                          error={errors.SexualOrientation}
                          value={data.SexualOrientation["@value"]}
                          options={sexualOrientationOptions}
                          onChange={handleChange}
                          identical={compare("SexualOrientation")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          title="who someone is physically, romantically, or sexually attracted to"
                        />
                      </div>
                      <div className="col">
                        {data.SexualOrientation !== undefined &&
                          data.SexualOrientation["@value"] ===
                            "Orientation not listed" && (
                            <Input
                              name="SexualOrientationDetails"
                              label="Please specify:"
                              error={errors.SexualOrientationDetails}
                              value={
                                data.SexualOrientationDetails !== undefined
                                  ? data.SexualOrientationDetails["@value"]
                                  : ""
                              }
                              onChange={handleChange}
                              identical={compare("SexualOrientationDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                      </div>
                    </div>
                    <RadioButton
                      name="SexualRelationshipsEducation"
                      label="Education provided on healthy sexual behavior/relationships:"
                      error={errors.SexualRelationshipsEducation}
                      value={data.SexualRelationshipsEducation["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <RadioButton
                      name="SexuallyActive"
                      label="Sexually Active:"
                      error={errors.SexuallyActive}
                      value={data.SexuallyActive["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SexuallyActive["@value"] === "Yes" && (
                      <div className="ms-3">
                        <RadioButton
                          name="CaregiverAware"
                          label="Caregiver aware of sexual history:"
                          error={errors.CaregiverAware}
                          value={data.CaregiverAware["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <RadioButton
                          name="UseProtection"
                          label="Use of protection:"
                          error={errors.UseProtection}
                          value={data.UseProtection["@value"]}
                          options={useOfProtectionOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <RadioButton
                          name="AwareOfRisks"
                          label="Awareness of sexual risks:"
                          error={errors.AwareOfRisks}
                          value={data.AwareOfRisks["@value"]}
                          options={yesNoNAOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <RadioButton
                          name="Pregnancy"
                          label="Pregnancy:"
                          error={errors.Pregnancy}
                          value={data.Pregnancy["@value"]}
                          options={pregnancyOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <RadioButton
                          name="Children"
                          label="Children:"
                          error={errors.Children}
                          value={data.Children["@value"]}
                          options={yesNoNoneNAOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <Input
                          name="SexuallyActiveComments"
                          label="Comments:"
                          error={errors.SexuallyActiveComments}
                          value={data.SexuallyActiveComments["@value"]}
                          onChange={handleChange}
                          identical={compare("SexuallyActiveComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    )}
                  </>
                )}
                <RadioButton
                  name="SexualAbuseVictim"
                  label="Victim of sexual abuse:"
                  error={errors.SexualAbuseVictim}
                  value={data.SexualAbuseVictim["@value"]}
                  options={yesNoNoneOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.SexualAbuseVictim["@value"] === "Yes" ? (
                  <TextArea
                    name="SexualAbuseVictimComments"
                    label="Describe:"
                    error={errors.SexualAbuseVictimComments}
                    value={data.SexualAbuseVictimComments["@value"]}
                    onChange={handleChange}
                    identical={compare("SexualAbuseVictimComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
                <RadioButton
                  name="SexualBehavior"
                  label="Age inappropriate sexual behavior:"
                  error={errors.SexualBehavior}
                  value={data.SexualBehavior["@value"]}
                  options={yesNoNoneOptions}
                  onChange={handleChange}
                  identical={compare("SexualBehavior")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.SexualBehavior["@value"] === "Yes" ? (
                  <TextArea
                    name="SexualBehaviorComments"
                    label="Describe:"
                    error={errors.SexualBehaviorComments}
                    value={data.SexualBehaviorComments["@value"]}
                    onChange={handleChange}
                    identical={compare("SexualBehaviorComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
                <RadioButton
                  name="SexuallyActingOut"
                  label="Sexually acting out/reactive behavior:"
                  error={errors.SexuallyActingOut}
                  value={data.SexuallyActingOut["@value"]}
                  options={yesNoNoneOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.SexuallyActingOut["@value"] === "Yes" ? (
                  <TextArea
                    name="SexuallyActingOutComments"
                    label="Describe:"
                    error={errors.SexuallyActingOutComments}
                    value={data.SexuallyActingOutComments["@value"]}
                    onChange={handleChange}
                    identical={compare("SexuallyActingOutComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
                <RadioButton
                  name="SexuallyAbusive"
                  label="Sexually abusive/offending behavior:"
                  error={errors.SexuallyAbusive}
                  value={data.SexuallyAbusive["@value"]}
                  options={yesNoNoneOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.SexuallyAbusive["@value"] === "Yes" ? (
                  <TextArea
                    name="SexuallyAbusiveComments"
                    label="Describe:"
                    error={errors.SexuallyAbusiveComments}
                    value={data.SexuallyAbusiveComments["@value"]}
                    onChange={handleChange}
                    identical={compare("SexuallyAbusiveComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="MentalHealthTreatmentHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Health Treatment History"
              >
                <RadioButton
                  name="PastMentalHealthTreatment"
                  label="Past mental health counseling/treatment:"
                  error={errors.PastMentalHealthTreatment}
                  value={data.PastMentalHealthTreatment["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="MentalHealthDiagnosis"
                  label="Previous or current mental health diagnosis:"
                  error={errors.MentalHealthDiagnosis}
                  value={data.MentalHealthDiagnosis["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("MentalHealthDiagnosis")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="MHTreatmentComments"
                  label="Comments:"
                  error={errors.MHTreatmentComments}
                  value={data.MHTreatmentComments["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("MHTreatmentComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PastMentalHealthTreatment["@value"] !== "No" && (
                  <>
                    <RadioButton
                      name="PastResidentialFacilityPlacement"
                      label="Past placement in a group home/residential treatment facility/psychiatric hospital/unit:"
                      error={errors.PastResidentialFacilityPlacement}
                      value={data.PastResidentialFacilityPlacement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.PastResidentialFacilityPlacement["@value"] ===
                      "Yes" && (
                      <Input
                        name="PastResidentialFacilityPlacementComments"
                        label="Describe:"
                        error={errors.PastResidentialFacilityPlacementComments}
                        value={
                          data.PastResidentialFacilityPlacementComments[
                            "@value"
                          ]
                        }
                        onChange={handleChange}
                        identical={compare(
                          "PastResidentialFacilityPlacementComments"
                        )}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    {data.PastMentalHealthTreatment["@value"] === "Yes" ? (
                      <FieldTable
                        name="MentalHealthHistory"
                        commentsHandler={handleComments}
                      >
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col-3 border-end py-2">
                              Agency/Facility
                            </div>
                            <div className="col-2 border-end py-2">Dates</div>
                            <div className="col-4 border-end py-2">Reason</div>
                            <div className="col-3 py-2">Outcome</div>
                          </div>
                          {data.MentalHealthHistory["@value"] &&
                            JSON.parse(data.MentalHealthHistory["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col-3 border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "MentalHealthHistor"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "MentalHealthHistor"
                                            );
                                          }}
                                        ></i>
                                        <Input
                                          name="Agency"
                                          value={item.Agency}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "MentalHealthHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "MentalHealthHistory"
                                            );
                                          }}
                                        ></i>
                                        {item.Agency}
                                      </>
                                    )}
                                  </div>
                                  <div className="col-2 border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Dates"
                                        value={item.Dates}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MentalHealthHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Dates
                                    )}
                                  </div>
                                  <div className="col-4 border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Reason"
                                        value={item.Reason}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MentalHealthHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Reason
                                    )}
                                  </div>
                                  <div className="col-3 py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <Select
                                          name="Outcome"
                                          value={item.Outcome}
                                          options={outcomeOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "MentalHealthHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        {item.Outcome === "Other" ? (
                                          <Input
                                            name="OutcomeDetails"
                                            value={item.OutcomeDetails}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "MentalHealthHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : null}
                                      </>
                                    ) : item.OutcomeDetails !== undefined &&
                                      item.OutcomeDetails !== "" ? (
                                      item.OutcomeDetails
                                    ) : (
                                      item.Outcome
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                        </div>
                        {!formDisabled && (
                          <>
                            <button
                              className="btn btn-primary my-2"
                              onClick={() => {
                                handleAddRow("MentalHealthHistory", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Agency/Facility
                            </button>
                          </>
                        )}
                        {errors.MentalHealthHistory !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.MentalHealthHistory}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </FieldTable>
                    ) : null}
                  </>
                )}
                <TextArea
                  name="ClientPerspectiveOfTreatment"
                  label="Client's perspective of treatment/counseling:"
                  error={errors.ClientPerspectiveOfTreatment}
                  value={data.ClientPerspectiveOfTreatment["@value"]}
                  onChange={handleChange}
                  identical={compare("ClientPerspectiveOfTreatment")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="CaregiverPerspectiveOfTreatment"
                  label="Caregiver's perspective of treatment/counseling:"
                  error={errors.CaregiverPerspectiveOfTreatment}
                  value={data.CaregiverPerspectiveOfTreatment["@value"]}
                  onChange={handleChange}
                  identical={compare("CaregiverPerspectiveOfTreatment")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="SubstanceUseHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Alcohol/Drug Use History"
              >
                <RadioButton
                  name="SubstanceUse"
                  label="Current or past use of Alcohol/Drugs:"
                  error={errors.SubstanceUse}
                  value={data.SubstanceUse["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.SubstanceUse["@value"] !== "No" && (
                  <>
                    <FieldTable
                      name="SubstanceUseHistory"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col-3 border-end py-2">
                            Substance Type
                          </div>
                          <div className="col-2 border-end py-2">Status</div>
                          <div className="col-4 border-end py-2">Frequency</div>
                          <div className="col-3 py-2">
                            Impact on Functioning
                          </div>
                        </div>
                        {data.SubstanceUseHistory["@value"] &&
                          JSON.parse(data.SubstanceUseHistory["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col-3 border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "SubstanceUseHistor"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "SubstanceUseHistor"
                                          );
                                        }}
                                      ></i>
                                      <Select
                                        name="Type"
                                        value={item.Type}
                                        options={substanceTypeOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                      ></i>
                                      {item.Type}
                                    </>
                                  )}
                                </div>
                                <div className="col-2 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <RadioButton
                                      name="Status"
                                      value={item.Status}
                                      options={currentPastOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Status
                                  )}
                                </div>
                                <div className="col-4 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Frequency"
                                      value={item.Frequency}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Frequency
                                  )}
                                </div>
                                <div className="col-3 py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Impact"
                                      value={item.Impact}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Impact
                                  )}
                                </div>
                              </div>
                            )
                          )}
                      </div>
                      {!formDisabled && (
                        <>
                          <button
                            className="btn btn-primary my-2"
                            onClick={() => {
                              handleAddRow("SubstanceUseHistory", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Substance
                          </button>
                        </>
                      )}
                      {errors.SubstanceUseHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SubstanceUseHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                    <h5>Alcohol/Drug Treatment History</h5>
                    <Checkbox
                      name="NoPastTreatment"
                      label="No past treatment"
                      divClassName="ms-3"
                      checked={data.NoPastTreatment["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.NoPastTreatment["@value"] !== "true" ? (
                      <FieldTable
                        name="SubstanceUseTreatmentHistory"
                        commentsHandler={handleComments}
                      >
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col-3 border-end py-2">
                              Agency/Facility
                            </div>
                            <div className="col-2 border-end py-2">
                              Treatment Type
                            </div>
                            <div className="col-2 border-end py-2">Dates</div>
                            <div className="col-3 border-end py-2">Reason</div>
                            <div className="col-2 py-2">Outcome</div>
                          </div>
                          {data.SubstanceUseTreatmentHistory["@value"] &&
                            JSON.parse(
                              data.SubstanceUseTreatmentHistory["@value"]
                            ).map((item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col-3 border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "SubstanceUseTreatmentHistor"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "SubstanceUseTreatmentHistor"
                                          );
                                        }}
                                      ></i>
                                      <Input
                                        name="Agency"
                                        value={item.Agency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseTreatmentHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "SubstanceUseTreatmentHistory"
                                          );
                                        }}
                                      ></i>
                                      {item.Agency}
                                    </>
                                  )}
                                </div>
                                <div className="col-2 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <Select
                                        name="TreatmentType"
                                        value={item.TreatmentType}
                                        options={treatmentTypeOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseTreatmentHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                      {item.TreatmentType === "Other" ? (
                                        <Input
                                          name="TreatmentTypeDetails"
                                          value={item.TreatmentTypeDetails}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseTreatmentHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : null}
                                    </>
                                  ) : item.TreatmentTypeDetails !== undefined &&
                                    item.TreatmentTypeDetails !== "" ? (
                                    item.TreatmentTypeDetails
                                  ) : (
                                    item.TreatmentType
                                  )}
                                </div>
                                <div className="col-2 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Dates"
                                      value={item.Dates}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SubstanceUseTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Dates
                                  )}
                                </div>
                                <div className="col-3 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Reason"
                                      value={item.Reason}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SubstanceUseTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Reason
                                  )}
                                </div>
                                <div className="col-2 py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <Select
                                        name="Outcome"
                                        value={item.Outcome}
                                        options={outcomeOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseTreatmentHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                      {item.Outcome === "Other" ? (
                                        <Input
                                          name="OutcomeDetails"
                                          value={item.OutcomeDetails}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseTreatmentHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : null}
                                    </>
                                  ) : item.OutcomeDetails !== undefined &&
                                    item.OutcomeDetails !== "" ? (
                                    item.OutcomeDetails
                                  ) : (
                                    item.Outcome
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                        {!formDisabled && (
                          <>
                            <button
                              className="btn btn-primary my-2"
                              onClick={() => {
                                handleAddRow("SubstanceUseTreatmentHistory", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Agency/Facility
                            </button>
                          </>
                        )}
                        {errors.SubstanceUseTreatmentHistory !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.SubstanceUseTreatmentHistory}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </FieldTable>
                    ) : null}
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="TraumaHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Trauma History"
              >
                <FieldTable
                  name="TraumaHistory"
                  commentsHandler={handleComments}
                >
                  <br />
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 border-end py-2">Trauma</div>
                      <div className="col-3 border-end py-2">Status</div>
                      <div className="col-6 py-2">Comments</div>
                    </div>
                    {data.TraumaHistory["@value"]
                      ? JSON.parse(data.TraumaHistory["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col-3 border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(index, "TraumaHistory");
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(index, "TraumaHistory");
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <Select
                                      name="Trauma"
                                      value={item.Trauma}
                                      options={traumaOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "TraumaHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(index, "TraumaHistory");
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    {item.Trauma}
                                  </>
                                )}
                              </div>
                              <div className="col-3 border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Select
                                    name="Status"
                                    value={item.Status}
                                    options={statusOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TraumaHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Status
                                )}
                              </div>
                              <div className="col-6 py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <TextArea
                                    name="Comments"
                                    value={item.Comments}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TraumaHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Comments
                                )}
                              </div>
                            </div>
                          )
                        )
                      : null}
                  </div>
                  {!formDisabled && (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("TraumaHistory", { IsEditable: true });
                        }}
                      >
                        Add Trauma
                      </button>
                    </>
                  )}
                  {errors.TraumaHistory !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TraumaHistory}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <br />
                <b>Trauma Symptoms/Responses:</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="DistressingMemories"
                        label="Distressing memories"
                        divClassName="ms-3"
                        checked={data.DistressingMemories["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="BlockOut"
                        label="Blocking out events"
                        divClassName="ms-3"
                        checked={data.BlockOut["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Hypervigilance"
                        label="Hypervigilance"
                        divClassName="ms-3"
                        checked={data.Hypervigilance["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="PhysiologicalResponses"
                        label="Physiological responses"
                        divClassName="ms-3"
                        checked={
                          data.PhysiologicalResponses["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="LossOfMemories"
                        label="Loss of memories"
                        divClassName="ms-3"
                        checked={data.LossOfMemories["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Hyperarousal"
                        label="Hyperarousal"
                        divClassName="ms-3"
                        checked={data.Hyperarousal["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="SleepDisturbances"
                        label="Sleep disturbances"
                        divClassName="ms-3"
                        checked={data.SleepDisturbances["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="DevelopmentalRegression"
                        label="Developmental regression"
                        divClassName="ms-3"
                        checked={
                          data.DevelopmentalRegression["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="Nightmares"
                        label="Frightening dreams"
                        divClassName="ms-3"
                        checked={data.Nightmares["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Avoidant"
                        label="Avoidant behaviors"
                        divClassName="ms-3"
                        checked={data.Avoidant["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="DifficultyConcentratingSymptom"
                        label="Difficulty concentrating"
                        divClassName="ms-3"
                        checked={
                          data.DifficultyConcentratingSymptom["@value"] ===
                          "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="UnhealthyBoundaries"
                        label="Unhealthy boundaries"
                        divClassName="ms-3"
                        checked={data.UnhealthyBoundaries["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="Flashbacks"
                        label="Flashbacks"
                        divClassName="ms-3"
                        checked={data.Flashbacks["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Detachment"
                        label="Detachment"
                        divClassName="ms-3"
                        checked={data.Detachment["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="EmotionalDysregulation"
                        label="Emotional dysregulation"
                        divClassName="ms-3"
                        checked={
                          data.EmotionalDysregulation["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="RepetitivePlayOfEvents"
                        label="Repetitive play of events"
                        divClassName="ms-3"
                        checked={
                          data.RepetitivePlayOfEvents["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="PsychologicalDistress"
                        label="Psychological distress"
                        divClassName="ms-3"
                        checked={
                          data.PsychologicalDistress["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="DiminishedInterests"
                        label="Diminished interests"
                        divClassName="ms-3"
                        checked={data.DiminishedInterests["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Dissocation"
                        label="Dissocation"
                        divClassName="ms-3"
                        checked={data.Dissocation["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="NegativeCognitions"
                        label="Negative cognitions"
                        divClassName="ms-3"
                        checked={data.NegativeCognitions["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="SocialWithdrawalSymptom"
                        label="Social withdrawal"
                        divClassName="ms-3"
                        checked={
                          data.SocialWithdrawalSymptom["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="OtherTraumaSymptoms"
                        label="Other"
                        divClassName="ms-3"
                        checked={
                          data.OtherTraumaSymptoms &&
                          data.OtherTraumaSymptoms["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                      {data.OtherTraumaSymptoms["@value"] === "true" && (
                        <Input
                          name="OtherTraumaSymptomsDetails"
                          error={errors.OtherTraumaSymptomsDetails}
                          value={data.OtherTraumaSymptomsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherTraumaSymptomsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      <Checkbox
                        name="NoSymptoms"
                        label="None reported"
                        divClassName="ms-3"
                        checked={
                          data.NoSymptoms &&
                          data.NoSymptoms["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                </div>
                {errors.TraumaSymptomsResponse !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.TraumaSymptomsResponse}
                      </div>
                    </div>
                  </div>
                ) : null}
                <br />
                <b>Protective Factors:</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="SocialSupports"
                        label="Social supports"
                        divClassName="ms-3"
                        checked={data.SocialSupports["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="SenseOfMastery"
                        label="Sense of mastery"
                        divClassName="ms-3"
                        checked={data.SenseOfMastery["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="CaregiverStability"
                        label="Caregiver stability"
                        divClassName="ms-3"
                        checked={data.CaregiverStability["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="StrongSpiritualBeliefs"
                        label="Strong spiritual beliefs"
                        divClassName="ms-3"
                        checked={
                          data.StrongSpiritualBeliefs["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="InterpersonalSkills"
                        label="Interpersonal skills"
                        divClassName="ms-3"
                        checked={data.InterpersonalSkills["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="PositiveOutlook"
                        label="Positive outlook"
                        divClassName="ms-3"
                        checked={data.PositiveOutlook["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="PlacementStability"
                        label="Placement stability"
                        divClassName="ms-3"
                        checked={data.PlacementStability["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Pets"
                        label="Pets"
                        divClassName="ms-3"
                        checked={data.Pets["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="ValidatingExperiences"
                        label="Validating experiences"
                        divClassName="ms-3"
                        checked={
                          data.ValidatingExperiences["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Resources"
                        label="Resources"
                        divClassName="ms-3"
                        checked={data.Resources["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="TiesToExtendedFamily"
                        label="Ties to extended family"
                        divClassName="ms-3"
                        checked={data.TiesToExtendedFamily["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Adaptability"
                        label="Adaptability"
                        divClassName="ms-3"
                        checked={data.Adaptability["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="SecureAttachments"
                        label="Secure attachments"
                        divClassName="ms-3"
                        checked={data.SecureAttachments["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="StrongCulturalIdentity"
                        label="Strong cultural identity"
                        divClassName="ms-3"
                        checked={
                          data.StrongCulturalIdentity["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="OtherTraumaProtectiveFactors"
                        label="Other"
                        divClassName="ms-3"
                        checked={
                          data.OtherTraumaProtectiveFactors &&
                          data.OtherTraumaProtectiveFactors["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.OtherTraumaProtectiveFactors["@value"] ===
                        "true" && (
                        <Input
                          name="OtherTraumaProtectiveFactorsDetails"
                          error={errors.OtherTraumaProtectiveFactorsDetails}
                          value={
                            data.OtherTraumaProtectiveFactorsDetails["@value"]
                          }
                          onChange={handleChange}
                          identical={compare(
                            "OtherTraumaProtectiveFactorsDetails"
                          )}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      <Checkbox
                        name="NoTraumaProtectiveFactors"
                        label="None reported"
                        divClassName="ms-3"
                        checked={
                          data.NoTraumaProtectiveFactors["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                {errors.TraumaProtectiveFactors !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.TraumaProtectiveFactors}
                      </div>
                    </div>
                  </div>
                ) : null}
                <TextArea
                  name="CopingStrategies"
                  label="Coping strategies:"
                  error={errors.CopingStrategies}
                  value={data.CopingStrategies["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("CopingStrategies")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="ClientAwareOfTrauma"
                  label="Client is aware of traumatic events/reports:"
                  error={errors.ClientAwareOfTrauma}
                  value={data.ClientAwareOfTrauma["@value"]}
                  options={awarenessOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="ClientPerspectiveOfTrauma"
                  label="Client's perspective of trauma:"
                  error={errors.ClientPerspectiveOfTrauma}
                  value={data.ClientPerspectiveOfTrauma["@value"]}
                  onChange={handleChange}
                  identical={compare("ClientPerspectiveOfTrauma")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="CaregiverPerspectiveOfTrauma"
                  label="Caregiver's perspective of trauma:"
                  error={errors.CaregiverPerspectiveOfTrauma}
                  value={data.CaregiverPerspectiveOfTrauma["@value"]}
                  onChange={handleChange}
                  identical={compare("CaregiverPerspectiveOfTrauma")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="TraumaHistoryComments"
                  label="Comments:"
                  error={errors.TraumaHistoryComments}
                  value={data.TraumaHistoryComments["@value"]}
                  onChange={handleChange}
                  identical={compare("TraumaHistoryComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="MentalStatus"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Status"
              >
                <div className="container">
                  <div className="row border-bottom">
                    <div className="col-3">Appearance</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="AppropriateAppearance"
                        label="Appropriate"
                        divClassName="ms-3"
                        checked={
                          data.AppropriateAppearance["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Neat"
                        label="Neat"
                        divClassName="ms-3"
                        checked={data.Neat["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Disheveled"
                        label="Disheveled"
                        divClassName="ms-3"
                        checked={data.Disheveled["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="BizarreAppearance"
                        label="Bizarre"
                        divClassName="ms-3"
                        checked={data.BizarreAppearance["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherAppearance"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherAppearance["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherAppearance["@value"] === "true" ? (
                        <Input
                          name="OtherAppearanceDetails"
                          label="Comments:"
                          error={errors.OtherAppearanceDetails}
                          value={data.OtherAppearanceDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherAppearanceDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSAppearance !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSAppearance}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Behavior</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Cooperative"
                        label="Cooperative"
                        divClassName="ms-3"
                        checked={data.Cooperative["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Guarded"
                        label="Guarded"
                        divClassName="ms-3"
                        checked={data.Guarded["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Hyperactive"
                        label="Hyperactive"
                        divClassName="ms-3"
                        checked={data.Hyperactive["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Agitated"
                        label="Agitated"
                        divClassName="ms-3"
                        checked={data.Agitated["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Paranoid"
                        label="Paranoid"
                        divClassName="ms-3"
                        checked={data.Paranoid["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Tics"
                        label="Tics"
                        divClassName="ms-3"
                        checked={data.Tics["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Impulsive"
                        label="Impulsive"
                        divClassName="ms-3"
                        checked={data.Impulsive["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Aggressive"
                        label="Aggressive"
                        divClassName="ms-3"
                        checked={data.Aggressive["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Withdrawn"
                        label="Withdrawn"
                        divClassName="ms-3"
                        checked={data.Withdrawn["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="BizarreBehavior"
                        label="Bizarre"
                        divClassName="ms-3"
                        checked={data.BizarreBehavior["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Rigidity"
                        label="Rigidity"
                        divClassName="ms-3"
                        checked={data.Rigidity["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherBehavior"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherBehavior["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherBehavior["@value"] === "true" ? (
                        <Input
                          name="OtherBehaviorDetails"
                          label="Comments:"
                          error={errors.OtherBehaviorDetails}
                          value={data.OtherBehaviorDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherBehaviorDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSBehavior !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSBehavior}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Speech</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Normal"
                        label="Normal"
                        divClassName="ms-3"
                        checked={data.Normal["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Slurred"
                        label="Slurred"
                        divClassName="ms-3"
                        checked={data.Slurred["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Pressured"
                        label="Pressured"
                        divClassName="ms-3"
                        checked={data.Pressured["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Tangential"
                        label="Tangential"
                        divClassName="ms-3"
                        checked={data.Tangential["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Incoherent"
                        label="Incoherent"
                        divClassName="ms-3"
                        checked={data.Incoherent["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Stutters"
                        label="Stutters"
                        divClassName="ms-3"
                        checked={data.Stutters["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherSpeech"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherSpeech["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherSpeech["@value"] === "true" ? (
                        <Input
                          name="OtherSpeechDetails"
                          label="Comments:"
                          error={errors.OtherSpeechDetails}
                          value={data.OtherSpeechDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherSpeechDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSSpeech !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSSpeech}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Mood</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Euthymic"
                        label="Euthymic"
                        divClassName="ms-3"
                        checked={data.Euthymic["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Anxious"
                        label="Anxious"
                        divClassName="ms-3"
                        checked={data.Anxious["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Angry"
                        label="Angry"
                        divClassName="ms-3"
                        checked={data.Angry["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Depressed"
                        label="Depressed"
                        divClassName="ms-3"
                        checked={data.Depressed["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Euphoric"
                        label="Euphoric"
                        divClassName="ms-3"
                        checked={data.Euphoric["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Irritable"
                        label="Irritable"
                        divClassName="ms-3"
                        checked={data.Irritable["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Fearful"
                        label="Fearful"
                        divClassName="ms-3"
                        checked={data.Fearful["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherMood"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherMood["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherMood["@value"] === "true" ? (
                        <Input
                          name="OtherMoodDetails"
                          label="Comments:"
                          error={errors.OtherMoodDetails}
                          value={data.OtherMoodDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherMoodDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSMood !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSMood}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Affect</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="FullRange"
                        label="Full range"
                        divClassName="ms-3"
                        checked={data.FullRange["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Restricted"
                        label="Restricted"
                        divClassName="ms-3"
                        checked={data.Restricted["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Blunted"
                        label="Blunted"
                        divClassName="ms-3"
                        checked={data.Blunted["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Flat"
                        label="Flat"
                        divClassName="ms-3"
                        checked={data.Flat["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Inappropriate"
                        label="Inappropriate"
                        divClassName="ms-3"
                        checked={data.Inappropriate["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherAffect"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherAffect["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherAffect["@value"] === "true" ? (
                        <Input
                          name="OtherAffectDetails"
                          label="Comments:"
                          error={errors.OtherAffectDetails}
                          value={data.OtherAffectDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherAffectDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSAffect !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSAffect}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Thought Process/Perception</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Logical"
                        label="Logical"
                        divClassName="ms-3"
                        checked={data.Logical["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Preoccupations"
                        label="Preoccupations"
                        divClassName="ms-3"
                        checked={data.Preoccupations["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Delusions"
                        label="Delusions"
                        divClassName="ms-3"
                        checked={data.Delusions["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Hallucinations"
                        label="Hallucinations"
                        divClassName="ms-3"
                        checked={data.Hallucinations["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Racing"
                        label="Racing"
                        divClassName="ms-3"
                        checked={data.Racing["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Disorganized"
                        label="Disorganized"
                        divClassName="ms-3"
                        checked={data.Disorganized["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Concrete"
                        label="Concrete"
                        divClassName="ms-3"
                        checked={data.Concrete["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="SuicidalIdeation"
                        label="Suicidal Ideation"
                        divClassName="ms-3"
                        checked={data.SuicidalIdeation["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="HomicidalIdeation"
                        label="Homicidal Ideation"
                        divClassName="ms-3"
                        checked={data.HomicidalIdeation["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherPerception"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherPerception["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherPerception["@value"] === "true" ? (
                        <Input
                          name="OtherPerceptionDetails"
                          label="Comments:"
                          error={errors.OtherPerceptionDetails}
                          value={data.OtherPerceptionDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherPerceptionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSPerception !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSPerception}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">
                      Cognition/Intellectual Functioning
                    </div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="NoImpairment"
                        label="No Impairment"
                        divClassName="ms-3"
                        checked={data.NoImpairment["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="ShortAttentionSpan"
                        label="Short Attention Span"
                        divClassName="ms-3"
                        checked={data.ShortAttentionSpan["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="DifficultyConcentrating"
                        label="Difficulty Concentrating"
                        divClassName="ms-3"
                        checked={
                          data.DifficultyConcentrating["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Distractibility"
                        label="Distractibility"
                        divClassName="ms-3"
                        checked={data.Distractibility["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="MemoryImpairment"
                        label="Memory Impairment"
                        divClassName="ms-3"
                        checked={data.MemoryImpairment["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="LowIntelligence"
                        label="Low Intelligence"
                        divClassName="ms-3"
                        checked={data.LowIntelligence["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherCognition"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherCognition["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherCognition["@value"] === "true" ? (
                        <Input
                          name="OtherCognitionDetails"
                          label="Comments:"
                          error={errors.OtherCognitionDetails}
                          value={data.OtherCognitionDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherCognitionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSCognition !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSCognition}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Insight/Judgment</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="AppropriateInsight"
                        label="Appropriate"
                        divClassName="ms-3"
                        checked={data.AppropriateInsight["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Fair"
                        label="Fair"
                        divClassName="ms-3"
                        checked={data.Fair["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Impaired"
                        label="Impaired"
                        divClassName="ms-3"
                        checked={data.Impaired["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherInsight"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherInsight["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherInsight["@value"] === "true" ? (
                        <Input
                          name="OtherInsightDetails"
                          label="Comments:"
                          error={errors.OtherInsightDetails}
                          value={data.OtherInsightDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherInsightDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSInsight !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSInsight}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2">
                    <div className="col-3">Comments/Observations</div>
                    <div className="col-9">
                      <TextArea
                        name="MentalStatusComments"
                        error={errors.MentalStatusComments}
                        value={data.MentalStatusComments["@value"]}
                        onChange={handleChange}
                        identical={compare("MentalStatusComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Lethality"
                expandStatus={expandStatus}
                className="mt-2"
                title="Lethality"
              >
                <Checkbox
                  name="LethalityNA"
                  label="N/A - youth is 5 or under with no history of harm to self or others"
                  divClassName="ms-3"
                  checked={data.LethalityNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.LethalityNA["@value"] !== "true" && (
                  <>
                    <b>Suicide/Harm to Self</b>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Self-injurious or Self-abusive Behavior
                        </div>
                        <div className="col py-2">
                          <Select
                            name="SelfAbusiveBehavior"
                            error={errors.SelfAbusiveBehavior}
                            value={data.SelfAbusiveBehavior["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Suicidal threats/gestures
                        </div>
                        <div className="col py-2">
                          <Select
                            name="SuicidalThreats"
                            error={errors.SuicidalThreats}
                            value={data.SuicidalThreats["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Suicide Attempt
                        </div>
                        <div className="col py-2">
                          <Select
                            name="SuicideAttempt"
                            error={errors.SuicideAttempt}
                            value={data.SuicideAttempt["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Parent/Caregiver concerns of suicide/self-harm
                        </div>
                        <div className="col py-2">
                          <Select
                            name="CaregiverConcernsOfSelfHarm"
                            error={errors.CaregiverConcernsOfSelfHarm}
                            value={data.CaregiverConcernsOfSelfHarm["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Other expressed concerns of suicide/self-harm
                        </div>
                        <div className="col py-2">
                          <Select
                            name="OtherConcernsOfSelfHarm"
                            error={errors.OtherConcernsOfSelfHarm}
                            value={data.OtherConcernsOfSelfHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Current Risk of Harm to Self
                        </div>
                        <div className="col py-2">
                          <Select
                            name="CurrentSelfHarmRisk"
                            error={errors.CurrentSelfHarmRisk}
                            value={data.CurrentSelfHarmRisk["@value"]}
                            options={riskOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    {data.SelfAbusiveBehavior["@value"] !== "None/Never" ||
                    data.SuicidalThreats["@value"] !== "None/Never" ||
                    data.SuicideAttempt["@value"] !== "None/Never" ||
                    data.CaregiverConcernsOfSelfHarm["@value"] !== "No" ||
                    (data.OtherConcernsOfSelfHarm["@value"] !== "No" &&
                      data.OtherConcernsOfSelfHarm["@value"] !==
                        "None reported") ||
                    data.CurrentSelfHarmRisk["@value"] !== "None Noted" ? (
                      <TextArea
                        name="SelfHarmComments"
                        label="Please provide any additional information:"
                        error={errors.SelfHarmComments}
                        value={data.SelfHarmComments["@value"]}
                        onChange={handleChange}
                        identical={compare("SelfHarmComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <br />
                    <b>Harm to Others/Endangerment</b>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Threats to harm/endanger others
                        </div>
                        <div className="col py-2">
                          <Select
                            name="ThreatsToHarmOthers"
                            error={errors.ThreatsToHarmOthers}
                            value={data.ThreatsToHarmOthers["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Thoughts of harming/endangering others
                        </div>
                        <div className="col py-2">
                          <Select
                            name="ThoughtsOfHarm"
                            error={errors.ThoughtsOfHarm}
                            value={data.ThoughtsOfHarm["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <Select
                            name="ActsOfHarm"
                            error={errors.ActsOfHarm}
                            value={data.ActsOfHarm["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts of harm/endangering animals
                        </div>
                        <div className="col py-2">
                          <Select
                            name="ActsOfHarmAnimals"
                            error={errors.ActsOfHarmAnimals}
                            value={data.ActsOfHarmAnimals["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts involving weapons
                        </div>
                        <div className="col py-2">
                          <Select
                            name="ActsInvolvingWeapons"
                            error={errors.ActsInvolvingWeapons}
                            value={data.ActsInvolvingWeapons["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Parent/Caregiver concerns of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <Select
                            name="CaregiverConcernOfOtherHarm"
                            error={errors.CaregiverConcernOfOtherHarm}
                            value={data.CaregiverConcernOfOtherHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Other expressed concerns of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <Select
                            name="OtherConcernsOfOtherHarm"
                            error={errors.OtherConcernsOfOtherHarm}
                            value={data.OtherConcernsOfOtherHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Current Risk of Harm to Others
                        </div>
                        <div className="col py-2">
                          <Select
                            name="CurrentOtherHarmRisk"
                            error={errors.CurrentOtherHarmRisk}
                            value={data.CurrentOtherHarmRisk["@value"]}
                            options={riskOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    {data.ThreatsToHarmOthers["@value"] !== "None/Never" ||
                    data.ThoughtsOfHarm["@value"] !== "None/Never" ||
                    data.ActsOfHarm["@value"] !== "None/Never" ||
                    data.ActsOfHarmAnimals["@value"] !== "None/Never" ||
                    data.ActsInvolvingWeapons["@value"] !== "None/Never" ||
                    data.CaregiverConcernOfOtherHarm["@value"] !== "No" ||
                    (data.OtherConcernsOfOtherHarm["@value"] !== "No" &&
                      data.OtherConcernsOfOtherHarm["@value"] !==
                        "None reported") ||
                    data.CurrentOtherHarmRisk["@value"] !== "None Noted" ? (
                      <TextArea
                        name="OtherHarmComments"
                        label="Please provide any additional information:"
                        error={errors.OtherHarmComments}
                        value={data.OtherHarmComments["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherHarmComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    {data.SelfAbusiveBehavior["@value"] === "Current" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 30 Days" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 6 Months" ||
                    data.SuicidalThreats["@value"] === "Current" ||
                    data.SuicidalThreats["@value"] === "Last 30 Days" ||
                    data.SuicidalThreats["@value"] === "Last 6 Months" ||
                    data.SuicideAttempt["@value"] === "Current" ||
                    data.SuicideAttempt["@value"] === "Last 30 Days" ||
                    data.SuicideAttempt["@value"] === "Last 6 Months" ? (
                      <div>
                        Based on the above answers, it is required to complete a{" "}
                        <Link
                          to={`/client/${clientid}?tab=cpass`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          CSSRS
                        </Link>
                        .
                      </div>
                    ) : null}

                    {data.SelfAbusiveBehavior["@value"] === "Current" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 30 Days" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 6 Months" ||
                    data.SuicidalThreats["@value"] === "Current" ||
                    data.SuicidalThreats["@value"] === "Last 30 Days" ||
                    data.SuicidalThreats["@value"] === "Last 6 Months" ||
                    data.SuicideAttempt["@value"] === "Current" ||
                    data.SuicideAttempt["@value"] === "Last 30 Days" ||
                    data.SuicideAttempt["@value"] === "Last 6 Months" ||
                    data.ThreatsToHarmOthers["@value"] === "Current" ||
                    data.ThreatsToHarmOthers["@value"] === "Last 30 Days" ||
                    data.ThreatsToHarmOthers["@value"] === "Last 6 Months" ||
                    data.ThoughtsOfHarm["@value"] === "Current" ||
                    data.ThoughtsOfHarm["@value"] === "Last 30 Days" ||
                    data.ThoughtsOfHarm["@value"] === "Last 6 Months" ||
                    data.ActsOfHarm["@value"] === "Current" ||
                    data.ActsOfHarm["@value"] === "Last 30 Days" ||
                    data.ActsOfHarm["@value"] === "Last 6 Months" ||
                    data.ActsOfHarmAnimals["@value"] === "Current" ||
                    data.ActsOfHarmAnimals["@value"] === "Last 30 Days" ||
                    data.ActsOfHarmAnimals["@value"] === "Last 6 Months" ||
                    data.ActsInvolvingWeapons["@value"] === "Current" ||
                    data.ActsInvolvingWeapons["@value"] === "Last 30 Days" ||
                    data.ActsInvolvingWeapons["@value"] === "Last 6 Months" ? (
                      <>
                        Based on the responses to these questions, please
                        complete a{" "}
                        <Link
                          to={`/client/${clientid}?tab=forms`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Safety Plan
                        </Link>
                        .
                      </>
                    ) : null}
                    <RadioButton
                      name="SafetyPlanStatus"
                      label="Safety plan status:"
                      error={errors.SafetyPlanStatus}
                      value={data.SafetyPlanStatus["@value"]}
                      options={safetyPlanStatusOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <TextArea
                      name="LethalityComments"
                      label="Comments/Plan:"
                      error={errors.LethalityComments}
                      value={data.LethalityComments["@value"]}
                      onChange={handleChange}
                      identical={compare("LethalityComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="SymptomChecklist"
                expandStatus={expandStatus}
                className="mt-2"
                title="Symptom Checklist"
              >
                Mark if symptoms of diagnostic category are present. Describe
                symptom <b>frequency, intensity and duration</b> if present to
                assist in formulating diagnosis.
                <br />
                <br />
                <Checkbox
                  name="NotProvidingDx"
                  label="Not providing client with a diagnosis"
                  divClassName="ms-3 fw-bold"
                  checked={data.NotProvidingDx["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NotProvidingDx["@value"] !== "true" && (
                  <>
                    <div className="border rounded mb-2 px-2 pt-2 mt-2">
                      <Checkbox
                        name="TraumaSymptoms"
                        label="Symptoms of Trauma and Other Stressor-Related Disorders"
                        divClassName="ms-3 fw-bold"
                        checked={data.TraumaSymptoms["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleTraumaSymptomsChange}
                        disabled={formDisabled}
                      />
                      {data.TraumaSymptoms["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="TraumaticExposure"
                                  label="Exposure to a traumatic event"
                                  divClassName="ms-3"
                                  checked={
                                    data.TraumaticExposure["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="AvoidantTrauma"
                                  label="Persistent avoidant behavior"
                                  divClassName="ms-3"
                                  checked={
                                    data.AvoidantTrauma["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="HypervigilanceTrauma"
                                  label="Hypervigilance"
                                  divClassName="ms-3"
                                  checked={
                                    data.HypervigilanceTrauma["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="IntrusiveDreams"
                                  label="Intrusive dreams/memories"
                                  divClassName="ms-3"
                                  checked={
                                    data.IntrusiveDreams["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="NegativeThoughts"
                                  label="Negative thoughts and mood"
                                  divClassName="ms-3"
                                  checked={
                                    data.NegativeThoughts["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="CocentrationProblems"
                                  label="Problems with concentration"
                                  divClassName="ms-3"
                                  checked={
                                    data.CocentrationProblems["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="RecklessBehaviorTrauma"
                                  label="Irritable or reckless behavior"
                                  divClassName="ms-3"
                                  checked={
                                    data.RecklessBehaviorTrauma["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SleepDisturbancesTrauma"
                                  label="Sleep disturbances"
                                  divClassName="ms-3"
                                  checked={
                                    data.SleepDisturbancesTrauma["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="OtherTrauma"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherTrauma["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherTrauma["@value"] === "true" ? (
                                  <Input
                                    name="OtherTraumaDetails"
                                    error={errors.OtherTraumaDetails}
                                    value={data.OtherTraumaDetails["@value"]}
                                    onChange={handleChange}
                                    identical={compare("OtherTraumaDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.TraumaSymptoms !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.TraumaSymptoms}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="TraumaSymptomsFrequency"
                            label="Frequency:"
                            error={errors.TraumaSymptomsFrequency}
                            value={data.TraumaSymptomsFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("TraumaSymptomsFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="TraumaSymptomsIntensity"
                            label="Intensity:"
                            error={errors.TraumaSymptomsIntensity}
                            value={data.TraumaSymptomsIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("TraumaSymptomsIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="TraumaSymptomsDuration"
                            label="Duration:"
                            error={errors.TraumaSymptomsDuration}
                            value={data.TraumaSymptomsDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("TraumaSymptomsDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="TraumaSymptomsOnset"
                            label="Onset:"
                            error={errors.TraumaSymptomsOnset}
                            value={data.TraumaSymptomsOnset["@value"]}
                            onChange={handleChange}
                            identical={compare("TraumaSymptomsOnset")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnoses to Consider: Post-traumatic Stress Disorder
                          (PTSD), Acute Stress Disorder, Adjustment Disorder,
                          Other Specified Trauma-and Stressor-Related Disorder
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="AnxietyDisorder"
                        label="Symptoms of Anxiety"
                        divClassName="ms-3 fw-bold"
                        checked={data.AnxietyDisorder["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleAnxietyDisorderChange}
                        disabled={formDisabled}
                      />
                      {data.AnxietyDisorder !== undefined &&
                        data.AnxietyDisorder["@value"] === "true" && (
                          <>
                            <div className="container">
                              <div className="row">
                                <div className="col">
                                  <Checkbox
                                    name="AnxietyDisorderExcessWorry"
                                    label="Excess worry"
                                    divClassName="ms-3"
                                    checked={
                                      data.AnxietyDisorderExcessWorry[
                                        "@value"
                                      ] === "true"
                                    }
                                    commentsHandler={handleComments}
                                    onChange={handleChange}
                                    disabled={formDisabled}
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="AnxietyDisorderEasilyFatigued"
                                    label="Easily fatigued"
                                    divClassName="ms-3"
                                    checked={
                                      data.AnxietyDisorderEasilyFatigued[
                                        "@value"
                                      ] === "true"
                                    }
                                    onChange={handleChange}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="MuscleTension"
                                    label="Muscle tension"
                                    divClassName="ms-3"
                                    checked={
                                      data.MuscleTension["@value"] === "true"
                                    }
                                    onChange={handleChange}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                </div>
                              </div>
                              <div className="row">
                                <div className="col">
                                  <Checkbox
                                    name="Overworried"
                                    label="Difficulty controlling worry"
                                    divClassName="ms-3"
                                    checked={
                                      data.Overworried["@value"] === "true"
                                    }
                                    onChange={handleChange}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="AnxietyDisorderDifficultyConcentrating"
                                    label="Difficulty concentrating"
                                    divClassName="ms-3"
                                    checked={
                                      data
                                        .AnxietyDisorderDifficultyConcentrating[
                                        "@value"
                                      ] === "true"
                                    }
                                    commentsHandler={handleComments}
                                    onChange={handleChange}
                                    disabled={formDisabled}
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="SleepDisturbancesAnxiety"
                                    label="Sleep difficulties"
                                    divClassName="ms-3"
                                    checked={
                                      data.SleepDisturbancesAnxiety[
                                        "@value"
                                      ] === "true"
                                    }
                                    onChange={handleChange}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                </div>
                              </div>
                              <div className="row">
                                <div className="col">
                                  <Checkbox
                                    name="RestlessAnxiety"
                                    label="Restless"
                                    divClassName="ms-3"
                                    checked={
                                      data.RestlessAnxiety["@value"] === "true"
                                    }
                                    commentsHandler={handleComments}
                                    onChange={handleChange}
                                    disabled={formDisabled}
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Irritability"
                                    label="Irritability"
                                    divClassName="ms-3"
                                    checked={
                                      data.Irritability["@value"] === "true"
                                    }
                                    commentsHandler={handleComments}
                                    onChange={handleChange}
                                    disabled={formDisabled}
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="OtherAnxiety"
                                    label="Other:"
                                    divClassName="ms-3"
                                    checked={
                                      data.OtherAnxiety["@value"] === "true"
                                    }
                                    commentsHandler={handleComments}
                                    onChange={handleChange}
                                    disabled={formDisabled}
                                  />
                                  {data.OtherAnxiety !== undefined &&
                                  data.OtherAnxiety["@value"] === "true" ? (
                                    <Input
                                      name="OtherAnxietyDetails"
                                      error={errors.OtherAnxietyDetails}
                                      value={data.OtherAnxietyDetails["@value"]}
                                      onChange={handleChange}
                                      identical={compare("OtherAnxietyDetails")}
                                      commentsHandler={handleComments}
                                      disabled={formDisabled}
                                    />
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            {errors.AnxietyDisorder !== undefined ? (
                              <div className="row">
                                <div className="col">
                                  <div className="alert alert-danger">
                                    {errors.AnxietyDisorder}
                                  </div>
                                </div>
                              </div>
                            ) : null}
                            <Input
                              name="AnxietyDisorderFrequency"
                              label="Frequency:"
                              error={errors.AnxietyDisorderFrequency}
                              value={data.AnxietyDisorderFrequency["@value"]}
                              onChange={handleChange}
                              identical={compare("AnxietyDisorderFrequency")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <Input
                              name="AnxietyDisorderIntensity"
                              label="Intensity:"
                              error={errors.AnxietyDisorderIntensity}
                              value={data.AnxietyDisorderIntensity["@value"]}
                              onChange={handleChange}
                              identical={compare("AnxietyDisorderIntensity")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <Input
                              name="AnxietyDisorderDuration"
                              label="Duration:"
                              error={errors.AnxietyDisorderDuration}
                              value={data.AnxietyDisorderDuration["@value"]}
                              onChange={handleChange}
                              identical={compare("AnxietyDisorderDuration")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            Diagnosis to consider: Generalized Anxiety Disorder
                          </>
                        )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="DepressionSymptoms"
                        label="Symptoms of Depression"
                        divClassName="ms-3 fw-bold"
                        checked={data.DepressionSymptoms["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleDepressionChange}
                        disabled={formDisabled}
                      />
                      {data.DepressionSymptoms["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DepressedMood"
                                  label="Depressed mood most of the day, nearly every day"
                                  divClassName="ms-3"
                                  checked={
                                    data.DepressedMood["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SleepChanges"
                                  label="Insomnia or hypersomnia"
                                  divClassName="ms-3"
                                  checked={
                                    data.SleepChanges["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Guilt"
                                  label="Feelings of worthlessness/guilt"
                                  divClassName="ms-3"
                                  checked={data.Guilt["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DiminishedInterest"
                                  label="Diminished interest or pleasure in most daily activities"
                                  divClassName="ms-3"
                                  checked={
                                    data.DiminishedInterest["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="PurposelessMovement"
                                  label="Purposeless movement"
                                  divClassName="ms-3"
                                  checked={
                                    data.PurposelessMovement["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DifficultyConcentratingDepression"
                                  label="Inability to think/concentrate"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyConcentratingDepression !==
                                      undefined &&
                                    data.DifficultyConcentratingDepression[
                                      "@value"
                                    ] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="WeightChanges"
                                  label="Significant weight loss or weight gain"
                                  divClassName="ms-3"
                                  checked={
                                    data.WeightChanges["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FatigueDepression"
                                  label="Fatigue"
                                  divClassName="ms-3"
                                  checked={
                                    data.FatigueDepression["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ThoughtsOfDeath"
                                  label="Recurrent thoughts of death"
                                  divClassName="ms-3"
                                  checked={
                                    data.ThoughtsOfDeath["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="OtherDepression"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherDepression["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherDepression["@value"] === "true" ? (
                                  <Input
                                    name="OtherDepressionDetails"
                                    error={errors.OtherDepressionDetails}
                                    value={
                                      data.OtherDepressionDetails !== undefined
                                        ? data.OtherDepressionDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "OtherDepressionDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.DepressionSymptoms !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DepressionSymptoms}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="DepressionFrequency"
                            label="Frequency:"
                            error={errors.DepressionFrequency}
                            value={data.DepressionFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("DepressionFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="DepressionIntensity"
                            label="Intensity:"
                            error={errors.DepressionIntensity}
                            value={data.DepressionIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("DepressionIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="DepressionDuration"
                            label="Duration:"
                            error={errors.DepressionDuration}
                            value={data.DepressionDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("DepressionDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Major Depressive Disorder,
                          Other Specified Depressive Disorder, Unspecified
                          Depressive Disorder
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="AttentionHyperactivity"
                        label="Symptoms of Attention and Hyperactivity"
                        divClassName="ms-3 fw-bold"
                        checked={
                          data.AttentionHyperactivity["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleAttentionHyperactivityChange}
                        disabled={formDisabled}
                      />
                      {data.AttentionHyperactivity["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="AttentionHyperactivityEasilyDistracted"
                                  label="Easily distracted"
                                  divClassName="ms-3"
                                  checked={
                                    data.AttentionHyperactivityEasilyDistracted[
                                      "@value"
                                    ] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DifficultyFollowThrough"
                                  label="Difficulty following-through"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyFollowThrough["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Forgetful"
                                  label="Forgetful"
                                  divClassName="ms-3"
                                  checked={data.Forgetful["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DifficultyListening"
                                  label="Difficulty listening"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyListening["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DisorganizedAttention"
                                  label="Disorganized in tasks and activities"
                                  divClassName="ms-3"
                                  checked={
                                    data.DisorganizedAttention["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Fidgety"
                                  label="Fidgety"
                                  divClassName="ms-3"
                                  checked={data.Fidgety["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="LackOfDetails"
                                  label="Lack of attention to detail"
                                  divClassName="ms-3"
                                  checked={
                                    data.LackOfDetails["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LosesThings"
                                  label="Loses things"
                                  divClassName="ms-3"
                                  checked={
                                    data.LosesThings["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="InterruptsOthers"
                                  label="Interrupts others"
                                  divClassName="ms-3"
                                  checked={
                                    data.InterruptsOthers["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="UnableToSitStill"
                                  label="Unable to sit still"
                                  divClassName="ms-3"
                                  checked={
                                    data.UnableToSitStill["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ExcessiveTalking"
                                  label="Excessive talking"
                                  divClassName="ms-3"
                                  checked={
                                    data.ExcessiveTalking["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DifficultyWaiting"
                                  label="Difficulty waiting for their turn"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyWaiting["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="OtherAttention"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherAttention["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.OtherAttention["@value"] === "true" ? (
                                  <Input
                                    name="OtherAttentionDetails"
                                    error={errors.OtherAttentionDetails}
                                    value={data.OtherAttentionDetails["@value"]}
                                    onChange={handleChange}
                                    identical={compare("OtherAttentionDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.AttentionHyperactivity !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.AttentionHyperactivity}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="AttentionHyperactivityFrequency"
                            label="Frequency:"
                            error={errors.AttentionHyperactivityFrequency}
                            value={
                              data.AttentionHyperactivityFrequency["@value"]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "AttentionHyperactivityFrequency"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="AttentionHyperactivityIntensity"
                            label="Intensity:"
                            error={errors.AttentionHyperactivityIntensity}
                            value={
                              data.AttentionHyperactivityIntensity["@value"]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "AttentionHyperactivityIntensity"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="AttentionHyperactivityDuration"
                            label="Duration:"
                            error={errors.AttentionHyperactivityDuration}
                            value={
                              data.AttentionHyperactivityDuration["@value"]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "AttentionHyperactivityDuration"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Attention-Deficit/Hyperactivity
                          Disorder
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="SU"
                        label="Symptoms of Substance Use Disorders"
                        divClassName="ms-3 fw-bold"
                        checked={data.SU["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleSubstanceUseSymptomsChange}
                        disabled={formDisabled}
                      />
                      {data.SU["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="PatternOfSubstanceUse"
                                  label="Takes substances in large amounts and/or over long periods of time"
                                  divClassName="ms-3"
                                  checked={
                                    data.PatternOfSubstanceUse["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DesiresCutDownUse"
                                  label="Persistent desire to cut down on substance use"
                                  divClassName="ms-3"
                                  checked={
                                    data.DesiresCutDownUse["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="TimeSpentObtaining"
                                  label="Spends a significant amount of time obtaining substance(s)"
                                  divClassName="ms-3"
                                  checked={
                                    data.TimeSpentObtaining["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Cravings"
                                  label="Cravings for substances"
                                  divClassName="ms-3"
                                  checked={data.Cravings["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FailureOfResponsibilities"
                                  label="Failure to fulfill major roles at work, school, or home"
                                  divClassName="ms-3"
                                  checked={
                                    data.FailureOfResponsibilities["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="NegativeImpact"
                                  label="Use consistently negatively impacts relationships"
                                  divClassName="ms-3"
                                  checked={
                                    data.NegativeImpact["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="AbandonsActivities"
                                  label="Giving up/reducing previously enjoyed activities"
                                  divClassName="ms-3"
                                  checked={
                                    data.AbandonsActivities["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Tolerance"
                                  label="Tolerance"
                                  divClassName="ms-3"
                                  checked={data.Tolerance["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SUWithdrawal"
                                  label="Withdrawal"
                                  divClassName="ms-3"
                                  checked={
                                    data.SUWithdrawal["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="SUOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={data.SUOther["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.SUOther["@value"] === "true" ? (
                                  <Input
                                    name="SUOtherDetails"
                                    error={errors.SUOtherDetails}
                                    value={data.SUOtherDetails["@value"]}
                                    onChange={handleChange}
                                    identical={compare("SUOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.SU !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.SU}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="SubstanceUseFrequency"
                            label="Frequency:"
                            error={errors.SubstanceUseFrequency}
                            value={data.SubstanceUseFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("SubstanceUseFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="SubstanceUseIntensity"
                            label="Intensity:"
                            error={errors.SubstanceUseIntensity}
                            value={data.SubstanceUseIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("SubstanceUseIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="SubstanceUseDuration"
                            label="Duration:"
                            error={errors.SubstanceUseDuration}
                            value={data.SubstanceUseDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("SubstanceUseDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="DMDD"
                        label="Symptoms of Disruptive Mood Dysregulation Disorder"
                        divClassName="ms-3 fw-bold"
                        checked={data.DMDD["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleDMDDChange}
                        disabled={formDisabled}
                      />
                      {data.DMDD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Temper"
                                  label="Severe and recurrent temper outbursts (verbally and/or behaviorally)"
                                  divClassName="ms-3"
                                  checked={data.Temper["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="OutsideOutbursts"
                                  label="Outbursts are outside of developmental level"
                                  divClassName="ms-3"
                                  checked={
                                    data.OutsideOutbursts["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FrequentOutbursts"
                                  label="Outbursts occur three or more times per week"
                                  divClassName="ms-3"
                                  checked={
                                    data.FrequentOutbursts["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DMDDIrritable"
                                  label="Irritable/angry mood most of the day"
                                  divClassName="ms-3"
                                  checked={
                                    data.DMDDIrritable["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="OutburstsAroundOthers"
                                  label="Symptoms occur in home, school and/or with peers"
                                  divClassName="ms-3"
                                  checked={
                                    data.OutburstsAroundOthers["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DMDDOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={data.DMDDOther["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.DMDDOther["@value"] === "true" ? (
                                  <Input
                                    name="DMDDOtherDetails"
                                    error={errors.DMDDOtherDetails}
                                    value={data.DMDDOtherDetails["@value"]}
                                    onChange={handleChange}
                                    identical={compare("DMDDOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.DMDD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DMDD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="DMDDFrequency"
                            label="Frequency:"
                            error={errors.DMDDFrequency}
                            value={data.DMDDFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("DMDDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="DMDDIntensity"
                            label="Intensity:"
                            error={errors.DMDDIntensity}
                            value={data.DMDDIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("DMDDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="DMDDDuration"
                            label="Duration:"
                            error={errors.DMDDDuration}
                            value={data.DMDDDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("DMDDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="ConductDisorder"
                        label="Symptoms of Conduct Disorder"
                        divClassName="ms-3 fw-bold"
                        checked={data.ConductDisorder["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleConductDisorderChange}
                        disabled={formDisabled}
                      />
                      {data.ConductDisorder["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Aggression"
                                  label="Aggression to people and/or animals"
                                  divClassName="ms-3"
                                  checked={data.Aggression["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DestructionOfProperty"
                                  label="Destruction of Property"
                                  divClassName="ms-3"
                                  checked={
                                    data.DestructionOfProperty["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Theft"
                                  label="Deceitfulness or theft"
                                  divClassName="ms-3"
                                  checked={data.Theft["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="SeriousViolations"
                                  label="Serious violations of rules"
                                  divClassName="ms-3"
                                  checked={
                                    data.SeriousViolations["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col-4">
                                <Checkbox
                                  name="OtherConductDisorder"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherConductDisorder["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherConductDisorder["@value"] ===
                                "true" ? (
                                  <Input
                                    name="OtherConductDisorderDetails"
                                    error={errors.OtherConductDisorderDetails}
                                    value={
                                      data.OtherConductDisorderDetails["@value"]
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "OtherConductDisorderDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.ConductDisorder !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.ConductDisorder}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="ConductDisorderFrequency"
                            label="Frequency:"
                            error={errors.ConductDisorderFrequency}
                            value={data.ConductDisorderFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("ConductDisorderFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ConductDisorderIntensity"
                            label="Intensity:"
                            error={errors.ConductDisorderIntensity}
                            value={data.ConductDisorderIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("ConductDisorderIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ConductDisorderDuration"
                            label="Duration:"
                            error={errors.ConductDisorderDuration}
                            value={data.ConductDisorderDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("ConductDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="ODD"
                        label="Symptoms of Oppositional Defiance Disorder (ODD)"
                        divClassName="ms-3 fw-bold"
                        checked={data.ODD["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleODDChange}
                        disabled={formDisabled}
                      />
                      {data.ODD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="IrritableODD"
                                  label="Angry/irritable mood"
                                  divClassName="ms-3"
                                  checked={
                                    data.IrritableODD["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Argumentative"
                                  label="Argumentative/defiant"
                                  divClassName="ms-3"
                                  checked={
                                    data.Argumentative["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Vindictive"
                                  label="Vindictive"
                                  divClassName="ms-3"
                                  checked={data.Vindictive["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="OtherODD"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={data.OtherODD["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.OtherODD["@value"] === "true" ? (
                                  <Input
                                    name="OtherODDDetails"
                                    error={errors.OtherODDDetails}
                                    value={data.OtherODDDetails["@value"]}
                                    onChange={handleChange}
                                    identical={compare("OtherODDDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.ODD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.ODD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="ODDFrequency"
                            label="Frequency:"
                            error={errors.ODDFrequency}
                            value={data.ODDFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("ODDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ODDIntensity"
                            label="Intensity:"
                            error={errors.ODDIntensity}
                            value={data.ODDIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("ODDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ODDDuration"
                            label="Duration:"
                            error={errors.ODDDuration}
                            value={data.ODDDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("ODDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="OCD"
                        label="Symptoms of Obsessive-Compulsive Disorder (OCD)"
                        divClassName="ms-3 fw-bold"
                        checked={data.OCD["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleOCDChange}
                        disabled={formDisabled}
                      />
                      {data.OCD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="OCDPresence"
                                  label="Presence of obsessions, compulsions, or both"
                                  divClassName="ms-3"
                                  checked={
                                    data.OCDPresence["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Obsessions"
                                  label="Obsessions (recurrent and persistent thoughts, urges, or images)"
                                  divClassName="ms-3"
                                  checked={data.Obsessions["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="TimeConsuming"
                                  label="Obsessions or compulsions are time consuming and cause distress/impairment"
                                  divClassName="ms-3"
                                  checked={
                                    data.TimeConsuming["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Compulsions"
                                  label="Compulsions/repetitive behaviors (hand washing, ordering, checking,) or mental acts (praying, counting, repeating words silently)"
                                  divClassName="ms-3"
                                  checked={
                                    data.Compulsions["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ReduceAnxiety"
                                  label="Behaviors and mental acts done to prevent or reduce anxiety or distress"
                                  divClassName="ms-3"
                                  checked={
                                    data.ReduceAnxiety["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SuppressUrges"
                                  label="Attempts to ignore or suppress thoughts, urges or images with other thought or action (compulsion)"
                                  divClassName="ms-3"
                                  checked={
                                    data.SuppressUrges["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="OCDOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={data.OCDOther["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OCDOther["@value"] === "true" ? (
                                  <Input
                                    name="OCDOtherDetails"
                                    error={errors.OCDOtherDetails}
                                    value={data.OCDOtherDetails["@value"]}
                                    onChange={handleChange}
                                    identical={compare("OCDOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.OCD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.OCD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="OCDFrequency"
                            label="Frequency:"
                            error={errors.OCDFrequency}
                            value={data.OCDFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("OCDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OCDIntensity"
                            label="Intensity:"
                            error={errors.OCDIntensity}
                            value={data.OCDIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("OCDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OCDDuration"
                            label="Duration:"
                            error={errors.OCDDuration}
                            value={data.OCDDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("OCDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="PanicDisorder"
                        label="Symptoms of Panic Disorder"
                        divClassName="ms-3 fw-bold"
                        checked={data.PanicDisorder["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handlePanicDisorderChange}
                        disabled={formDisabled}
                      />
                      {data.PanicDisorder["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="PoundingHeart"
                                  label="Palpitations, pounding heart, accelerated heart rate"
                                  divClassName="ms-3"
                                  checked={
                                    data.PoundingHeart["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="PanicDisorderSweating"
                                  label="Sweating"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderSweating["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Trembling"
                                  label="Trembling/shaking"
                                  divClassName="ms-3"
                                  checked={data.Trembling["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="ShortnessOfBreath"
                                  label="Shortness of breath"
                                  divClassName="ms-3"
                                  checked={
                                    data.ShortnessOfBreath["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Choking"
                                  label="Feelings of choking"
                                  divClassName="ms-3"
                                  checked={data.Choking["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ChestPain"
                                  label="Chest pain/discomfort"
                                  divClassName="ms-3"
                                  checked={data.ChestPain["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="PanicDisorderNausea"
                                  label="Nausea/abdominal discomfort"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderNausea["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Dizzy"
                                  label="Dizzy/faint"
                                  divClassName="ms-3"
                                  checked={data.Dizzy["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Chills"
                                  label="Chills"
                                  divClassName="ms-3"
                                  checked={data.Chills["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Derealization"
                                  label="Derealization or depersonalization"
                                  divClassName="ms-3"
                                  checked={
                                    data.Derealization["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FearOfCrazy"
                                  label="Fear of losing control/going crazy"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearOfCrazy["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FearOfDying"
                                  label="Fear of dying"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearOfDying["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="PanicDisorderOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderOther["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.PanicDisorderOther["@value"] ===
                                "true" ? (
                                  <Input
                                    name="PanicDisorderOtherDetails"
                                    error={errors.PanicDisorderOtherDetails}
                                    value={
                                      data.PanicDisorderOtherDetails["@value"]
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "PanicDisorderOtherDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.PanicDisorder !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.PanicDisorder}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="PanicDisorderFrequency"
                            label="Frequency:"
                            error={errors.PanicDisorderFrequency}
                            value={data.PanicDisorderFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("PanicDisorderFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="PanicDisorderIntensity"
                            label="Intensity:"
                            error={errors.PanicDisorderIntensity}
                            value={data.PanicDisorderIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("PanicDisorderIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="PanicDisorderDuration"
                            label="Duration:"
                            error={errors.PanicDisorderDuration}
                            value={data.PanicDisorderDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("PanicDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="Phobias"
                        label="Symptoms of Phobias"
                        divClassName="ms-3 fw-bold"
                        checked={data.Phobias["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handlePhobiasChange}
                        disabled={formDisabled}
                      />
                      {data.Phobias["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="PhobiasSpecificFear"
                                  label="Marked fear/anxiety about a specific object or situation"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasSpecificFear["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ImmediateFear"
                                  label="Object or situation provokes immediate fear or anxiety"
                                  divClassName="ms-3"
                                  checked={
                                    data.ImmediateFear["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="PhobiasAvoidance"
                                  label="Avoids objective/situation"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasAvoidance["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="FearExaggerated"
                                  label="Fear and anxiety is out of proportion"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearExaggerated["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col-4">
                                <Checkbox
                                  name="PhobiasOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasOther["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.PhobiasOther["@value"] === "true" ? (
                                  <Input
                                    name="PhobiasOtherDetails"
                                    error={errors.PhobiasOtherDetails}
                                    value={data.PhobiasOtherDetails["@value"]}
                                    onChange={handleChange}
                                    identical={compare("PhobiasOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.Phobias !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.Phobias}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="PhobiasFrequency"
                            label="Frequency:"
                            error={errors.PhobiasFrequency}
                            value={data.PhobiasFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("PhobiasFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="PhobiasIntensity"
                            label="Intensity:"
                            error={errors.PhobiasIntensity}
                            value={data.PhobiasIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("PhobiasIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="PhobiasDuration"
                            label="Duration:"
                            error={errors.PhobiasDuration}
                            value={data.PhobiasDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("PhobiasDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Specific Phobia, Social Anxiety
                          Disorder (Social Phobia), Agoraphobia
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="DI"
                        label="Developmental Impairments"
                        divClassName="ms-3 fw-bold"
                        checked={data.DI["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleDIChange}
                        disabled={formDisabled}
                      />
                      {data.DI["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DILowIQ"
                                  label="Low intelligence/IQ"
                                  divClassName="ms-3"
                                  checked={data.DILowIQ["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DISensoryImpairments"
                                  label="Sensory impairments"
                                  divClassName="ms-3"
                                  checked={
                                    data.DISensoryImpairments["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DISpeechDelays"
                                  label="Language/speech delays"
                                  divClassName="ms-3"
                                  checked={
                                    data.DISpeechDelays["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DIMotorCoordinationDelays"
                                  label="Motor coordination delays"
                                  divClassName="ms-3"
                                  checked={
                                    data.DIMotorCoordinationDelays["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DILearningDisorder"
                                  label="Specific learning disorder"
                                  divClassName="ms-3"
                                  checked={
                                    data.DILearningDisorder["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DISpatialDelays"
                                  label="Visio-spatial skill delays"
                                  divClassName="ms-3"
                                  checked={
                                    data.DISpatialDelays["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="DevelopmentalImpairmentsOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.DevelopmentalImpairmentsOther[
                                      "@value"
                                    ] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.DevelopmentalImpairmentsOther[
                                  "@value"
                                ] === "true" ? (
                                  <Input
                                    name="DevelopmentalImpairmentsOtherDetails"
                                    error={
                                      errors.DevelopmentalImpairmentsOtherDetails
                                    }
                                    value={
                                      data.DevelopmentalImpairmentsOtherDetails[
                                        "@value"
                                      ]
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "DevelopmentalImpairmentsOtherDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.DI !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DI}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          {data.DILowIQ["@value"] === "true" ||
                          data.DISensoryImpairments["@value"] === "true" ||
                          data.DISpeechDelays["@value"] === "true" ||
                          data.DIMotorCoordinationDelays["@value"] === "true" ||
                          data.DILearningDisorder["@value"] === "true" ||
                          data.DISpatialDelays["@value"] === "true" ? (
                            <Input
                              name="DevelopmentalImpairmentsDetails"
                              label="Please describe:"
                              error={errors.DevelopmentalImpairmentsDetails}
                              value={
                                data.DevelopmentalImpairmentsDetails["@value"]
                              }
                              onChange={handleChange}
                              identical={compare(
                                "DevelopmentalImpairmentsDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <Checkbox
                        name="OtherSymptoms"
                        label="Other Symptoms"
                        divClassName="ms-3 fw-bold"
                        checked={data.OtherSymptoms["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleOtherSymptomsChange}
                        disabled={formDisabled}
                      />
                      {data.OtherSymptoms["@value"] === "true" && (
                        <>
                          <Input
                            name="OtherSymptomsDetails"
                            label="Please describe:"
                            error={errors.OtherSymptomsDetails}
                            value={data.OtherSymptomsDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherSymptomsDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OtherSymptomsFrequency"
                            label="Frequency:"
                            error={errors.OtherSymptomsFrequency}
                            value={data.OtherSymptomsFrequency["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherSymptomsFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OtherSymptomsIntensity"
                            label="Intensity:"
                            error={errors.OtherSymptomsIntensity}
                            value={data.OtherSymptomsIntensity["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherSymptomsIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OtherSymptomsDuration"
                            label="Duration:"
                            error={errors.OtherSymptomsDuration}
                            value={data.OtherSymptomsDuration["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherSymptomsDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="ClinicalNarrative"
                expandStatus={expandStatus}
                className="mt-2"
                title="Clinical Narrative Summary"
              >
                <TextArea
                  name="ClinicalNarrativeSummary"
                  error={errors.ClinicalNarrativeSummary}
                  value={data.ClinicalNarrativeSummary["@value"]}
                  onChange={handleChange}
                  identical={compare("ClinicalNarrativeSummary")}
                  commentsHandler={handleComments}
                  disabled
                  rows="5"
                />
                <TextArea
                  name="ClinicalNarrativeSummaryComments"
                  label="Clinical Narrative Additional Comments"
                  error={errors.ClinicalNarrativeSummaryComments}
                  value={data.ClinicalNarrativeSummaryComments["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("ClinicalNarrativeSummaryComments")}
                  comments={parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  disabled={formDisabled}
                  rows="5"
                />
              </ExpandDiv>
              <ExpandDiv
                id="Evaluation"
                expandStatus={expandStatus}
                className="mt-2"
                title="Diagnoses and Evaluation"
              >
                <FieldTable
                  name="DiagnosisDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 border-end py-2">
                        ICD-10 or DSM-5 Code
                      </div>
                      <div className="col py-2">Diagnosis</div>
                    </div>
                    {data.DiagnosisDetails["@value"] &&
                      JSON.parse(data.DiagnosisDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-3 border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(index, "DiagnosisDetail");
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(index, "DiagnosisDetail");
                                    }}
                                  ></i>
                                  <Input
                                    name="Code"
                                    value={item.Code}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "DiagnosisDetails");
                                    }}
                                  ></i>
                                  {item.Code}
                                </>
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <TextArea
                                  name="Diagnosis"
                                  value={item.Diagnosis}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "DiagnosisDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                <>{item.Diagnosis}</>
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("DiagnosisDetails", { IsEditable: true });
                      }}
                    >
                      Add Diagnosis
                    </button>
                  ) : null}
                  {errors.DiagnosisDetails !== undefined && (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DiagnosisDetails}
                        </div>
                      </div>
                    </div>
                  )}
                </FieldTable>
              </ExpandDiv>
              <ExpandDiv
                id="TreatmentRecommendations"
                expandStatus={expandStatus}
                className="mt-2"
                title="Treatment Recommendations/Needs"
              >
                <b>Additional Assessments/Evaluation Needs:</b>
                <Checkbox
                  name="NoAdditionalNeeds"
                  label="No additional assessment/evaluation needed"
                  divClassName="ms-3 fw-bold"
                  checked={data.NoAdditionalNeeds["@value"] === "true"}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoAdditionalNeeds["@value"] !== "true" ? (
                  <>
                    <FieldTable
                      name="AdditionalNeeds"
                      commentsHandler={handleComments}
                    >
                      <div className="container border mb-2">
                        <div className="row border-bottom">
                          <div className="col-4 border-end py-2">
                            Additional Assessment/Evalutation Need
                          </div>
                          <div className="col py-2">Explain</div>
                        </div>
                        {data.AdditionalNeeds["@value"] &&
                          JSON.parse(data.AdditionalNeeds["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col-4 border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "AdditionalNeed"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "AdditionalNeed"
                                          );
                                        }}
                                      ></i>
                                      <Select
                                        name="Need"
                                        value={item.Need}
                                        options={assessmentNeedOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "AdditionalNeeds"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "AdditionalNeeds"
                                          );
                                        }}
                                      ></i>
                                      {item.Need}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Explain"
                                      value={item.Explain}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "AdditionalNeeds"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Explain
                                  )}
                                </div>
                              </div>
                            )
                          )}
                      </div>
                      {!formDisabled ? (
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("AdditionalNeeds", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Need
                        </button>
                      ) : null}
                      {errors.AdditionalNeeds !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.AdditionalNeeds}
                            </div>
                          </div>
                        </div>
                      )}
                    </FieldTable>
                    <br />
                  </>
                ) : null}
                <FieldTable
                  name="TreatmentRecommendations"
                  commentsHandler={handleComments}
                >
                  <b>Treatment Recommendations</b>
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Recommendation</div>
                      <div className="col border-end py-2">Status</div>
                      <div className="col py-2">Reason</div>
                    </div>
                    {data.TreatmentRecommendations["@value"] &&
                      JSON.parse(data.TreatmentRecommendations["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "TreatmentRecommendation"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "TreatmentRecommendation"
                                      );
                                    }}
                                  ></i>
                                  <Select
                                    name="Recommendation"
                                    value={item.Recommendation}
                                    options={treatmentRecommendationOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  {item.Recommendation}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <div className="container">
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Deferred-${index}`}
                                        label="SAFY will be providing"
                                        checked={item.Deferred === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Referred-${index}`}
                                        label="SAFY will be referring to another agency/individual"
                                        checked={item.Referred === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Declined-${index}`}
                                        label="Declined by Client"
                                        checked={item.Declined === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                getTreatmentRecommendationStatus(index)
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Reason"
                                  value={item.Reason}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Reason
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled && (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("TreatmentRecommendations", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Treatment Recommendation
                      </button>
                    </>
                  )}
                  {errors.TreatmentRecommendations !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TreatmentRecommendations}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <br />
                <RadioButton
                  name="RecommendationsProvided"
                  label="Recommendations provided to client/family:"
                  error={errors.RecommendationsProvided}
                  value={data.RecommendationsProvided["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.RecommendationsProvided["@value"] === "No" && (
                  <Input
                    name="NoRecommendationsProvided"
                    label="Explain:"
                    error={errors.NoRecommendationsProvided}
                    value={data.NoRecommendationsProvided["@value"]}
                    onChange={handleChange}
                    identical={compare("NoRecommendationsProvided")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="CaregiverClientAgrees"
                  label="Caregiver/Client in agreement with recommendations:"
                  error={errors.CaregiverClientAgrees}
                  value={data.CaregiverClientAgrees["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.CaregiverClientAgrees["@value"] === "No" && (
                  <Input
                    name="NoCaregiverClientAgrees"
                    label="Explain:"
                    error={errors.NoCaregiverClientAgrees}
                    value={data.NoCaregiverClientAgrees["@value"]}
                    onChange={handleChange}
                    identical={compare("NoCaregiverClientAgrees")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              {showNarrative === "Show" && (
                <div className="clinicalNarrative bg-white border p-2">
                  <label>
                    <b>Clinical Narrative Summary:</b>
                  </label>
                  <textarea
                    value={data.ClinicalNarrativeSummary["@value"]}
                    rows="5"
                    disabled
                  />
                </div>
              )}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare && (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default INOPPSE;
